import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildAgentHandoffSummary,
  buildZipImplementationPrompt,
} from "../../utils/devIdePromptGenerators";
import FullChangeReviewWorkspace from "../components/FullChangeReviewWorkspace";
import FullChangeLocMetricsPanel from "../components/FullChangeLocMetricsPanel";
import FullChangeSmokeTestPanel from "../components/FullChangeSmokeTestPanel";
import FullChangeSqlReviewPanel from "../components/FullChangeSqlReviewPanel";
import FullChangePlanningDocumentationEntry from "../components/FullChangePlanningDocumentationEntry";
import FullChangePlanningDocumentsPanel from "../components/FullChangePlanningDocumentsPanel";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  CircularProgress,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from "@mui/material";

const TASK_TYPES = ["ANALYSIS", "SCHEMA", "BACKEND", "FRONTEND", "TEST", "DOCS", "RELEASE", "GENERAL"];
const TASK_STATUSES = ["DRAFT", "PROPOSED", "APPROVED", "IN_PROGRESS", "IN_REVIEW", "DONE", "BLOCKED", "CANCELLED"];
const DEV_IDE_CHANGE_STATUSES = ["OPEN", "IN_PROGRESS", "DEVELOPMENT", "SANDBOX_TESTED", "INSTALLED_TO_TEST", "TESTED", "INSTALLED_TO_PROD", "DONE", "CANCELLED"];
const ALL_MODULES_VALUE = "__ALL_MODULES__";
const ALL_MODULES_LABEL = "All modules (project-wide)";
const DEFAULT_FULL_CHANGE_WORKFLOW_MODE = "codex_led";
const FULL_CHANGE_WORKFLOW_MODES = [
  {
    value: "codex_led",
    label: "Codex-led",
    helperText: "Use the guided Codex handoff, manual review, and automation controls for this Full Change.",
  },
  {
    value: "person_led_gpt_chat",
    label: "Person-led GPT Chat",
    helperText: "Use this for manual/GPT Chat implementation without Codex automation controls.",
  },
];
const LOCAL_CODEX_HANDOFF_FOLDERS = [
  "incoming_from_devide",
  "outgoing_to_devide",
  "working",
  "archive",
  "failed",
];

const isAllModulesValue = (value) => String(value || "").trim() === ALL_MODULES_VALUE;

const getFullChangeWorkflowMode = (value) => {
  const normalised = String(value || "").trim();
  return FULL_CHANGE_WORKFLOW_MODES.some((option) => option.value === normalised)
    ? normalised
    : DEFAULT_FULL_CHANGE_WORKFLOW_MODE;
};

const getFullChangeWorkflowModeHelperText = (value) => {
  const mode = getFullChangeWorkflowMode(value);
  const option = FULL_CHANGE_WORKFLOW_MODES.find((row) => row.value === mode);
  return option ? option.helperText : "";
};

const getLinkedChangeDetailValue = (change, keys) => {
  for (const key of keys) {
    const value = change && change[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const formatLinkedChangeDetailValue = (value, fallback = "-") => {
  if (value === undefined || value === null || String(value).trim() === "") return fallback;
  return String(value);
};

const CODEX_EFFICIENCY_METADATA_MARKER = "CODEX_EFFICIENCY_METADATA_JSON:";
const CODEX_TARGETING_LIST_FIELDS = ["target_files", "reference_files", "avoid_files", "search_terms"];
const CODEX_TARGETING_EMPTY_DRAFT = {
  codex_efficiency_mode: "",
  target_files: "",
  reference_files: "",
  avoid_files: "",
  search_terms: "",
  docs_required: false,
  max_initial_file_reads: "",
  commentary_level: "",
  verification_guidance: "",
};

const parseCodexTargetingMetadata = (description) => {
  const text = String(description || "");
  if (!text.includes(CODEX_EFFICIENCY_METADATA_MARKER)) return {};
  const raw = text.split(CODEX_EFFICIENCY_METADATA_MARKER, 2)[1].trim();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (err) {
    return {};
  }
};

const stripCodexTargetingMetadata = (description) => {
  const text = String(description || "").trim();
  if (!text.includes(CODEX_EFFICIENCY_METADATA_MARKER)) return text;
  return text.split(CODEX_EFFICIENCY_METADATA_MARKER, 1)[0].trim();
};

const codexTargetingListToText = (value) => {
  if (Array.isArray(value)) return value.map((row) => String(row || "").trim()).filter(Boolean).join("\n");
  return String(value || "").trim();
};

const codexTargetingTextToList = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((row) => row.trim().replace(/^-+\s*/, ""))
    .filter(Boolean);

const buildCodexTargetingDraft = (change) => {
  const metadata = parseCodexTargetingMetadata(change && change.description);
  return {
    ...CODEX_TARGETING_EMPTY_DRAFT,
    codex_efficiency_mode: String(metadata.codex_efficiency_mode || metadata.mode || ""),
    target_files: codexTargetingListToText(metadata.target_files),
    reference_files: codexTargetingListToText(metadata.reference_files),
    avoid_files: codexTargetingListToText(metadata.avoid_files),
    search_terms: codexTargetingListToText(metadata.search_terms),
    docs_required: Boolean(metadata.docs_required),
    max_initial_file_reads: metadata.max_initial_file_reads ? String(metadata.max_initial_file_reads) : "",
    commentary_level: String(metadata.commentary_level || ""),
    verification_guidance: codexTargetingListToText(metadata.verification_guidance),
  };
};

const buildCodexTargetingMetadata = (draft) => {
  const metadata = {};
  const source = draft || {};
  ["codex_efficiency_mode", "commentary_level", "verification_guidance"].forEach((field) => {
    const value = String(source[field] || "").trim();
    if (value) metadata[field] = value;
  });
  CODEX_TARGETING_LIST_FIELDS.forEach((field) => {
    const rows = codexTargetingTextToList(source[field]);
    if (rows.length) metadata[field] = rows;
  });
  const maxReads = Number(source.max_initial_file_reads);
  if (Number.isFinite(maxReads) && maxReads > 0) metadata.max_initial_file_reads = Math.floor(maxReads);
  if (source.docs_required || Object.keys(metadata).length > 0) metadata.docs_required = Boolean(source.docs_required);
  return metadata;
};

const buildDescriptionWithCodexTargeting = (description, draft) => {
  const base = stripCodexTargetingMetadata(description);
  const metadata = buildCodexTargetingMetadata(draft);
  if (!Object.keys(metadata).length) return base;
  return [base, CODEX_EFFICIENCY_METADATA_MARKER, JSON.stringify(metadata, null, 2)].filter(Boolean).join("\n\n");
};

const buildLocalCodexHandoffFolders = (rootPath) => {
  const root = String(rootPath || "").trim().replace(/[\\/]+$/, "");
  if (!root) return [];
  const separator = root.includes("\\") || /^[A-Za-z]:/.test(root) ? "\\" : "/";
  return LOCAL_CODEX_HANDOFF_FOLDERS.map((folder) => ({
    folder,
    path: `${root}${separator}${folder}${separator}`,
  }));
};

const joinOperatorPath = (rootPath, ...parts) => {
  const root = String(rootPath || "").trim().replace(/[\\/]+$/, "");
  if (!root) return "";
  const separator = root.includes("\\") || /^[A-Za-z]:/.test(root) ? "\\" : "/";
  const suffix = parts
    .map((part) => String(part || "").trim().replace(/^[\\/]+|[\\/]+$/g, ""))
    .filter(Boolean)
    .join(separator);
  return `${root}${separator}${suffix}${separator}`;
};

const buildLocalCodexHandoffRunFolders = (rootPath, fullChangeId, devIdeChangeId, runId) => {
  const root = String(rootPath || "").trim();
  if (!root) {
    return {
      configured: false,
      message: "Local Codex handoff root is not configured for this Full Change.",
    };
  }
  if (!fullChangeId || !devIdeChangeId || !runId) {
    return {
      configured: true,
      root,
      message: "Latest Codex handoff run paths are available after Export Codex Handoff.",
    };
  }
  const runParts = [
    `full_change_${fullChangeId}`,
    `dev_ide_change_${devIdeChangeId}`,
    runId,
  ];
  return {
    configured: true,
    root,
    incomingFromDevideFolder: joinOperatorPath(root, "incoming_from_devide", ...runParts, "outgoing"),
    outgoingToDevideFolder: joinOperatorPath(root, "outgoing_to_devide", ...runParts, "incoming"),
  };
};

const getModuleOptionValue = (row) => {
  if (row === null || row === undefined) return "";
  if (typeof row === "string") return row;
  if (typeof row === "object") return String(row.module_name || row.value || "");
  return String(row || "");
};

const getModuleOptionLabel = (row) => {
  if (row === null || row === undefined) return "";
  if (typeof row === "string") return isAllModulesValue(row) ? ALL_MODULES_LABEL : row;
  if (typeof row === "object") {
    const value = getModuleOptionValue(row);
    if (isAllModulesValue(value)) return String(row.label || ALL_MODULES_LABEL);
    return String(row.label || row.module_name || row.value || "");
  }
  return String(row || "");
};

const getQueueStatusChipProps = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (!value || value === "—") return { label: "—", color: "default", variant: "outlined" };
  if (["exported", "imported", "imported for review", "saved", "passed", "verification passed", "accepted", "completed", "success", "ready to import", "checks passed"].includes(value)) {
    return { label: status, color: "success", variant: "filled" };
  }
  if (["failed", "failure", "returned zip invalid"].includes(value)) return { label: status, color: "error", variant: "filled" };
  if (["pending", "needs review", "review required", "returned zip ready", "waiting for returned zip", "running", "planned", "partial checks"].includes(value)) {
    return { label: status, color: "warning", variant: "outlined" };
  }
  if (["not started", "no handoff", "not recorded", "status unavailable", "loading status"].includes(value)) return { label: status, color: "default", variant: "outlined" };
  return { label: status, color: "default", variant: "outlined" };
};


const QUEUE_WORKFLOW_STEPS = [
  ["packageExported", "Package exported"],
  ["packageImported", "Package imported"],
  ["changedFilesSaved", "Changed files saved"],
  ["frontendDependencyInstallCompleted", "Frontend dependency install/check completed"],
  ["backendDependencyCheckCompleted", "Backend dependency check completed"],
  ["frontendBuildCompleted", "Frontend build completed"],
  ["backendRestartCompleted", "Backend restart completed"],
  ["backendHealthCheckCompleted", "Backend health check completed"],
  ["verificationSummaryCopied", "Human review / summary copied"],
];

const FULL_CHANGE_VERIFICATION_ACTIONS = [
  {
    key: "frontend_dependency_install",
    label: "Run Frontend Dependency Install",
    shortLabel: "Dep Install",
    stepKey: "frontendDependencyInstallCompleted",
  },
  {
    key: "backend_dependency_check",
    label: "Run Backend Dependency Check",
    shortLabel: "Backend Dep Check",
    stepKey: "backendDependencyCheckCompleted",
  },
  {
    key: "frontend_build",
    label: "Run Frontend Build",
    shortLabel: "Frontend Build",
    stepKey: "frontendBuildCompleted",
  },
  {
    key: "backend_restart",
    label: "Restart Backend",
    shortLabel: "Backend Restart",
    stepKey: "backendRestartCompleted",
  },
  {
    key: "backend_health_check",
    label: "Check Backend Health",
    shortLabel: "Health Check",
    stepKey: "backendHealthCheckCompleted",
  },
];

const CODEX_QUEUE_HELPER_TEXT = "Codex path: Export Codex Handoff creates the outbound ZIP for Codex. Codex should return a DEV IDE-compatible implementation ZIP; place that returned ZIP in the configured Codex incoming folder. Open Codex Import Review opens the existing Import Review modal within Full Change and does not save files, run SQL, build, restart, deploy, or verify. Run Codex Auto Import uses the backend automation path and respects force-save or controlled-save settings. Verification and human acceptance still happen separately.";
const CODEX_MANUAL_SYNC_STEPS = [
  "server outgoing -> local incoming_from_devide",
  "local outgoing_to_devide -> server incoming",
];
const CODEX_CLOSEOUT_CHECKLIST = [
  {
    key: "code_docs_review",
    label: "Code/docs review",
    detail: "Review saved child-change code and document diffs.",
  },
  {
    key: "frontend_rebuild",
    label: "Frontend rebuild",
    detail: "Run the normal frontend rebuild outside Codex automation.",
  },
  {
    key: "backend_restart",
    label: "Backend restart",
    detail: "Restart the backend through the operator-controlled path.",
  },
  {
    key: "smoke_test",
    label: "Smoke test",
    detail: "Run the required smoke test before acceptance.",
  },
  {
    key: "accepted_closed",
    label: "Accepted/closed decision",
    detail: "Accept or close the parent Full Change only after verification.",
  },
];

// B1 is a read-only interpretation of already recorded server checkpoints.
// The API emits empty default "pending" rows for steps that have never been run.
const B1_LIFECYCLE_STEPS = [
  ["packageExported", "Exported"],
  ["packageImported", "Imported"],
  ["changedFilesSaved", "Saved"],
  ["frontendBuildCompleted", "Frontend built"],
  ["backendRestartCompleted", "Backend restarted"],
  ["backendHealthCheckCompleted", "Backend health"],
  ["verificationSummaryCopied", "Summary copied (not verification)"],
];
const B1_AUXILIARY_STEPS = [
  ["frontendDependencyInstallCompleted", "Frontend dependencies"],
  ["backendDependencyCheckCompleted", "Backend dependencies"],
];

const safeQueueEvidenceMessage = (value) => String(value || "")
  .replace(/\bBearer\s+[^\s]+/gi, "Bearer [redacted]")
  .replace(/\b(password|api[_-]?key|secret|access[_-]?token)\s*[:=]\s*[^\s,;]+/gi, "$1=[redacted]")
  .slice(0, 800);

const getQueueEvidence = (status, key) => {
  if (status?.__loading) {
    return { label: "Loading status", kind: "unknown", timestamp: "", message: "Retrieving server checkpoint evidence." };
  }
  if (!status || status.__unavailable || status.__loaded !== true) {
    return { label: "Status unavailable", kind: "unknown", timestamp: "", message: safeQueueEvidenceMessage(status?.__error || "Workflow evidence unavailable.") };
  }
  const step = status[key];
  if (!step || typeof step !== "object") {
    return { label: "Not recorded", kind: "not_recorded", timestamp: "", message: "" };
  }
  const timestamp = String(step.timestamp || "").trim();
  const message = safeQueueEvidenceMessage(step.message).trim();
  const state = String(step.state || "").trim().toLowerCase();
  if (state === "success" || state === "failure" || state === "running") {
    // Server timestamps are evidence; UI-generated optimistic timestamps aren't
    // counted by parent aggregation unless the server has answered successfully.
    return { label: state === "success" ? "Success" : state === "failure" ? "Failed" : "Running", kind: state, timestamp, message };
  }
  if (state === "skipped" || state === "not_applicable") {
    return { label: state === "skipped" ? "Skipped" : "Not applicable", kind: state, timestamp, message };
  }
  if (state === "pending" && (timestamp || message)) {
    return { label: "Pending", kind: "pending", timestamp, message };
  }
  return { label: "Not recorded", kind: "not_recorded", timestamp: "", message: "" };
};

const getQueueVerificationChecksEvidence = (status) => {
  const evidence = FULL_CHANGE_VERIFICATION_ACTIONS.map((action) => getQueueEvidence(status, action.stepKey));
  if (evidence.some((step) => step.kind === "unknown")) return "Status unavailable";
  if (evidence.some((step) => step.kind === "failure")) return "Failed";
  if (evidence.some((step) => step.kind === "running")) return "Running";
  if (evidence.every((step) => step.kind === "success")) return "Checks passed";
  if (evidence.some((step) => step.kind === "success")) return "Partial checks";
  return "Not recorded";
};

const getQueueLifecycleTotals = (changes, lookup) => {
  const rows = Array.isArray(changes) ? changes : [];
  const totals = {};
  B1_LIFECYCLE_STEPS.slice(0, 6).forEach(([key]) => {
    const values = rows.map((change) => getQueueEvidence(lookup(change), key));
    totals[key] = {
      success: values.filter((step) => step.kind === "success").length,
      failed: values.filter((step) => step.kind === "failure").length,
      running: values.filter((step) => step.kind === "running").length,
      unknown: values.filter((step) => step.kind === "unknown").length,
      notRecorded: values.filter((step) => step.kind === "not_recorded").length,
      skipped: values.filter((step) => step.kind === "skipped" || step.kind === "not_applicable").length,
      pending: values.filter((step) => step.kind === "pending").length,
    };
  });
  return totals;
};

const createDefaultQueueWorkflowStatus = () =>
  QUEUE_WORKFLOW_STEPS.reduce((acc, [key, label]) => {
    acc[key] = { label, state: "pending", timestamp: "", message: "" };
    return acc;
  }, {});

const mergeQueueWorkflowStatus = (incomingStatus) => {
  const base = createDefaultQueueWorkflowStatus();
  Object.entries(incomingStatus || {}).forEach(([key, value]) => {
    if (!base[key]) return;
    base[key] = {
      ...base[key],
      ...(value || {}),
      label: (value && value.label) || base[key].label,
      state: (value && value.state) || base[key].state,
      timestamp: (value && value.timestamp) || "",
      message: (value && value.message) || "",
    };
  });
  return base;
};

const getQueueWorkflowStateLabel = (state) => {
  if (state === "success") return "Success";
  if (state === "failure") return "Failed";
  if (state === "running") return "Running";
  return "Pending";
};

const getQueueStatusFromWorkflowStep = (status, pendingLabel = "Pending") => {
  const state = status && status.state ? status.state : "pending";
  if (state === "success") return "Success";
  if (state === "failure") return "Failed";
  if (state === "running") return "Running";
  return pendingLabel;
};

const getVerificationRollupStatus = (workflowStatus) => {
  const statuses = FULL_CHANGE_VERIFICATION_ACTIONS.map((action) =>
    getQueueStatusFromWorkflowStep(workflowStatus[action.stepKey])
  );
  if (statuses.includes("Failed")) return "Failed";
  if (statuses.every((status) => status === "Success")) return "Success";
  if (statuses.includes("Running")) return "Running";
  return "Pending";
};

const normaliseQueueModuleName = (value) => {
  const moduleName = String(value || "").trim();
  if (!moduleName || isAllModulesValue(moduleName)) return "ALL";
  return moduleName;
};

const getFullChangeWorkflowModeLabel = (value) => {
  const mode = getFullChangeWorkflowMode(value);
  const option = FULL_CHANGE_WORKFLOW_MODES.find((row) => row.value === mode);
  return option ? option.label : mode;
};

const getProjectDisplayName = (projectOptions, projectId) => {
  const project = (projectOptions || []).find((row) => String(row.project_id || "") === String(projectId || ""));
  if (!project) return projectId || "Not selected";
  const label = project.label || project.name || "";
  return label && label !== project.project_id ? `${label} (${project.project_id})` : project.project_id;
};

const getStartPromptActiveLinkedChange = (state, linkedChanges = []) => {
  const activeId = state?.active_dev_ide_change_id || state?.active_linked_change?.id || state?.next_queued_change?.id;
  return (
    (linkedChanges || []).find((row) => String(row.id) === String(activeId)) ||
    state?.active_linked_change ||
    state?.next_queued_change ||
    null
  );
};

const buildFullChangeStartPrompt = ({
  selectedFullChange,
  activeLinkedChange,
  automationState,
  projectOptions,
} = {}) => {
  const fullChangeId = selectedFullChange?.id || automationState?.full_change_id || "";
  const projectId = selectedFullChange?.project_id || automationState?.project_id || activeLinkedChange?.project_id || "";
  const moduleName = normaliseQueueModuleName(activeLinkedChange?.module || activeLinkedChange?.module_name || selectedFullChange?.module_name || "ALL");
  const workflowMode = getFullChangeWorkflowMode(selectedFullChange?.workflow_mode);
  const localRoot = selectedFullChange?.local_codex_handoff_root_path || "";
  const activeChangeId = activeLinkedChange?.id || automationState?.active_dev_ide_change_id || "";
  const activeChangeTitle = activeLinkedChange?.title || "Untitled linked DEV IDE change";
  const activeChangeOrder = activeLinkedChange?.queue_order ?? activeLinkedChange?.order ?? activeLinkedChange?.full_change_order ?? "";
  const activeChangeStatus = activeLinkedChange?.status || "Unknown";
  const pathPattern = localRoot
    ? [
        `Local Codex handoff root: ${localRoot}`,
        `Wait for handoff ZIPs under: ${joinOperatorPath(localRoot, "incoming_from_devide", `full_change_${fullChangeId}`, `dev_ide_change_${activeChangeId}`, "run-<RUN_ID>", "outgoing")}*_codex_handoff.zip`,
        `Return ZIP to: ${joinOperatorPath(localRoot, "outgoing_to_devide", `full_change_${fullChangeId}`, `dev_ide_change_${activeChangeId}`, "run-<RUN_ID>", "incoming")}`,
      ]
    : [
        "Local Codex handoff root: not configured in DEV IDE.",
        `Wait for actual local handoff ZIPs under the configured/synced incoming_from_devide/full_change_${fullChangeId}/dev_ide_change_${activeChangeId}/run-<RUN_ID>/outgoing folder.`,
        `Return one ZIP to the matching outgoing_to_devide/full_change_${fullChangeId}/dev_ide_change_${activeChangeId}/run-<RUN_ID>/incoming folder.`,
      ];

  return [
    "You are implementing a DEV IDE Codex handoff ZIP.",
    "",
    "This work is for:",
    `Full Change: ${fullChangeId}${selectedFullChange?.title ? ` - ${selectedFullChange.title}` : ""}`,
    `App/project: ${getProjectDisplayName(projectOptions, projectId)}`,
    `Project ID: ${projectId || "Not selected"}`,
    `Workflow mode: ${getFullChangeWorkflowModeLabel(workflowMode)} (${workflowMode})`,
    `Module scope: ${moduleName}`,
    "",
    "Active linked DEV IDE change:",
    `- ID: ${activeChangeId || "Not selected"}`,
    `- Title: ${activeChangeTitle}`,
    `- Queue order: ${activeChangeOrder || "Not set"}`,
    `- Status: ${activeChangeStatus}`,
    `- Module: ${moduleName}`,
    "",
    "Local handoff path guidance:",
    ...pathPattern.map((line) => `- ${line}`),
    "",
    "Return ZIP content rules:",
    "- Include only files that were genuinely changed or newly created for the linked DEV IDE change.",
    "- Do not include README.md unless the handoff ZIP explicitly required a README/documentation update or README.md was genuinely changed as part of the implementation.",
    "- Do not include README.md as a generic implementation note.",
    "- Use CHANGED_FILES_MANIFEST.md and CHANGED_FILES_MANIFEST.json for implementation notes and changed-file reporting.",
    "- Every file listed in CHANGED_FILES_MANIFEST.json must also exist in the returned ZIP as a real changed/new file.",
    "- Every real changed/new file in the returned ZIP must be listed in CHANGED_FILES_MANIFEST.json.",
    "- Manifest paths and ZIP entry paths must match exactly after using forward slashes.",
    "- If SQL is required, return it as a .sql file and list it in CHANGED_FILES_MANIFEST.json with artifact_type: sql, apply_mode: review_only, and execution_required: false.",
    "- Do not execute SQL and do not assume SQL has been applied; DEV IDE will capture SQL as a SQL Review artifact.",
    "- Do not list CHANGED_FILES_MANIFEST.md or CHANGED_FILES_MANIFEST.json inside the changed_files array.",
    "- Do not include RULES.md, ZIP_RETURN_STANDARD.md, DEV_IDE_PATCH_INSTRUCTIONS.md, or other support files unless genuinely changed for the implementation.",
    "",
    "Queue rules:",
    `- Process only Full Change ${fullChangeId} and its linked DEV IDE change queue.`,
    `- Start with linked DEV IDE change ${activeChangeId || "<active change>"} and do not skip ahead.`,
    "- Do not process another Full Change and do not reuse paths from another Full Change.",
    "- Do not process a change unless its handoff ZIP exists locally.",
    "- Open and inspect each actual handoff ZIP before making changes.",
    "- Follow the instruction files inside the ZIP exactly.",
    "- Preserve relative paths exactly in the returned ZIP.",
    "- Returned ZIP archive entries must use portable forward-slash relative paths even when the ZIP is created on Windows.",
    "- Do not use Windows backslashes, drive letters, absolute paths, leading slashes, or traversal segments in ZIP entry names.",
    "- Return only changed/new files plus required implementation notes/docs requested by the handoff.",
    "- Include CHANGED_FILES_MANIFEST.md and CHANGED_FILES_MANIFEST.json.",
    "- In CHANGED_FILES_MANIFEST.json, include changed file paths, change_type, lines_added, lines_removed, final_line_count, and sha256 where practical.",
    "- Do not rebuild, restart, deploy, run health checks, or verify unless the handoff ZIP explicitly instructs it.",
    "- Do not silently auto-save imported data; use only the controlled save path explicitly instructed by the linked DEV IDE change.",
    "- Automated data import, if explicitly requested, must clearly flag missing, stale, or unavailable source data.",
    "- After creating the returned ZIP for the current handoff, stop and wait for the next actual local handoff ZIP in this Full Change queue.",
  ].join("\n");
};

const buildFullChangeEditOnlyWorkflowPrompt = ({
  selectedFullChange,
  linkedChanges,
  projectOptions,
} = {}) => {
  const fullChangeId = selectedFullChange?.id || "";
  const projectId = selectedFullChange?.project_id || "";
  const localRoot = selectedFullChange?.local_codex_handoff_root_path || "";
  const workflowMode = getFullChangeWorkflowMode(selectedFullChange?.workflow_mode);
  const orderedChanges = [...(linkedChanges || [])].sort((a, b) => {
    const aOrder = a.full_change_order ?? a.queue_order ?? a.order ?? 999999;
    const bOrder = b.full_change_order ?? b.queue_order ?? b.order ?? 999999;
    if (Number(aOrder) !== Number(bOrder)) return Number(aOrder) - Number(bOrder);
    return Number(a.id || 0) - Number(b.id || 0);
  });
  const firstChange = orderedChanges[0] || null;
  const lastChange = orderedChanges.length ? orderedChanges[orderedChanges.length - 1] : null;
  const workflowStateJson = localRoot
    ? joinOperatorPath(localRoot, "workflow_state", projectId || "<PROJECT_ID>", `full_change_${fullChangeId}`, "CURRENT_CODEX_HANDOFF.json").replace(/[\\/]+$/, "")
    : `C:\\Users\\stream\\Documents\\Codex\\devide_handoff\\workflow_state\\${projectId || "<PROJECT_ID>"}\\full_change_${fullChangeId}\\CURRENT_CODEX_HANDOFF.json`;
  const workflowPromptPath = localRoot
    ? joinOperatorPath(localRoot, "workflow_state", projectId || "<PROJECT_ID>", `full_change_${fullChangeId}`, "CURRENT_EDIT_ONLY_PROMPT.md").replace(/[\\/]+$/, "")
    : `C:\\Users\\stream\\Documents\\Codex\\devide_handoff\\workflow_state\\${projectId || "<PROJECT_ID>"}\\full_change_${fullChangeId}\\CURRENT_EDIT_ONLY_PROMPT.md`;
  const runnerCommand = `python .\\local_codex_sync_runner.py --config .\\local_codex_runner_${projectId || "<project_id>"}.json --workflow-loop`;
  const changeLines = orderedChanges.length
    ? orderedChanges.map((change) => `- #${change.id}: ${change.title || "Untitled change"}${lastChange && String(change.id) === String(lastChange.id) ? " <= LAST" : ""}`)
    : ["- No linked DEV IDE changes are currently loaded. Start the Full Change after linked changes exist."];

  return [
    "You are running a DEV IDE Full Change workflow in EDIT-ONLY mode.",
    "",
    "The local runner is the execution orchestrator. DEV IDE remains the governance/review/save/rollback authority.",
    "Your job is to edit only the workspace files for one DEV IDE change at a time, then write the done marker requested by the current edit-only prompt.",
    "Do not create implementation ZIPs, return ZIPs, outputs folders, or CHANGED_FILES_MANIFEST files.",
    "",
    "Full Change:",
    `- ID: ${fullChangeId}`,
    `- Title: ${selectedFullChange?.title || "Untitled Full Change"}`,
    `- Project: ${getProjectDisplayName(projectOptions, projectId)}`,
    `- Project ID: ${projectId || "Not selected"}`,
    `- Workflow mode: ${getFullChangeWorkflowModeLabel(workflowMode)} (${workflowMode})`,
    "",
    "Linked DEV IDE changes in order:",
    ...changeLines,
    "",
    `First DEV IDE change: ${firstChange ? `#${firstChange.id} - ${firstChange.title || "Untitled"}` : "not available"}`,
    `Last DEV IDE change: ${lastChange ? `#${lastChange.id} - ${lastChange.title || "Untitled"}` : "not available"}`,
    "",
    "Runner command the operator should have running:",
    runnerCommand,
    "",
    "Workflow coordination files updated by the runner:",
    `- CURRENT_CODEX_HANDOFF.json: ${workflowStateJson}`,
    `- CURRENT_EDIT_ONLY_PROMPT.md: ${workflowPromptPath}`,
    "",
    "Loop instructions:",
    "1. Wait until CURRENT_CODEX_HANDOFF.json exists or is updated.",
    "2. Read CURRENT_CODEX_HANDOFF.json.",
    "3. Read the current edit-only prompt from current_edit_only_prompt_path inside that JSON, or from CURRENT_EDIT_ONLY_PROMPT.md above.",
    "4. Complete only the DEV IDE change named in that current prompt.",
    "5. Edit only inside workspace_dir from CURRENT_CODEX_HANDOFF.json.",
    "6. Write the done_marker_path JSON requested by CURRENT_CODEX_HANDOFF.json/current prompt.",
    "7. Do not package anything; the runner detects the done marker, builds the ZIP, and uploads it to DEV IDE.",
    "8. If is_last_dev_ide_change is false, wait for CURRENT_CODEX_HANDOFF.json to update to the next dev_ide_change_id, then repeat.",
    "9. If is_last_dev_ide_change is true, stop after writing the done marker for that last change.",
    "",
    "Important boundaries:",
    "- Do not implement future linked DEV IDE changes early.",
    "- Do not combine multiple linked DEV IDE changes into one workspace edit unless the current edit-only prompt explicitly says to do so.",
    "- Do not read packaging docs or create manifests/ZIPs.",
    "- Do not rebuild, restart, deploy, or verify; DEV IDE/operator handles that after import/save.",
    "- If the runner reports a failure or the workflow state file does not update, stop and report the exact file/path/status you saw.",
  ].join("\n");
};

const copyQueueTextToClipboard = async (text) => {
  if (!text) return false;
  if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    await navigator.clipboard.writeText(text);
    return true;
  }
  return false;
};

const renderQueueStatusChip = (status, title) => {
  const props = getQueueStatusChipProps(status || "—");
  return (
    <Chip
      size="small"
      label={props.label}
      color={props.color}
      variant={props.variant}
      title={title || props.label}
      sx={{ maxWidth: "100%" }}
    />
  );
};

const normaliseCodexStatusValue = (value) => String(value || "").trim().toLowerCase();

const getCodexRunStatusLabel = (value) => {
  const status = normaliseCodexStatusValue(value);
  if (!status) return "No handoff";
  if (status === "planned") return "Planned";
  if (status === "outgoing_ready") return "Exported";
  if (status === "incoming_received") return "Imported to review";
  if (["verification_passed", "verified", "passed"].includes(status)) return "Verification passed";
  if (["human_review_required", "needs_review", "review_required"].includes(status)) return "Review required";
  if (["completed", "accepted", "done"].includes(status)) return "Completed";
  if (["failed", "failure", "error"].includes(status)) return "Failed";
  return value || "No handoff";
};

const getCodexHandoffDerivedStatus = (statusInfo = {}) => {
  const latestRun = statusInfo.latestRun || null;
  const returnedZip = statusInfo.returnedZip || null;
  const loadError = statusInfo.loadError || "";
  const operatorStatus = statusInfo.operatorStatus || null;
  const rawRunStatus = normaliseCodexStatusValue(latestRun && latestRun.status);

  if (loadError) {
    return {
      label: "Status unavailable",
      detail: loadError,
      nextAction: "Check Codex status endpoint or backend logs.",
    };
  }

  if (operatorStatus && operatorStatus.label) {
    return {
      label: operatorStatus.label,
      detail: operatorStatus.message || (returnedZip ? `${returnedZip.filename || "Returned ZIP"}${returnedZip.run_id ? ` · Run ${returnedZip.run_id}` : ""}` : ""),
      nextAction: operatorStatus.next_action || operatorStatus.nextAction || "Review Codex handoff status.",
    };
  }

  if (returnedZip && rawRunStatus !== "incoming_received") {
    return {
      label: "Ready to import",
      detail: `${returnedZip.filename || "Returned ZIP"}${returnedZip.run_id ? ` · Run ${returnedZip.run_id}` : ""}`,
      nextAction: "Open Codex Import Review",
    };
  }

  if (!latestRun) {
    return {
      label: "No handoff",
      detail: "No Codex handoff run has been created for this linked change.",
      nextAction: "Export Codex Handoff to create the outbound ZIP for Codex.",
    };
  }

  if (["planned", "outgoing_ready", "local_synced", "returned_detected"].includes(rawRunStatus)) {
    return {
      label: "Waiting for returned ZIP",
      detail: latestRun.message || `Run ${latestRun.run_id || "latest"} has no returned ZIP in the server incoming folder yet.`,
      nextAction: "Place the returned ZIP in the server incoming folder, then use Open Codex Import Review or Run Codex Auto Import.",
    };
  }

  if (rawRunStatus === "incoming_received") {
    return {
      label: getCodexRunStatusLabel(latestRun.status),
      detail: latestRun.message || `Run ${latestRun.run_id || "received"}`,
      nextAction: "Review import, save changed files, then verify.",
    };
  }

  if (["verification_passed", "verified", "passed"].includes(rawRunStatus)) {
    return {
      label: getCodexRunStatusLabel(latestRun.status),
      detail: latestRun.message || `Run ${latestRun.run_id || "verified"}`,
      nextAction: "Complete human review / acceptance.",
    };
  }

  if (["human_review_required", "needs_review", "review_required"].includes(rawRunStatus)) {
    return {
      label: getCodexRunStatusLabel(latestRun.status),
      detail: latestRun.message || `Run ${latestRun.run_id || "requires review"}`,
      nextAction: "Complete human review.",
    };
  }

  if (["completed", "accepted", "done"].includes(rawRunStatus)) {
    return {
      label: getCodexRunStatusLabel(latestRun.status),
      detail: latestRun.message || `Run ${latestRun.run_id || "completed"}`,
      nextAction: "No Codex handoff action required.",
    };
  }

  if (["failed", "failure", "error"].includes(rawRunStatus)) {
    return {
      label: getCodexRunStatusLabel(latestRun.status),
      detail: latestRun.message || `Run ${latestRun.run_id || "failed"}`,
      nextAction: "Review failed handoff details and retry if needed.",
    };
  }

  return {
    label: getCodexRunStatusLabel(latestRun.status),
    detail: latestRun.message || `Run ${latestRun.run_id || "latest"}`,
    nextAction: "Review Codex handoff run status.",
  };
};

const renderCodexHandoffStatus = (codexStatus, compact = false) => {
  const derived = getCodexHandoffDerivedStatus(codexStatus);
  return (
    <Stack spacing={0.35} sx={{ minWidth: 0 }}>
      {renderQueueStatusChip(derived.label, derived.detail)}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        Next: {derived.nextAction}
      </Typography>
      {!compact && derived.detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {derived.detail}
        </Typography>
      ) : null}
      {!compact && codexStatus.incomingFolderChecked ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Incoming checked: {codexStatus.incomingFolderChecked}
        </Typography>
      ) : null}
    </Stack>
  );
};

const buildCodexManualSyncSummary = (selectedFullChange, linkedChange, codexStatus = {}) => {
  const latestRun = codexStatus.latestRun || null;
  const returnedZip = codexStatus.returnedZip || null;
  const fullChangeId = (latestRun && latestRun.full_change_id) || (selectedFullChange && selectedFullChange.id);
  const devIdeChangeId = (latestRun && latestRun.dev_ide_change_id) || (linkedChange && linkedChange.id);
  const runId = latestRun && latestRun.run_id;
  const localRoot = selectedFullChange && selectedFullChange.local_codex_handoff_root_path;
  const localPaths = buildLocalCodexHandoffRunFolders(localRoot, fullChangeId, devIdeChangeId, runId);
  return {
    latestRun,
    returnedZip,
    localPaths,
    serverOutgoingFolder: latestRun && latestRun.outgoing_path,
    serverIncomingFolder: latestRun && latestRun.incoming_path,
  };
};

const renderPathValue = (value, fallback = "Available after Export Codex Handoff") => (
  <Typography
    variant="caption"
    color={value ? "text.primary" : "text.secondary"}
    sx={{ fontFamily: value ? "monospace" : "inherit", overflowWrap: "anywhere" }}
  >
    {value || fallback}
  </Typography>
);

const renderCodexManualSyncGuidance = (summary, compact = false) => {
  const localPaths = summary.localPaths || {};
  const statusLabel = summary.returnedZip ? "Ready to import" : "Waiting for returned ZIP";
  return (
    <Box sx={{ border: "1px solid rgba(0,0,0,0.10)", borderRadius: 1, p: 1, bgcolor: "background.default" }}>
      <Stack spacing={0.6}>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Manual Codex sync</Typography>
          {renderQueueStatusChip(statusLabel, summary.returnedZip ? summary.returnedZip.filename : "No returned ZIP detected in server incoming folder yet.")}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {CODEX_MANUAL_SYNC_STEPS.join("; ")}. Open Codex Import Review opens the standard modal; Run Codex Auto Import runs the preview/risk gate before either controlled save or force-save automation.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: compact ? "130px 1fr" : "190px 1fr" },
            gap: 0.5,
            alignItems: "baseline",
          }}
        >
          <Typography variant="caption" color="text.secondary">Local root</Typography>
          {localPaths.configured ? renderPathValue(localPaths.root) : (
            <Typography variant="caption" color="text.secondary">
              {localPaths.message || "Local Codex handoff root is not configured for this Full Change."}
            </Typography>
          )}
          {!compact || summary.latestRun ? (
            <>
              <Typography variant="caption" color="text.secondary">Local input</Typography>
              {renderPathValue(localPaths.incomingFromDevideFolder, localPaths.message || "Available after Export Codex Handoff")}
              <Typography variant="caption" color="text.secondary">Local output</Typography>
              {renderPathValue(localPaths.outgoingToDevideFolder, localPaths.message || "Available after Export Codex Handoff")}
              <Typography variant="caption" color="text.secondary">Server outgoing</Typography>
              {renderPathValue(summary.serverOutgoingFolder)}
              <Typography variant="caption" color="text.secondary">Server incoming</Typography>
              {renderPathValue(summary.serverIncomingFolder)}
            </>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
};
const getCodexAutomationStatusLabel = (value) => {
  const status = normaliseCodexStatusValue(value || "not_started");
  if (status === "not_started") return "Not started";
  if (status === "running") return "Running";
  if (status === "paused") return "Paused";
  if (status === "waiting_for_codex") return "Waiting for Codex";
  if (status === "returned_zip_detected") return "Returned ZIP detected";
  if (status === "import_ready") return "Import ready";
  if (status === "failed") return "Failed";
  if (status === "completed") return "Completed";
  return value || "Not started";
};

const getCodexAutomationChipStatus = (state) => getCodexAutomationStatusLabel(state && state.automation_status);

const getCodexAutomationNextActionLabel = (state) => {
  const nextAction = state && state.next_action;
  if (nextAction && nextAction.label) return nextAction.label;
  const status = normaliseCodexStatusValue(state && state.automation_status);
  if (!status || status === "not_started") return "Start automation";
  if (status === "paused") return "Resume automation";
  if (status === "waiting_for_codex") return "Wait for returned Codex ZIP";
  if (status === "import_ready") return "Open Codex Import Review";
  if (status === "completed") return "No action required";
  return "Process next";
};

const getCodexSaveProgressLabel = (progress) => {
  const state = String((progress && progress.state) || "").toLowerCase();
  if (state === "saved") return "Saved";
  if (state === "failed" || state === "saved_without_import_preview") return "Save blocked/failed";
  if (state === "imported_waiting_for_save") return "Imported; waiting for save";
  if (state === "waiting_for_import") return "Waiting for import";
  return "Not started";
};

const sumCodexCount = (source, keys) =>
  keys.reduce((total, key) => total + Number(source?.[key] || 0), 0);

const getCodexLatestSaveEvidence = (automationState, activeCodexStatus = {}) => {
  const metadata = automationState?.automation_metadata_json || {};
  const latestRun = activeCodexStatus?.latest_run || activeCodexStatus?.latestRun || {};
  const returnedZip = activeCodexStatus?.returned_zip || activeCodexStatus?.returnedZip || {};
  const latestManifest = latestRun?.manifest || {};
  const forceSave = latestManifest.force_save_all || {};
  const controlledSave = latestManifest.controlled_auto_save || {};
  const returnIntegrity = latestManifest.return_integrity || {};
  const activeSaveProgress = automationState?.active_save_progress || {};

  const forceHasResult = Boolean(forceSave.status || forceSave.attempted || forceSave.requested);
  const controlledHasResult = Boolean(controlledSave.status || controlledSave.attempted || controlledSave.requested);
  const selectedResult = forceHasResult ? forceSave : controlledHasResult ? controlledSave : {};
  const selectedMode = forceHasResult
    ? "Force-save / autonomous"
    : controlledHasResult
      ? "Controlled risk-gated save"
      : metadata.force_save_all_returned_files_enabled
        ? "Force-save / autonomous"
        : metadata.controlled_save_enabled
          ? "Controlled risk-gated save"
          : "Import Review only";

  return {
    modeLabel: selectedMode,
    forceSaveEnabled: Boolean(metadata.force_save_all_returned_files_enabled),
    controlledSaveEnabled: Boolean(metadata.controlled_save_enabled),
    resultStatus: selectedResult.status || "not_available",
    savedExistingCount: sumCodexCount(selectedResult, ["saved_code_count", "saved_document_count"]),
    capturedSqlArtifactCount: sumCodexCount(selectedResult, ["captured_sql_artifact_count"]),
    createdNewCount: sumCodexCount(selectedResult, ["created_code_count"]),
    skippedCount: sumCodexCount(selectedResult, ["skipped_code_count", "skipped_create_count", "skipped_document_count", "skipped_sql_artifact_count"]),
    errorCount: sumCodexCount(selectedResult, ["error_count"]),
    integrityStatus: returnIntegrity.status || "not_available",
    returnedZipFilename: returnIntegrity.returned_zip_filename || returnedZip.filename || latestRun.returned_zip_filename || "",
    runId: returnIntegrity.run_id || returnedZip.run_id || latestRun.run_id || "",
    manifestFound: Boolean(returnIntegrity.manifest_found),
    markdownManifestFound: Boolean(returnIntegrity.markdown_manifest_found),
    jsonManifestFound: Boolean(returnIntegrity.json_manifest_found),
    integrityExpectedCount: Number(returnIntegrity.expected_file_count || 0),
    integrityZipCount: Number(returnIntegrity.zip_file_count || 0),
    integrityManifestCount: Number(returnIntegrity.manifest_file_count || 0),
    integritySavedCount: Number(returnIntegrity.saved_file_count || 0),
    capturedSqlIntegrityCount: Number(returnIntegrity.captured_sql_artifact_count || 0),
    appSaveRequiredCount: Number(returnIntegrity.app_save_required_count || 0),
    sqlArtifactCount: Number(returnIntegrity.sql_artifact_count || 0),
    reviewOnlyCount: Number(returnIntegrity.review_only_count || 0),
    metadataFileCount: Number(returnIntegrity.metadata_file_count || 0),
    optionalMetadataCount: Number(returnIntegrity.optional_metadata_count || 0),
    unsafeEntryCount: Number(returnIntegrity.unsafe_entry_count || 0),
    missingFileCount: Number(returnIntegrity.missing_file_count || 0),
    integrityErrorCount: (returnIntegrity.errors || []).length,
    integrityWarningCount: (returnIntegrity.warnings || []).length,
    integrityDetails: [
      ...(returnIntegrity.errors || []).map((row) => ({ ...row, severity: "FAIL" })),
      ...(returnIntegrity.warnings || []).map((row) => ({ ...row, severity: "WARNING" })),
    ].slice(0, 12),
    manifestChangedFiles: returnIntegrity.manifest_changed_files || [],
    zipChangedFiles: returnIntegrity.zip_changed_files || [],
    appSaveRequiredFiles: returnIntegrity.app_save_required_files || [],
    sqlArtifactFiles: returnIntegrity.sql_artifact_files || [],
    reviewOnlyFiles: returnIntegrity.review_only_files || [],
    metadataFiles: returnIntegrity.metadata_files || [],
    optionalMetadataFiles: returnIntegrity.optional_metadata_files || [],
    changedFilesSavedState: activeSaveProgress?.changedFilesSaved?.state || "pending",
    changedFilesSavedMessage: activeSaveProgress?.changedFilesSaved?.message || activeSaveProgress?.message || "No changedFilesSaved checkpoint evidence has been recorded yet.",
    latestMessage: selectedResult.message || latestManifest.review_boundary || latestRun?.message || "No server-side save result has been recorded for the active Codex run yet.",
  };
};

const renderCodexLatestSaveEvidence = (automationState, activeCodexStatus = {}) => {
  const evidence = getCodexLatestSaveEvidence(automationState, activeCodexStatus);
  const forceModeActive = evidence.forceSaveEnabled;
  const showFailureDetails = evidence.changedFilesSavedState === "failure" || evidence.integrityStatus === "FAIL";

  return (
    <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: forceModeActive ? "rgba(255,152,0,0.06)" : "background.default", borderColor: forceModeActive ? "warning.light" : "rgba(0,0,0,0.10)" }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="subtitle2">Latest Codex save evidence</Typography>
          <Chip size="small" label={evidence.modeLabel} color={forceModeActive ? "warning" : "default"} variant={forceModeActive ? "filled" : "outlined"} />
          <Chip size="small" label={`changedFilesSaved: ${evidence.changedFilesSavedState}`} color={evidence.changedFilesSavedState === "success" ? "success" : evidence.changedFilesSavedState === "failure" ? "error" : "default"} variant="outlined" />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {forceModeActive
            ? "Autonomous force-save bypasses the soft Import Review/risk-gate pause for this mode only. Hard ZIP, metadata, and path safety checks remain enforced before any eligible file is saved."
            : evidence.controlledSaveEnabled
              ? "Controlled save remains risk-gated: files are saved only when the backend preview/risk gate allows the returned ZIP."
              : "Import Review only mode: returned ZIPs are parsed for operator review and are not saved automatically."}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Chip size="small" label={`Saved existing: ${evidence.savedExistingCount}`} variant="outlined" />
          <Chip size="small" label={`SQL captured: ${evidence.capturedSqlArtifactCount}`} variant="outlined" color={evidence.capturedSqlArtifactCount ? "warning" : "default"} />
          <Chip size="small" label={`Created new: ${evidence.createdNewCount}`} variant="outlined" />
          <Chip size="small" label={`Skipped: ${evidence.skippedCount}`} variant="outlined" color={evidence.skippedCount ? "warning" : "default"} />
          <Chip size="small" label={`Errors: ${evidence.errorCount}`} variant="outlined" color={evidence.errorCount ? "error" : "default"} />
          <Chip size="small" label={`Save result: ${evidence.resultStatus}`} variant="outlined" />
          <Chip
            size="small"
            label={`Integrity: ${evidence.integrityStatus}`}
            variant={evidence.integrityStatus === "PASS" ? "filled" : "outlined"}
            color={evidence.integrityStatus === "PASS" ? "success" : evidence.integrityStatus === "FAIL" ? "error" : evidence.integrityStatus === "WARNING" ? "warning" : "default"}
          />
          <Chip size="small" label={`Expected files: ${evidence.integrityExpectedCount}`} variant="outlined" />
          <Chip size="small" label={`App save required: ${evidence.appSaveRequiredCount}`} variant="outlined" />
          <Chip size="small" label={`SQL artifacts: ${evidence.sqlArtifactCount}`} variant="outlined" color={evidence.sqlArtifactCount ? "warning" : "default"} />
          <Chip size="small" label={`Review-only: ${evidence.reviewOnlyCount}`} variant="outlined" />
          <Chip size="small" label={`ZIP files: ${evidence.integrityZipCount}`} variant="outlined" />
          <Chip size="small" label={`Integrity saved: ${evidence.integritySavedCount}`} variant="outlined" />
          <Chip size="small" label={`Integrity SQL captured: ${evidence.capturedSqlIntegrityCount}`} variant="outlined" />
        </Stack>
        {showFailureDetails ? (
          <Box sx={{ border: "1px solid rgba(211,47,47,0.35)", borderRadius: 1, p: 1, bgcolor: "rgba(211,47,47,0.04)" }}>
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" color="error.main">Review Save Failure Details</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
                  gap: 0.5,
                  alignItems: "baseline",
                }}
              >
                <Typography variant="caption" color="text.secondary">Returned ZIP</Typography>
                <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>{evidence.returnedZipFilename || "Unknown"}</Typography>
                <Typography variant="caption" color="text.secondary">Run ID</Typography>
                <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>{evidence.runId || "Unknown"}</Typography>
                <Typography variant="caption" color="text.secondary">Manifest presence</Typography>
                <Typography variant="caption">
                  manifest_found: {String(evidence.manifestFound)}; markdown_manifest_found: {String(evidence.markdownManifestFound)}; json_manifest_found: {String(evidence.jsonManifestFound)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Counts</Typography>
                <Typography variant="caption">
                  expected: {evidence.integrityExpectedCount}; manifest-listed: {evidence.integrityManifestCount}; app save required: {evidence.appSaveRequiredCount}; SQL artifacts: {evidence.sqlArtifactCount}; review-only: {evidence.reviewOnlyCount}; metadata: {evidence.metadataFileCount}; ZIP changed: {evidence.integrityZipCount}; optional metadata: {evidence.optionalMetadataCount}; unsafe: {evidence.unsafeEntryCount}; missing: {evidence.missingFileCount}; saved: {evidence.integritySavedCount}; SQL captured: {evidence.capturedSqlIntegrityCount}
                </Typography>
              </Box>
              {evidence.integrityDetails.length ? (
                <Stack spacing={0.25}>
                  {evidence.integrityDetails.map((row, index) => (
                    <Typography key={`${row.code || "integrity-failure"}-${index}`} variant="caption" color={row.severity === "FAIL" ? "error.main" : "text.secondary"} sx={{ overflowWrap: "anywhere" }}>
                      {row.severity}: {row.code || "finding"}{row.path ? ` - ${row.path}` : ""}{row.message ? ` - ${row.message}` : ""}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">No error or warning rows were recorded with the integrity result.</Typography>
              )}
            </Stack>
          </Box>
        ) : null}
        {evidence.integrityDetails.length ? (
          <Stack spacing={0.25}>
            {evidence.integrityDetails.map((row, index) => (
              <Typography key={`${row.code || "integrity"}-${index}`} variant="caption" color={row.severity === "FAIL" ? "error.main" : "text.secondary"} sx={{ overflowWrap: "anywhere" }}>
                {row.severity}: {row.path ? `${row.path} - ` : ""}{row.message || row.code || "Integrity finding"}
              </Typography>
            ))}
          </Stack>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          {evidence.latestMessage}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          Checkpoint: {evidence.changedFilesSavedMessage}
        </Typography>
      </Stack>
    </Paper>
  );
};

const getCodexLinkedChangeLabel = (change) => {
  if (!change || !change.id) return "No next queued DEV IDE change.";
  return `#${change.id} — ${change.title || "Untitled change"}`;
};

const getCodexFullChangeSummaryLabel = (summary) => {
  if (!summary || !summary.total) return "No linked DEV IDE changes summarized yet.";
  return `${summary.saved || 0} saved, ${summary.imported || 0} imported/pending save, ${summary.failed || 0} failed, ${summary.pending || 0} pending, ${summary.resolved || 0} otherwise resolved of ${summary.total || 0} linked changes.`;
};

const getAutoProcessNextLabel = (autoProcessNext) => {
  if (!autoProcessNext || !autoProcessNext.enabled) return "Disabled";
  if (autoProcessNext.eligible) return "Enabled — safe checkpoint ready";
  if (autoProcessNext.waiting) return "Enabled — waiting";
  if (autoProcessNext.blocked) return "Enabled — blocked";
  return "Enabled";
};

const getAutoProcessNextDetail = (autoProcessNext, automationState) => {
  const status = String(automationState?.automation_status || "").toLowerCase();
  if (!autoProcessNext || !autoProcessNext.enabled) {
    return "Automatic Process Next is off. Use the manual Process Next button when ready.";
  }
  if (status === "paused") {
    return "Automation is paused. Auto Process Next will not run until the operator resumes automation.";
  }
  if (autoProcessNext.eligible) {
    return autoProcessNext.reason || "A safe checkpoint is ready. The browser poll will call the same Process Next action once for this condition.";
  }
  if (autoProcessNext.blocked) {
    return autoProcessNext.reason || "Automatic advancement is blocked for the current Full Change state.";
  }
  if (autoProcessNext.waiting) {
    return autoProcessNext.reason || "Waiting for a returned ZIP, saved files, or another safe checkpoint.";
  }
  return autoProcessNext.reason || "Auto Process Next is enabled and monitoring for the next safe checkpoint.";
};

const getAutoProcessNextLastActionLabel = (autoProcessNext) => {
  if (!autoProcessNext) return "No automatic action recorded.";
  const timestamp = autoProcessNext.last_auto_process_next_at || "";
  const status = autoProcessNext.last_auto_process_next_status || "not_run";
  const message = autoProcessNext.last_auto_process_next_message || "";
  if (!timestamp && status === "not_run") return "No automatic action recorded.";
  return `${timestamp || "No timestamp"} — ${status}${message ? `: ${message}` : ""}`;
};

const shouldShowCodexCloseoutChecklist = (automationState) => {
  if (!automationState) return false;
  if (automationState.operator_verification_state) return true;
  const finishInstruction = automationState.finish_instruction || {};
  const automationStatus = normaliseCodexStatusValue(automationState.automation_status);
  return Boolean(finishInstruction.status || finishInstruction.instruction) && automationStatus === "completed";
};

const getCodexCloseoutTitle = (automationState) =>
  automationState?.operator_verification_state?.label ||
  automationState?.finish_instruction?.operator_state_label ||
  automationState?.finish_instruction?.title ||
  "Automation Complete - Awaiting Operator Verification";

const renderCodexCloseoutChecklist = (automationState) => {
  if (!shouldShowCodexCloseoutChecklist(automationState)) return null;
  const summary = automationState?.full_change_codex_summary || {};
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 1, borderColor: "warning.light", backgroundColor: "rgba(255,193,7,0.08)" }}>
      <Stack spacing={1}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" } }}>
          <Box>
            <Typography variant="subtitle2">{getCodexCloseoutTitle(automationState)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Codex package processing is complete; final Full Change acceptance is still an operator decision.
            </Typography>
          </Box>
          {renderQueueStatusChip("Needs review", "Parent closeout checklist is waiting for operator verification.")}
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(5, minmax(0, 1fr))" },
            gap: 0.75,
          }}
        >
          {CODEX_CLOSEOUT_CHECKLIST.map((item) => (
            <Box key={item.key} sx={{ border: "1px solid rgba(0,0,0,0.10)", borderRadius: 1, p: 0.75, backgroundColor: "background.paper", minWidth: 0 }}>
              <Stack spacing={0.4}>
                <Typography variant="caption" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{item.detail}</Typography>
                <Chip size="small" label="Operator gate" variant="outlined" color="warning" sx={{ alignSelf: "flex-start" }} />
              </Stack>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Linked-change summary: {getCodexFullChangeSummaryLabel(summary)} Rebuild, restart, smoke test, deployment, release verification, and parent acceptance are not triggered automatically.
        </Typography>
      </Stack>
    </Paper>
  );
};

const getCodexImportModeMessage = (automationState) => {
  const metadata = automationState?.automation_metadata_json || {};
  if (metadata.force_save_all_returned_files_enabled) {
    return "Force-save mode is enabled. Run Codex Auto Import will parse the returned ZIP and save eligible files directly, bypassing only the soft Import Review/risk-gate pause while keeping hard ZIP/path safety enforced. It will not run build, restart, deployment, or verification.";
  }
  if (metadata.controlled_save_enabled) {
    return "Controlled auto-save mode is enabled. Run Codex Auto Import will save only if the backend preview/risk gate allows it; blocked or review-required returns remain pending for operator review.";
  }
  return "Import Review only mode is enabled. Open Codex Import Review will load the returned ZIP into the existing review boundary without saving files.";
};

const getFullChangeStatusLabel = (fullChange) => {
  return String((fullChange && fullChange.status) || "DRAFT").trim() || "DRAFT";
};

const getFullChangeSelectorLabel = (fullChange) => {
  if (!fullChange) return "";
  const id = fullChange.id !== undefined && fullChange.id !== null ? `#${fullChange.id}` : "#?";
  const title = String(fullChange.title || "Untitled Full Change").trim();
  return `${id} ${title} - ${getFullChangeStatusLabel(fullChange)}`;
};

const buildCodexReturnedZipContext = (nextAction, state) => {
  const action = nextAction || {};
  const returnedZip = action.returned_zip || action.returnedZip || action.codex_returned_zip || {};
  const context = action.codex_handoff_context || action.codexHandoffContext || {};
  return {
    project_id: action.project_id || action.projectId || context.project_id || context.projectId || state?.project_id || "",
    full_change_id: action.full_change_id || action.fullChangeId || context.full_change_id || context.fullChangeId || state?.full_change_id || "",
    dev_ide_change_id: action.dev_ide_change_id || action.devIdeChangeId || context.dev_ide_change_id || context.devIdeChangeId || state?.active_dev_ide_change_id || "",
    run_id: action.run_id || action.runId || context.run_id || context.runId || returnedZip.run_id || returnedZip.runId || "",
    filename: action.filename || context.filename || returnedZip.filename || "",
    path: action.path || context.path || returnedZip.path || "",
    returned_zip: returnedZip,
  };
};

// Keep selection local to this browser; only restore IDs returned by the Full Change list API.
const SELECTED_FULL_CHANGE_STORAGE_KEY = "devtools.fullChange.selectedId.v1";

const readStoredFullChangeId = () => {
  try {
    return typeof window !== "undefined"
      ? String(window.localStorage.getItem(SELECTED_FULL_CHANGE_STORAGE_KEY) || "").trim()
      : "";
  } catch (_error) {
    return "";
  }
};

const persistSelectedFullChangeId = (id) => {
  try {
    if (id !== null && id !== undefined && String(id).trim()) {
      window.localStorage.setItem(SELECTED_FULL_CHANGE_STORAGE_KEY, String(id));
    } else {
      window.localStorage.removeItem(SELECTED_FULL_CHANGE_STORAGE_KEY);
    }
  } catch (_error) {
    // Browser storage is optional; selecting a Full Change still works.
  }
};

// Stage A2: navigation-only context, scoped to the validated parent/project.
// Never store modal state, unsaved files, ZIP contents or permissions here.
const LINKED_QUEUE_NAV_STORAGE_PREFIX = "devtools.fullChange.linkedQueue.v1.";
const getLinkedQueueNavigationKey = (projectId, fullChangeId) => {
  if (!projectId || !fullChangeId) return "";
  return `${LINKED_QUEUE_NAV_STORAGE_PREFIX}${encodeURIComponent(String(projectId))}.${encodeURIComponent(String(fullChangeId))}`;
};

const readLinkedQueueNavigation = (key) => {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw);
    const childId = String(value?.childId || "");
    if (!/^\d+$/.test(childId)) return null;
    const rowTop = Number(value.rowTop);
    const scrollLeft = Number(value.scrollLeft);
    return {
      childId,
      rowTop: value.rowTop !== null && value.rowTop !== undefined && Number.isFinite(rowTop) && Math.abs(rowTop) <= 100000 ? rowTop : null,
      scrollLeft: value.scrollLeft !== null && value.scrollLeft !== undefined && Number.isFinite(scrollLeft) && scrollLeft >= 0 && scrollLeft <= 100000 ? scrollLeft : 0,
    };
  } catch (_error) {
    // Corrupt or inaccessible storage must not block the queue.
    return null;
  }
};

const persistLinkedQueueNavigation = (key, value) => {
  if (!key) return;
  try {
    if (value) window.localStorage.setItem(key, JSON.stringify(value));
    else window.localStorage.removeItem(key);
  } catch (_error) {
    // Private browsing / blocked storage: session navigation still works.
  }
};

const findLinkedQueueScrollParent = (element) => {
  if (typeof window === "undefined") return null;
  let ancestor = element?.parentElement;
  while (ancestor && ancestor !== document.body && ancestor !== document.documentElement) {
    const overflowY = window.getComputedStyle(ancestor).overflowY;
    if (/(auto|scroll)/.test(overflowY) && ancestor.scrollHeight > ancestor.clientHeight + 2) return ancestor;
    ancestor = ancestor.parentElement;
  }
  return null; // Otherwise the document is the vertical scroll container.
};

const getVisibleLinkedQueueRow = (container, childId) => {
  if (!container || !/^\d+$/.test(String(childId || ""))) return null;
  // Mobile and desktop rows exist in the DOM together; use only the displayed row.
  return Array.from(container.querySelectorAll(`[data-linked-queue-child-id="${childId}"]`))
    .find((row) => row.getClientRects().length > 0) || null;
};

const FullChangePanel = ({ selectedProjectId, onSelectedProjectChange, onImportReturnedPackage, onOpenPlanningDocument, importSavedVersion = 0 } = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectionValidated, setSelectionValidated] = useState(false);
  const [selected, setSelected] = useState(null);

  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newProjectId, setNewProjectId] = useState("");
  const [newModuleName, setNewModuleName] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [createModuleOptions, setCreateModuleOptions] = useState([]);
  const [selectedModuleOptions, setSelectedModuleOptions] = useState([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const [projectOptionsError, setProjectOptionsError] = useState(null);
  const [moduleOptionsLoading, setModuleOptionsLoading] = useState(false);

  const [linkedLoading, setLinkedLoading] = useState(false);
  const [linkedError, setLinkedError] = useState(null);
  const [linkedChanges, setLinkedChanges] = useState([]);
  const [linkedLoadedForId, setLinkedLoadedForId] = useState(null);
  const linkedLoadSequenceRef = useRef(0);
  const linkedQueueRef = useRef(null);
  const [lastInteractedLinkedChild, setLastInteractedLinkedChild] = useState(null);
  const linkedFocusRef = useRef(null);
  const linkedQueueContextRef = useRef("");
  const restoredLinkedQueueRef = useRef("");
  const restoringLinkedQueueScrollRef = useRef(false);
  const [queueWorkflowStatusByChangeId, setQueueWorkflowStatusByChangeId] = useState({});
  const queueWorkflowLoadSequenceRef = useRef(0);
  const [queueWorkflowStatusError, setQueueWorkflowStatusError] = useState(null);
  const [codexHandoffStatusByChangeId, setCodexHandoffStatusByChangeId] = useState({});
  const [codexHandoffStatusError, setCodexHandoffStatusError] = useState(null);
  const [codexAutomationState, setCodexAutomationState] = useState(null);
  const [codexAutomationLoading, setCodexAutomationLoading] = useState(false);
  const [codexAutomationError, setCodexAutomationError] = useState(null);
  const [codexStartPromptText, setCodexStartPromptText] = useState("");
  const [codexStartPromptCopyMessage, setCodexStartPromptCopyMessage] = useState("");
  const autoProcessNextInFlightRef = useRef(false);
  const autoProcessNextLastMarkerRef = useRef("");
  const [queueActionMessage, setQueueActionMessage] = useState("");
  const [queueActionLoadingId, setQueueActionLoadingId] = useState(null);
  const [codexTargetingDraftByChangeId, setCodexTargetingDraftByChangeId] = useState({});
  const [codexTargetingSavingId, setCodexTargetingSavingId] = useState(null);
  const [codexTargetingErrorByChangeId, setCodexTargetingErrorByChangeId] = useState({});
  const [planningExportLoading, setPlanningExportLoading] = useState(false);
  const [planningDocumentsRefreshVersion, setPlanningDocumentsRefreshVersion] = useState(0);
  const [planningImportLoading, setPlanningImportLoading] = useState(false);
  const [planningImportApplyLoading, setPlanningImportApplyLoading] = useState(false);
  const [planningImportDialogOpen, setPlanningImportDialogOpen] = useState(false);
  const [planningImportPreview, setPlanningImportPreview] = useState(null);
  const [planningImportManifest, setPlanningImportManifest] = useState(null);
  const planningImportZipRef = useRef(null); // Original reviewed ZIP; never apply browser-modified v2 manifests.
  const [planningImportGptResponse, setPlanningImportGptResponse] = useState("");
  const [planningImportErrors, setPlanningImportErrors] = useState([]);
  const [planningImportWarnings, setPlanningImportWarnings] = useState([]);

  const [targetPreviewSourceProjectId, setTargetPreviewSourceProjectId] = useState("");
  const [targetPreviewTargetProjectId, setTargetPreviewTargetProjectId] = useState("");
  const [targetPreviewIncludeSql, setTargetPreviewIncludeSql] = useState(true);
  const [targetPreviewLoading, setTargetPreviewLoading] = useState(false);
  const [targetPreviewError, setTargetPreviewError] = useState(null);
  const [targetPreviewResult, setTargetPreviewResult] = useState(null);
  const [targetPreviewDialogOpen, setTargetPreviewDialogOpen] = useState(false);
  const [targetInstallLoading, setTargetInstallLoading] = useState(false);
  const [targetInstallError, setTargetInstallError] = useState(null);
  const [targetInstallResult, setTargetInstallResult] = useState(null);

  const [assignDevIdeChangeId, setAssignDevIdeChangeId] = useState("");
  const [assignOrder, setAssignOrder] = useState("");
  const [createLinkedTitle, setCreateLinkedTitle] = useState("");
  const [createLinkedDescription, setCreateLinkedDescription] = useState("");
  const [createLinkedModule, setCreateLinkedModule] = useState(ALL_MODULES_VALUE);
  const [createLinkedLoading, setCreateLinkedLoading] = useState(false);
  const [createLinkedDialogOpen, setCreateLinkedDialogOpen] = useState(false);
  const [createLinkedError, setCreateLinkedError] = useState("");
  const [createdLinkedChange, setCreatedLinkedChange] = useState(null);
  const createLinkedRequestRef = useRef(false);
  const createLinkedParentRef = useRef(null);
  const [viewLinkedChange, setViewLinkedChange] = useState(null);
  const [auditChild, setAuditChild] = useState(null);
  const [auditLoad, setAuditLoad] = useState({ loading: false, data: null, error: "" });
  const [auditHistoryRecord, setAuditHistoryRecord] = useState(null);
  const auditLoadSequenceRef = useRef(0);

  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskCreateTitle, setTaskCreateTitle] = useState("");
  const [taskCreateType, setTaskCreateType] = useState("GENERAL");
  const [taskCreateLoading, setTaskCreateLoading] = useState(false);
  const [taskSaveLoading, setTaskSaveLoading] = useState(false);
  const [taskDeleteLoading, setTaskDeleteLoading] = useState(false);
  const [taskGenerateLoading, setTaskGenerateLoading] = useState(false);
  const [replaceTasksOnGenerate, setReplaceTasksOnGenerate] = useState(false);
  const [useLlmGeneration, setUseLlmGeneration] = useState(false);
  const [taskGenerationInfo, setTaskGenerationInfo] = useState(null);
  const [taskDiscoveryLoading, setTaskDiscoveryLoading] = useState(false);
  const [taskDiscoveryError, setTaskDiscoveryError] = useState(null);
  const [taskDiscoveryDialogOpen, setTaskDiscoveryDialogOpen] = useState(false);
  const [taskDiscoveryResult, setTaskDiscoveryResult] = useState(null);
  const [taskDiscoverySelectedFiles, setTaskDiscoverySelectedFiles] = useState([]);
  const [taskDiscoveryTargetIds, setTaskDiscoveryTargetIds] = useState([]);
  const [taskDiscoveryApplyLoading, setTaskDiscoveryApplyLoading] = useState(false);
  const [taskPatchPreviewLoading, setTaskPatchPreviewLoading] = useState(false);
  const [taskPatchPreviewError, setTaskPatchPreviewError] = useState(null);
  const [taskPatchDialogOpen, setTaskPatchDialogOpen] = useState(false);
  const [taskPatchPreviewResult, setTaskPatchPreviewResult] = useState(null);
  const [taskPatchApplyLoading, setTaskPatchApplyLoading] = useState(false);
  const [taskPatchApplyResult, setTaskPatchApplyResult] = useState(null);
  const [taskSqlProposalLoading, setTaskSqlProposalLoading] = useState(false);
  const [taskSqlProposalError, setTaskSqlProposalError] = useState(null);
  const [taskSchemaTargetLoading, setTaskSchemaTargetLoading] = useState(false);
  const [taskSchemaTargetError, setTaskSchemaTargetError] = useState(null);
  const [taskSchemaTargetResult, setTaskSchemaTargetResult] = useState(null);
  const [taskSqlDialogOpen, setTaskSqlDialogOpen] = useState(false);
  const [taskSqlProposalResult, setTaskSqlProposalResult] = useState(null);
  const [taskSqlSummaryDraft, setTaskSqlSummaryDraft] = useState("");
  const [taskSqlProposalDraft, setTaskSqlProposalDraft] = useState("");
  const [taskRollbackSqlDraft, setTaskRollbackSqlDraft] = useState("");
  const [taskSqlApplyLoading, setTaskSqlApplyLoading] = useState(false);
  const [taskSqlRunDialogOpen, setTaskSqlRunDialogOpen] = useState(false);
  const [taskSqlRunLoading, setTaskSqlRunLoading] = useState(false);
  const [taskSqlRunError, setTaskSqlRunError] = useState(null);
  const [taskSqlRunPreview, setTaskSqlRunPreview] = useState(null);
  const [taskSqlRunExecution, setTaskSqlRunExecution] = useState(null);

  const [plannerRunsLoading, setPlannerRunsLoading] = useState(false);
  const [plannerRunsError, setPlannerRunsError] = useState(null);
  const [plannerRuns, setPlannerRuns] = useState([]);

  const [plannerCreateLoading, setPlannerCreateLoading] = useState(false);
  const [plannerCreateError, setPlannerCreateError] = useState(null);
  const [plannerModule, setPlannerModule] = useState("devtools");
  const [plannerGoalText, setPlannerGoalText] = useState("");

  const loadProjectOptions = async () => {
    setProjectOptionsLoading(true);
    setProjectOptionsError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/projects");
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load projects.");
      setProjectOptions(data.projects || []);
    } catch (e) {
      setProjectOptionsError(e.message);
      setProjectOptions([]);
    } finally {
      setProjectOptionsLoading(false);
    }
  };

  const loadModuleOptions = async (projectId, target = "selected") => {
    const project = String(projectId || "").trim();
    if (!project) {
      if (target === "create") setCreateModuleOptions([]);
      else setSelectedModuleOptions([]);
      return;
    }

    setModuleOptionsLoading(true);
    try {
      const res = await fetch(`/api/dev-ide/full-change/modules?project_id=${encodeURIComponent(project)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load modules.");
      const modules = data.modules || [];
      if (target === "create") setCreateModuleOptions(modules);
      else setSelectedModuleOptions(modules);
    } catch (e) {
      if (target === "create") setCreateModuleOptions([]);
      else setSelectedModuleOptions([]);
    } finally {
      setModuleOptionsLoading(false);
    }
  };

  const buildFullChangeContextText = (fc, linked, fallbackGoalText) => {
    const title = fc && fc.title ? String(fc.title) : "";
    const desc = fc && fc.description ? String(fc.description) : "";

    const lines = [];
    lines.push("FULL CHANGE CONTEXT");
    lines.push("");
    if (fc && fc.id !== undefined && fc.id !== null) lines.push(`Full Change ID: ${fc.id}`);
    if (title) lines.push(`Title: ${title}`);
    if (fc && fc.status) lines.push(`Status: ${fc.status}`);
    if (fc && fc.priority) lines.push(`Priority: ${fc.priority}`);
    if (fc && fc.owner) lines.push(`Owner: ${fc.owner}`);
    if (fc && fc.project_id) lines.push(`Project: ${fc.project_id}`);
    if (fc && fc.module_name) lines.push(`Module: ${getModuleOptionLabel(fc.module_name)}`);

    if (desc) {
      lines.push("");
      lines.push("Description:");
      lines.push(desc);
    }

    if (linked && Array.isArray(linked) && linked.length > 0) {
      lines.push("");
      lines.push("Linked Dev IDE Changes:");
      linked.forEach((c) => {
        const cid = c && c.id !== undefined && c.id !== null ? `#${c.id}` : "#?";
        const ctitle = c && c.title ? String(c.title) : "";
        const cstatus = c && c.status ? String(c.status) : "";
        const ord = c && c.full_change_order !== null && c.full_change_order !== undefined ? ` (order ${c.full_change_order})` : "";
        lines.push(`- ${cid}${ord} ${ctitle}${cstatus ? ` — ${cstatus}` : ""}`.trim());
      });
    }

    const goal = String(fallbackGoalText || "").trim();
    if (goal) {
      lines.push("");
      lines.push("Planner Goal Text:");
      lines.push(goal);
    }

    return lines.join("\n").trim();
  };

  const selectedTaskSummary = useMemo(() => {
    if (!selectedTask) return null;
    return `Task #${selectedTask.id || "new"} • ${selectedTask.task_type || "GENERAL"} • ${selectedTask.status || "DRAFT"}`;
  }, [selectedTask]);

  const selectedTaskAllowedFiles = useMemo(() => {
    return String((selectedTask && selectedTask.allowed_files) || "")
      .split("\n")
      .map((line) => String(line || "").trim())
      .filter(Boolean);
  }, [selectedTask]);

  const canRunTaskPatcher = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    if (!["FRONTEND", "BACKEND", "SCHEMA", "GENERAL"].includes(taskType)) return false;
    return selectedTaskAllowedFiles.length > 0;
  }, [selectedTask, selected, selectedTaskAllowedFiles]);

  const canResolveTaskSchemaTarget = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    return taskType === "SCHEMA";
  }, [selectedTask, selected]);

  const canRunTaskSqlProposal = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    if (taskType !== "SCHEMA") return false;
    return !!(taskSchemaTargetResult && taskSchemaTargetResult.generation_ready);
  }, [selectedTask, selected, taskSchemaTargetResult]);

  const canRunSavedTaskSql = useMemo(() => {
    const taskType = String((selectedTask && selectedTask.task_type) || "").toUpperCase();
    if (!selectedTask || !selectedTask.id) return false;
    if (!selected?.project_id) return false;
    if (taskType !== "SCHEMA") return false;
    return String((selectedTask && selectedTask.sql_proposal) || "").trim().length > 0;
  }, [selectedTask, selected]);

  // The selected parent is trusted only after its detail has loaded and matches the selector.
  const linkedQueueContextKey =
    selected && selectedId && String(selected.id) === String(selectedId)
      ? getLinkedQueueNavigationKey(selected.project_id, selectedId)
      : "";
  const highlightedLinkedChildId =
    lastInteractedLinkedChild?.contextKey === linkedQueueContextKey
      ? lastInteractedLinkedChild.childId
      : null;

  const getLinkedQueueViewport = (childId) => {
    const container = linkedQueueRef.current;
    const row = getVisibleLinkedQueueRow(container, childId);
    if (!row) return { rowTop: null, scrollLeft: 0 };
    const verticalParent = findLinkedQueueScrollParent(container);
    const parentTop = verticalParent ? verticalParent.getBoundingClientRect().top : 0;
    const rowTop = row.getBoundingClientRect().top - parentTop;
    return {
      rowTop: Number.isFinite(rowTop) && Math.abs(rowTop) <= 100000 ? rowTop : null,
      scrollLeft: container && Number.isFinite(container.scrollLeft) ? Math.max(0, container.scrollLeft) : 0,
    };
  };

  const rememberLinkedChild = (linkedChange) => {
    if (!linkedQueueContextKey || !linkedChange?.id || linkedLoading ||
        String(linkedLoadedForId) !== String(selectedId) ||
        !(linkedChanges || []).some((item) => String(item.id) === String(linkedChange.id) &&
          (!item.project_id || String(item.project_id) === String(selected.project_id)))) return;
    const childId = String(linkedChange.id);
    const focus = { contextKey: linkedQueueContextKey, childId };
    linkedFocusRef.current = focus;
    setLastInteractedLinkedChild(focus);
    persistLinkedQueueNavigation(linkedQueueContextKey, {
      childId,
      ...getLinkedQueueViewport(childId),
    });
  };

  const loadList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/list");
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to load full changes.");
      const fullChanges = Array.isArray(data.full_changes) ? data.full_changes : [];
      setItems(fullChanges);
      const storedId = readStoredFullChangeId();
      setSelectedId((currentId) => {
        // Validate saved context before selecting/loading any Full Change.
        // Also keep an already selected record only while it still exists.
        const candidateId = currentId || storedId;
        const match = fullChanges.find((item) => String(item.id) === String(candidateId));
        return match ? match.id : null;
      });
      setSelectionValidated(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOne = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/get?id=${encodeURIComponent(id)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load full change.");
      setSelected(data.full_change || null);

      const fc = data.full_change || null;
      const seedTitle = fc && fc.title ? String(fc.title) : "";
      const seedDesc = fc && fc.description ? String(fc.description) : "";
      const seed = `${seedTitle}${seedDesc ? `\n\n${seedDesc}` : ""}`.trim();
      setPlannerGoalText((prev) => (prev && String(prev).trim().length > 0 ? prev : seed));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLinked = async (fullChangeId) => {
    const sequence = ++linkedLoadSequenceRef.current;
    ++queueWorkflowLoadSequenceRef.current;
    setLinkedLoadedForId(null);
    if (!fullChangeId) {
      setLinkedChanges([]);
      setLinkedLoading(false);
      return;
    }
    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/linked-changes?full_change_id=${encodeURIComponent(fullChangeId)}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to load linked changes.");
      if (sequence !== linkedLoadSequenceRef.current) return;
      setLinkedChanges(Array.isArray(data.linked_changes) ? data.linked_changes : []);
      setLinkedLoadedForId(String(fullChangeId));
    } catch (e) {
      if (sequence === linkedLoadSequenceRef.current) setLinkedError(e.message);
    } finally {
      if (sequence === linkedLoadSequenceRef.current) setLinkedLoading(false);
    }
  };

  const getQueueProjectId = (linkedChange) => {
    return String(
      (linkedChange && (linkedChange.project_id || linkedChange.workflow_project_id)) ||
      (selected && selected.project_id) ||
      "default"
    ).trim() || "default";
  };

  const getNextLinkedChangeOrder = () => {
    const orders = (linkedChanges || [])
      .map((row) => Number(row && row.full_change_order))
      .filter((value) => Number.isFinite(value));
    if (!orders.length) return 1;
    return Math.max(...orders) + 1;
  };

  const pickDefaultTargetPreviewSourceProjectId = (projectList, fallbackProjectId = "") => {
    const list = projectList || [];
    const fallback = String(fallbackProjectId || "").trim();
    if (fallback) return fallback;
    const sandbox = list.find((p) => /sandbox/i.test(`${p?.project_id || ""} ${p?.label || ""}`));
    return sandbox?.project_id || list[0]?.project_id || "";
  };

  const pickDefaultTargetPreviewTargetProjectId = (projectList, sourceProjectId = "") => {
    const source = String(sourceProjectId || "").trim();
    const list = (projectList || []).filter((p) => p && p.project_id && p.project_id !== source);
    const testProject = list.find((p) => /test/i.test(`${p?.project_id || ""} ${p?.label || ""} ${p?.project_notes || ""}`));
    return testProject?.project_id || list[0]?.project_id || "";
  };

  const getChildQueueWorkflowStatus = (change) => {
    if (!change || !change.id || String(linkedLoadedForId) !== String(selectedId)) return { __unavailable: true, __error: "Child queue not loaded." };
    const entry = queueWorkflowStatusByChangeId[change.id];
    if (!entry || entry.__loaded !== true || entry.__unavailable ||
        entry.__projectId !== getQueueProjectId(change) ||
        String(entry.__fullChangeId) !== String(selectedId)) {
      return { __unavailable: true, __error: entry?.__error || "Workflow status has not been loaded for this child." };
    }
    return entry;
  };

  const renderChildLifecycleEvidence = (linkedChange, compact = false) => {
    const status = getChildQueueWorkflowStatus(linkedChange);
    const evidenceRows = [...B1_LIFECYCLE_STEPS, ...B1_AUXILIARY_STEPS];
    const checks = getQueueVerificationChecksEvidence(status);
    return (
      <Box component="details" data-testid={`full-change-child-${linkedChange.id}-lifecycle-${compact ? "mobile" : "desktop"}`} sx={{ mt: 0.75, maxWidth: "100%" }}>
        <Box component="summary" sx={{ cursor: "pointer", fontSize: "0.75rem", fontWeight: 600 }}>
          Lifecycle evidence · Checks: {checks} (not acceptance)
        </Box>
        <Box sx={{ mt: 0.75, display: "grid", gridTemplateColumns: "minmax(86px, 1fr) minmax(110px, 2fr)", gap: 0.6 }}>
          {evidenceRows.map(([key, label]) => {
            const result = getQueueEvidence(status, key);
            return (
              <React.Fragment key={key}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
                <Box data-testid={`full-change-child-${linkedChange.id}-stage-${key}-${compact ? "mobile" : "desktop"}`} sx={{ minWidth: 0 }}>
                  {renderQueueStatusChip(result.label, `${label} — child #${linkedChange.id}`)}
                  {result.timestamp ? <Typography variant="caption" sx={{ display: "block", overflowWrap: "anywhere" }}>{result.timestamp}</Typography> : null}
                  {result.message ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{result.message}</Typography> : null}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          These are recorded action checkpoints, not proof of full acceptance. Copying a summary or passing command checks never marks the child Verified.
        </Typography>
      </Box>
    );
  };

  const renderQueueLifecycleTotals = () => {
    if (!linkedChanges.length || linkedLoading || String(linkedLoadedForId) !== String(selectedId)) return null;
    const totals = getQueueLifecycleTotals(linkedChanges, getChildQueueWorkflowStatus);
    const total = linkedChanges.length;
    const failedChildren = linkedChanges.filter((change) => B1_LIFECYCLE_STEPS.some(([key]) =>
      getQueueEvidence(getChildQueueWorkflowStatus(change), key).kind === "failure")).length;
    const unknownChildren = linkedChanges.filter((change) => {
      const row = getChildQueueWorkflowStatus(change);
      return row.__unavailable || row.__loading;
    }).length;
    const blockedChildren = linkedChanges.filter((change) =>
      String(change.status || "").trim().toUpperCase() === "BLOCKED").length;
    return (
      <Box data-testid="full-change-lifecycle-summary" sx={{ mb: 1.25, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        <Typography variant="subtitle2">Recorded child lifecycle evidence · {total} linked changes</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Children with failed checkpoints: {failedChildren} · Status unavailable: {unknownChildren} · Explicitly blocked child status: {blockedChildren}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.75 }}>
          {B1_LIFECYCLE_STEPS.slice(0, 6).map(([key, label]) => {
            const value = totals[key];
            return (
              <Box key={key} data-testid={`full-change-lifecycle-count-${key}`} sx={{ px: 1, py: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>{label}: {value.success}/{total}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Failed {value.failed} · Running {value.running} · Unknown {value.unknown} · Not recorded {value.notRecorded}
                  {value.skipped ? ` · Skipped/N/A ${value.skipped}` : ""}{value.pending ? ` · Pending ${value.pending}` : ""}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Counts show successful recorded checkpoints, not verified or deployed changes. Build, restart and health are independent; unavailable data is never treated as pending or successful. A final Verified count is not shown because the current per-child workflow-status endpoint does not supply an authoritative acceptance outcome.
        </Typography>
      </Box>
    );
  };

  const loadQueueWorkflowStatuses = async (changesToLoad = linkedChanges) => {
    const requestId = ++queueWorkflowLoadSequenceRef.current;
    const rows = Array.isArray(changesToLoad) ? changesToLoad.filter((change) => change && change.id) : [];
    const parentId = String(selectedId || "");
    if (!rows.length || String(linkedLoadedForId) !== parentId) {
      setQueueWorkflowStatusByChangeId({});
      setQueueWorkflowStatusError(null);
      return;
    }

    setQueueWorkflowStatusError(null);
    // Clear stale evidence immediately; loading isn't a failed/unavailable request.
    setQueueWorkflowStatusByChangeId(Object.fromEntries(rows.map((change) => [
      change.id,
      { __loaded: true, __loading: true, __projectId: getQueueProjectId(change), __fullChangeId: parentId },
    ])));
    const next = {};
    const failures = [];
    for (const change of rows) {
      const projectId = getQueueProjectId(change);
      const context = { __projectId: projectId, __fullChangeId: parentId, __loaded: true };
      try {
        const params = new URLSearchParams();
        params.set("project_id", projectId);
        params.set("change_id", change.id);
        const res = await fetch(`/api/dev-ide/workflow-status?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.success || !data.workflow_status || typeof data.workflow_status !== "object" || Array.isArray(data.workflow_status) ||
            !["packageExported", "packageImported", "changedFilesSaved"].every((key) =>
              data.workflow_status[key] && typeof data.workflow_status[key].state === "string")) {
          throw new Error(data.error || "Missing required workflow checkpoint fields.");
        }
        next[change.id] = { ...mergeQueueWorkflowStatus(data.workflow_status), ...context };
      } catch (e) {
        const error = String(e?.message || "Workflow status request failed.");
        next[change.id] = { ...context, __unavailable: true, __error: error };
        failures.push(`Change #${change.id}: ${error}`);
      }
    }
    if (requestId !== queueWorkflowLoadSequenceRef.current) return;
    setQueueWorkflowStatusByChangeId(next);
    setQueueWorkflowStatusError(failures.length ? failures.join("; ") : null);
  };

  const loadCodexHandoffStatuses = async (changesToLoad = linkedChanges) => {
    const rows = Array.isArray(changesToLoad) ? changesToLoad : [];
    if (!rows.length) {
      setCodexHandoffStatusByChangeId({});
      return;
    }

    setCodexHandoffStatusError(null);
    const next = {};
    for (const change of rows) {
      if (!change || !change.id) continue;
      const projectId = getQueueProjectId(change);
      const fullChangeId = selected && selected.id ? selected.id : selectedId;
      try {
        const statusParams = new URLSearchParams();
        statusParams.set("project_id", projectId);
        if (fullChangeId) statusParams.set("full_change_id", fullChangeId);
        statusParams.set("dev_ide_change_id", change.id);

        const statusRes = await fetch(`/api/dev-ide/codex-handoff/status?${statusParams.toString()}`);
        const statusData = await statusRes.json();
        if (!statusRes.ok || !statusData.success) {
          throw new Error((statusData && statusData.error) || `Failed to load Codex handoff status (status ${statusRes.status}).`);
        }

        next[change.id] = {
          latestRun: statusData.latest_run || null,
          returnedZip: statusData.returned_zip || null,
          returnedZipDetected: Boolean(statusData.returned_zip_detected),
          incomingFolderChecked: statusData.incoming_folder_checked || "",
          operatorStatus: statusData.operator_status || null,
          reviewGate: statusData.review_gate || "",
          loadError: "",
        };
      } catch (e) {
        next[change.id] = { latestRun: null, returnedZip: null, loadError: e.message };
        setCodexHandoffStatusError(e.message);
      }
    }
    setCodexHandoffStatusByChangeId(next);
  };

  const loadCodexAutomationState = async (fullChangeId = selectedId) => {
    if (!fullChangeId) {
      setCodexAutomationState(null);
      return null;
    }

    setCodexAutomationError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/codex-automation-state?full_change_id=${encodeURIComponent(fullChangeId)}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error((data && data.error) || `Failed to load Codex automation state (status ${res.status}).`);
      }
      setCodexAutomationState(data.codex_automation_state || null);
      return data.codex_automation_state || null;
    } catch (e) {
      setCodexAutomationError(e.message);
      return null;
    }
  };

  const loadFullQueueDevIdeChange = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return linkedChange;

    try {
      const params = new URLSearchParams();
      const projectId = getQueueProjectId(linkedChange);
      if (projectId) {
        params.set("project_id", projectId);
      }

      const query = params.toString();
      const res = await fetch(`/api/dev-ide/changes/${encodeURIComponent(linkedChange.id)}${query ? `?${query}` : ""}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load linked DEV IDE change details.");

      return {
        ...linkedChange,
        ...(data.change || {}),
      };
    } catch (e) {
      setQueueWorkflowStatusError(e.message);
      return linkedChange;
    }
  };

  const getCodexTargetingDraft = (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return { ...CODEX_TARGETING_EMPTY_DRAFT };
    return codexTargetingDraftByChangeId[linkedChange.id] || buildCodexTargetingDraft(linkedChange);
  };

  const updateCodexTargetingDraft = (changeId, patch) => {
    if (!changeId) return;
    setCodexTargetingDraftByChangeId((prev) => ({
      ...(prev || {}),
      [changeId]: {
        ...CODEX_TARGETING_EMPTY_DRAFT,
        ...((prev && prev[changeId]) || buildCodexTargetingDraft((linkedChanges || []).find((row) => String(row.id) === String(changeId)))),
        ...patch,
      },
    }));
  };

  const resetCodexTargetingDraft = (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;
    setCodexTargetingDraftByChangeId((prev) => ({
      ...(prev || {}),
      [linkedChange.id]: buildCodexTargetingDraft(linkedChange),
    }));
    setCodexTargetingErrorByChangeId((prev) => ({ ...(prev || {}), [linkedChange.id]: "" }));
  };

  const handleSaveCodexTargeting = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;
    const draft = getCodexTargetingDraft(linkedChange);
    const description = buildDescriptionWithCodexTargeting(linkedChange.description, draft);
    setCodexTargetingSavingId(linkedChange.id);
    setCodexTargetingErrorByChangeId((prev) => ({ ...(prev || {}), [linkedChange.id]: "" }));
    try {
      const res = await fetch(`/api/dev-ide/changes/${encodeURIComponent(linkedChange.id)}/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          description,
          status: linkedChange.status || "OPEN",
          developer: linkedChange.developer || "",
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error((data && (data.error || (data.errors || []).join(", "))) || `Codex targeting save failed (status ${res.status}).`);
      }
      const updatedChange = data.change || {};
      setLinkedChanges((prev) =>
        (prev || []).map((row) =>
          String(row.id) === String(linkedChange.id)
            ? { ...row, ...updatedChange, full_change_id: row.full_change_id, full_change_order: row.full_change_order }
            : row
        )
      );
      setCodexTargetingDraftByChangeId((prev) => ({
        ...(prev || {}),
        [linkedChange.id]: buildCodexTargetingDraft({ ...linkedChange, ...updatedChange, description }),
      }));
      setQueueActionMessage(`Codex targeting metadata saved for linked DEV IDE change #${linkedChange.id}.`);
      if (selectedId) {
        await loadLinked(selectedId);
      }
    } catch (e) {
      setCodexTargetingErrorByChangeId((prev) => ({ ...(prev || {}), [linkedChange.id]: e.message || "Codex targeting save failed." }));
    } finally {
      setCodexTargetingSavingId(null);
    }
  };

  const renderCodexTargetingEditor = (linkedChange, compact = false) => {
    if (!linkedChange || !linkedChange.id) return null;
    const draft = getCodexTargetingDraft(linkedChange);
    const parsedMetadata = parseCodexTargetingMetadata(linkedChange.description);
    const hasMetadata = Object.keys(parsedMetadata).length > 0;
    const errorText = codexTargetingErrorByChangeId[linkedChange.id] || "";
    const saving = codexTargetingSavingId === linkedChange.id;
    const textAreaRows = compact ? 2 : 1;

    return (
      <Box
        component="details"
        sx={{
          border: "1px solid rgba(25,118,210,0.18)",
          borderRadius: 1,
          p: 1,
          backgroundColor: "rgba(25,118,210,0.03)",
        }}
      >
        <Typography component="summary" variant="caption" sx={{ cursor: "pointer", fontWeight: 700 }}>
          Codex targeting {hasMetadata ? "configured" : "not configured"}
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Stack direction={{ xs: "column", md: compact ? "column" : "row" }} spacing={1}>
            <TextField
              size="small"
              label="Efficiency mode"
              value={draft.codex_efficiency_mode}
              onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { codex_efficiency_mode: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Commentary level"
              value={draft.commentary_level}
              onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { commentary_level: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Max initial reads"
              value={draft.max_initial_file_reads}
              onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { max_initial_file_reads: e.target.value })}
              inputProps={{ min: 1 }}
              fullWidth
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(draft.docs_required)}
                onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { docs_required: e.target.checked })}
                size="small"
              />
            }
            label="Docs required"
          />
          <Stack direction={{ xs: "column", md: compact ? "column" : "row" }} spacing={1}>
            <TextField size="small" multiline minRows={textAreaRows} label="Target files" value={draft.target_files} onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { target_files: e.target.value })} fullWidth />
            <TextField size="small" multiline minRows={textAreaRows} label="Reference files" value={draft.reference_files} onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { reference_files: e.target.value })} fullWidth />
          </Stack>
          <Stack direction={{ xs: "column", md: compact ? "column" : "row" }} spacing={1}>
            <TextField size="small" multiline minRows={textAreaRows} label="Avoid files" value={draft.avoid_files} onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { avoid_files: e.target.value })} fullWidth />
            <TextField size="small" multiline minRows={textAreaRows} label="Search terms" value={draft.search_terms} onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { search_terms: e.target.value })} fullWidth />
          </Stack>
          <TextField
            size="small"
            multiline
            minRows={compact ? 2 : 1}
            label="Verification guidance"
            value={draft.verification_guidance}
            onChange={(e) => updateCodexTargetingDraft(linkedChange.id, { verification_guidance: e.target.value })}
            fullWidth
          />
          {errorText ? (
            <Alert severity="error" sx={{ py: 0.5 }}>{errorText}</Alert>
          ) : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
            <Button size="small" variant="contained" onClick={() => handleSaveCodexTargeting(linkedChange)} disabled={saving || Boolean(queueActionLoadingId)} fullWidth={compact}>
              {saving ? "Saving..." : "Save Codex Targeting"}
            </Button>
            <Button size="small" variant="text" onClick={() => resetCodexTargetingDraft(linkedChange)} disabled={saving} fullWidth={compact}>
              Reset
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const markQueueWorkflowStep = async (linkedChange, stepKey, state = "success", message = "") => {
    if (!linkedChange || !linkedChange.id) return null;
    const timestamp = new Date().toLocaleString();
    const fallbackStatus = mergeQueueWorkflowStatus(queueWorkflowStatusByChangeId[linkedChange.id]);
    const stepLabel = (QUEUE_WORKFLOW_STEPS.find(([key]) => key === stepKey) || [null, stepKey])[1];

    setQueueWorkflowStatusByChangeId((prev) => ({
      ...(prev || {}),
      [linkedChange.id]: {
        ...fallbackStatus,
        __unavailable: true,
        __error: "Workflow checkpoint update has not been confirmed by the server.",
        [stepKey]: {
          ...(fallbackStatus[stepKey] || {}),
          label: (fallbackStatus[stepKey] && fallbackStatus[stepKey].label) || stepLabel,
          state,
          timestamp,
          message: message || "",
        },
      },
    }));

    try {
      const res = await fetch("/api/dev-ide/workflow-status/step", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: getQueueProjectId(linkedChange),
          change_id: linkedChange.id,
          step_key: stepKey,
          state,
          message: message || "",
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to save workflow status.");
      setQueueWorkflowStatusByChangeId((prev) => ({
        ...(prev || {}),
        [linkedChange.id]: {
          ...mergeQueueWorkflowStatus(data.workflow_status),
          __projectId: getQueueProjectId(linkedChange),
          __fullChangeId: String(selectedId || ""),
          __loaded: true,
        },
      }));
      setQueueWorkflowStatusError(null);
      return data.workflow_status;
    } catch (e) {
      setQueueWorkflowStatusError(e.message);
      setQueueWorkflowStatusByChangeId((prev) => ({
        ...(prev || {}),
        [linkedChange.id]: {
          ...((prev || {})[linkedChange.id] || {}),
          __unavailable: true,
          __error: `Checkpoint persistence failed: ${e.message}`,
        },
      }));
      return null;
    }
  };

  const handleRunFullChangeVerificationAction = async (linkedChange, actionKey) => {
    if (!selectedId || !linkedChange || !linkedChange.id || !actionKey) return;
    const action = FULL_CHANGE_VERIFICATION_ACTIONS.find((item) => item.key === actionKey);
    if (!action) return;

    setQueueActionLoadingId(`verification-${actionKey}-${linkedChange.id}`);
    setQueueActionMessage(`${action.shortLabel} started for linked change #${linkedChange.id}.`);
    await markQueueWorkflowStep(linkedChange, action.stepKey, "running", `${action.shortLabel} started.`);

    try {
      const res = await fetch("/api/dev-ide/full-change/verification-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          dev_ide_change_id: linkedChange.id,
          action: actionKey,
        }),
      });
      const data = await res.json();
      const workflowStep = data.workflow_step || {};
      if (!res.ok && !workflowStep.step_key) {
        throw new Error((data && (data.error || (data.errors || []).join(", "))) || `Verification action failed (status ${res.status}).`);
      }

      if (workflowStep.step_key) {
        await markQueueWorkflowStep(
          linkedChange,
          workflowStep.step_key,
          workflowStep.state || (data.success ? "success" : "failure"),
          workflowStep.message || ""
        );
      }
      await loadLinked(selectedId);
      await loadQueueWorkflowStatuses(linkedChanges);
      setQueueActionMessage(`${action.shortLabel}: ${workflowStep.message || (data.success ? "completed successfully." : "failed.")}`);
    } catch (e) {
      await markQueueWorkflowStep(linkedChange, action.stepKey, "failure", e.message || `${action.shortLabel} failed.`);
      setQueueActionMessage(e.message || `${action.shortLabel} failed.`);
    } finally {
      setQueueActionLoadingId(null);
    }
  };

  const getVerificationTargetChange = () => {
    const activeId = codexAutomationState?.active_linked_change?.id || codexAutomationState?.active_dev_ide_change_id;
    if (activeId) {
      const match = (linkedChanges || []).find((row) => Number(row?.id) === Number(activeId));
      if (match) return match;
      if (codexAutomationState?.active_linked_change) return codexAutomationState.active_linked_change;
    }
    return (linkedChanges || [])[0] || null;
  };

  const buildVerificationDetailsText = (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return "";
    const workflowStatus = getChildQueueWorkflowStatus(linkedChange);
    const lines = [
      `Full Change ${selectedId || ""} verification checkpoints (not acceptance)`,
      `Linked DEV IDE change #${linkedChange.id}: ${linkedChange.title || "Untitled change"}`,
    ];
    FULL_CHANGE_VERIFICATION_ACTIONS.forEach((action) => {
      const evidence = getQueueEvidence(workflowStatus, action.stepKey);
      lines.push("");
      lines.push(`${action.shortLabel}: ${evidence.label}`);
      if (evidence.timestamp) lines.push(`Timestamp: ${evidence.timestamp}`);
      if (evidence.message) lines.push(`Message: ${evidence.message}`);
    });
    return lines.join("\n").trim();
  };

  const renderVerificationActionControls = (linkedChange, compact = false) => {
    if (!linkedChange || !linkedChange.id) {
      return (
        <Typography variant="caption" color="text.secondary">
          Select or create a linked DEV IDE change before running verification actions.
        </Typography>
      );
    }

    const workflowStatus = getChildQueueWorkflowStatus(linkedChange);
    const rollupStatus = getQueueVerificationChecksEvidence(workflowStatus);
    const detailsText = buildVerificationDetailsText(linkedChange);

    return (
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Verification target: #{linkedChange.id}
          </Typography>
          {renderQueueStatusChip(rollupStatus, `Recorded verification-check outcomes for linked change #${linkedChange.id}`)}
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}>
          {FULL_CHANGE_VERIFICATION_ACTIONS.map((action) => {
            const step = workflowStatus[action.stepKey] || {};
            const loading = queueActionLoadingId === `verification-${action.key}-${linkedChange.id}`;
            return (
              <Button
                key={action.key}
                data-testid={`full-change-child-${linkedChange.id}-verify-${action.key}-${compact ? "mobile" : "desktop"}`}
                size="small"
                variant={step.state === "success" ? "outlined" : "contained"}
                color={step.state === "failure" ? "error" : "primary"}
                onClick={() => handleRunFullChangeVerificationAction(linkedChange, action.key)}
                disabled={Boolean(queueActionLoadingId)}
                fullWidth={compact}
                title={step.message || action.shortLabel}
              >
                {loading ? "Running..." : action.label}
              </Button>
            );
          })}
          <Button
            data-testid={`full-change-child-${linkedChange.id}-verification-details-${compact ? "mobile" : "desktop"}`}
            size="small"
            variant="text"
            onClick={() => copyQueueTextToClipboard(detailsText).then((ok) => setQueueActionMessage(ok ? "Verification details copied." : "Verification details are available in the status messages."))}
            disabled={!detailsText}
            fullWidth={compact}
          >
            Copy Details
          </Button>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: compact ? "1fr" : "repeat(3, minmax(0, 1fr))" },
            gap: 0.75,
          }}
        >
          {FULL_CHANGE_VERIFICATION_ACTIONS.map((action) => {
            const step = workflowStatus[action.stepKey] || {};
            const evidence = getQueueEvidence(workflowStatus, action.stepKey);
            const stateLabel = evidence.label;
            const detail = [evidence.timestamp, evidence.message].filter(Boolean).join(" - ");
            return (
              <Box key={action.stepKey} sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>{action.shortLabel}</Typography>
                {renderQueueStatusChip(stateLabel, detail || action.shortLabel)}
                {detail ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, overflowWrap: "anywhere" }}>
                    {detail}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Stack>
    );
  };

  const buildQueueExportAgentHandoffSummary = ({ linkedChange, moduleName }) => {
    const effectiveModule = moduleName && moduleName.trim().length > 0 ? moduleName : "ALL";
    const safeName = effectiveModule.toUpperCase() === "ALL" ? "all_modules" : effectiveModule.toLowerCase();
    const packageName = `${safeName}_dev_ide_files_with_docs.zip`;
    const workflowStatus = getChildQueueWorkflowStatus(linkedChange);
    const workflowLines = QUEUE_WORKFLOW_STEPS.map(([key, label]) => {
      const evidence = getQueueEvidence(workflowStatus, key);
      const message = evidence.message ? ` - ${evidence.message}` : "";
      return `${label}: ${evidence.label}${evidence.timestamp ? ` at ${evidence.timestamp}` : ""}${message}`;
    });

    return buildAgentHandoffSummary({
      phase: "Export",
      projectId: getQueueProjectId(linkedChange),
      codeModule: effectiveModule,
      includeDocs: true,
      docsMode: "all",
      docModules: ["ALL"],
      packageName,
      changedCodePaths: [],
      changedDocumentPaths: [],
      verificationLines: workflowLines,
      warnings: [],
      errors: [],
      recommendedNextStep: "Attach the exported ZIP and this handoff summary to the implementation agent, then import the returned package through DEV IDE.",
    });
  };

  const buildQueueResultSummary = (linkedChange) => {
    const status = getChildQueueWorkflowStatus(linkedChange);
    const codexStatus = codexHandoffStatusByChangeId[linkedChange.id] || null;
    const codexDerivedStatus = getCodexHandoffDerivedStatus(codexStatus || {});
    const latestCodexRun = codexStatus && codexStatus.latestRun;
    const returnedCodexZip = codexStatus && codexStatus.returnedZip;
    const codexStatusFieldsPresent = Boolean(
      (codexStatus && codexStatus.loadError) ||
      latestCodexRun ||
      returnedCodexZip
    );
    const codexStatusLines = codexStatusFieldsPresent
      ? [
          "",
          "Codex handoff status:",
          `Status: ${codexDerivedStatus.label}`,
          `Next action: ${codexDerivedStatus.nextAction}`,
          ...(codexDerivedStatus.detail ? [`Detail: ${codexDerivedStatus.detail}`] : []),
          ...(latestCodexRun && latestCodexRun.run_id ? [`Run: ${latestCodexRun.run_id}`] : []),
          ...(latestCodexRun && latestCodexRun.status ? [`Run status: ${latestCodexRun.status}`] : []),
          ...(returnedCodexZip && returnedCodexZip.filename ? [`Returned ZIP: ${returnedCodexZip.filename}`] : []),
        ]
      : [];
    return [
      "DEV IDE Agent Workflow Result Summary",
      "",
      `Full Change: ${selected && selected.id ? `#${selected.id} ${selected.title || ""}`.trim() : "None selected"}`,
      `Linked DEV IDE change: #${linkedChange.id} ${linkedChange.title || "Untitled change"}`,
      `Project: ${getQueueProjectId(linkedChange)}`,
      "",
      "Workflow status:",
      ...QUEUE_WORKFLOW_STEPS.map(([key, label], index) => {
        const evidence = getQueueEvidence(status, key);
        const timestamp = evidence.timestamp ? ` at ${evidence.timestamp}` : "";
        const message = evidence.message ? ` - ${evidence.message}` : "";
        return `${index + 1}. ${label}: ${evidence.label}${timestamp}${message}`;
      }),
      ...codexStatusLines,
      "",
      `Latest queue action: ${queueActionMessage || "No queue action message yet."}`,
    ].join("\n");
  };

  const handleQueueExportPackage = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;

    const initialModuleName = normaliseQueueModuleName(linkedChange.module || linkedChange.module_name || "ALL");
    const initialContextLabel = `linked DEV IDE change #${linkedChange.id} — ${linkedChange.title || "Untitled change"}`;

    setQueueActionLoadingId(`export-${linkedChange.id}`);
    setQueueActionMessage(`Preparing Development-tab export package for ${initialContextLabel}.`);
    await markQueueWorkflowStep(linkedChange, "packageExported", "running", `Export package started for ${initialContextLabel}.`);

    try {
      const exportChange = await loadFullQueueDevIdeChange(linkedChange);
      const moduleName = normaliseQueueModuleName(exportChange.module || exportChange.module_name || initialModuleName || "ALL");
      const projectId = getQueueProjectId(exportChange);
      const contextLabel = `linked DEV IDE change #${exportChange.id} — ${exportChange.title || "Untitled change"}`;

      const prompt = buildZipImplementationPrompt({
        projectId,
        codeModule: moduleName,
        includeDocs: true,
        docsMode: "all",
        docModules: ["ALL"],
        changeTitle: exportChange?.title || "",
        changeDescription: exportChange?.description || "",
        docsChanged: "Yes - update existing docs included in the ZIP when needed",
      });
      const linkedPlanningNote = `

Linked Full Change Planning context: Before implementing this child change, review the read-only files under context/full_change_planning_documents/ (including UI mockup assets) if present in the ZIP. These files define the agreed design but are NOT source files or importable module docs. Do not return or edit this context folder; changes to planning docs belong in the parent Full Change Planning ZIP.
`;


      let copiedPrompt = false;
      try {
        copiedPrompt = await copyQueueTextToClipboard(`${prompt}${linkedPlanningNote}`);
      } catch (err) {
        copiedPrompt = false;
      }

      const exportHandoffSummary = buildQueueExportAgentHandoffSummary({ linkedChange: exportChange, moduleName });
      let copiedHandoff = false;
      if (copiedPrompt) {
        try {
          copiedHandoff = await copyQueueTextToClipboard(`${prompt}\n\n${"=".repeat(80)}\n\n${exportHandoffSummary}`);
        } catch (err) {
          copiedHandoff = false;
        }
      }

      const successMessage = copiedHandoff
        ? `Agent export package started for module '${moduleName}' with all documents. Implementation prompt and agent handoff summary copied to clipboard.`
        : copiedPrompt
          ? `Agent export package started for module '${moduleName}' with all documents. Implementation prompt copied to clipboard.`
          : `Agent export package started for module '${moduleName}' with all documents. Clipboard copy was blocked by the browser.`;

      const params = new URLSearchParams();
      params.set("module", moduleName.toUpperCase() === "ALL" ? "ALL" : moduleName);
      if (projectId) {
        params.set("project_id", projectId);
      }
      params.set("include_documents", "true");
      // Bind this ZIP to the persisted linked child, so the backend can add the
      // parent's Planning Markdown and images as read-only implementation context.
      params.set("linked_change_id", String(exportChange.id));

      const res = await fetch(`/api/dev-ide/zip-module?${params.toString()}`, { method: "GET" });
      if (!res.ok) {
        let errMsg = `Failed to download module ZIP (status ${res.status}).`;
        try {
          const data = await res.json();
          if (data && data.error) {
            errMsg = data.error;
          }
        } catch {}
        throw new Error(errMsg);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = moduleName.toUpperCase() === "ALL" ? "all_modules" : moduleName.toLowerCase();

      link.href = url;
      link.download = `${safeName}_dev_ide_files_with_docs.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setQueueActionMessage(successMessage);
      await markQueueWorkflowStep(exportChange, "packageExported", "success", successMessage);
      await loadCodexHandoffStatuses([exportChange]);
      await loadCodexAutomationState(selectedId);
    } catch (e) {
      const message = e.message || `Agent export package download failed for ${initialContextLabel}.`;
      setQueueActionMessage(message);
      await markQueueWorkflowStep(linkedChange, "packageExported", "failure", message);
    } finally {
      setQueueActionLoadingId(null);
    }
  };


  const handleCodexAutomationMetadataToggle = async (metadataKey, enabled) => {
    if (!selectedId || !codexAutomationState) return;

    setCodexAutomationLoading(true);
    setCodexAutomationError(null);
    try {
      const metadata = {
        ...(codexAutomationState.automation_metadata_json || {}),
        [metadataKey]: Boolean(enabled),
      };
      const res = await fetch("/api/dev-ide/full-change/codex-automation-state", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          automation_status: codexAutomationState.automation_status || "not_started",
          active_dev_ide_change_id: codexAutomationState.active_dev_ide_change_id || null,
          latest_codex_handoff_run_db_id: codexAutomationState.latest_codex_handoff_run_db_id || null,
          latest_codex_handoff_run_id: codexAutomationState.latest_codex_handoff_run_id || null,
          status_message: codexAutomationState.status_message || "",
          last_error: codexAutomationState.last_error || "",
          automation_metadata_json: metadata,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error((data && (data.error || (data.errors || []).join(", "))) || `Could not update Codex automation setting (status ${res.status}).`);
      }
      setCodexAutomationState(data.codex_automation_state || null);
      const settingLabel =
        metadataKey === "force_save_all_returned_files_enabled"
          ? "Force-save all returned Codex files"
          : metadataKey === "auto_process_next_enabled"
            ? "Auto Process Next"
            : "Controlled Codex save";
      setQueueActionMessage(
        enabled
          ? `${settingLabel} enabled. Run Codex Auto Import will use this setting; no build or restart will run.`
          : `${settingLabel} disabled. Open Codex Import Review remains available; Run Codex Auto Import will fall back to the next available safe path.`,
      );
    } catch (e) {
      setCodexAutomationError(e.message || "Could not update Codex automation setting.");
    } finally {
      setCodexAutomationLoading(false);
    }
  };

  const handleCodexControlledSaveToggle = (enabled) =>
    handleCodexAutomationMetadataToggle("controlled_save_enabled", enabled);

  const handleCodexForceSaveAllToggle = (enabled) =>
    handleCodexAutomationMetadataToggle("force_save_all_returned_files_enabled", enabled);

  const handleCodexAutoProcessNextToggle = (enabled) =>
    handleCodexAutomationMetadataToggle("auto_process_next_enabled", enabled);

  const buildCodexStartPromptForState = (state) =>
    buildFullChangeStartPrompt({
      selectedFullChange: selected,
      activeLinkedChange: getStartPromptActiveLinkedChange(state, linkedChanges),
      automationState: state,
      projectOptions,
    });

  const copyCodexStartPromptForState = async (state) => {
    const prompt = buildCodexStartPromptForState(state);
    setCodexStartPromptText(prompt);
    try {
      const copied = await copyQueueTextToClipboard(prompt);
      const message = copied
        ? "Full Change Start prompt copied to clipboard."
        : "Full Change Start prompt is ready below. Browser clipboard copy was blocked.";
      setCodexStartPromptCopyMessage(message);
      return { copied, message };
    } catch (err) {
      const message = "Full Change Start prompt is ready below. Browser clipboard copy was blocked.";
      setCodexStartPromptCopyMessage(message);
      return { copied: false, message };
    }
  };

  const handleCopyCodexStartPrompt = async () => {
    if (!selectedId) return;
    const state = codexAutomationState || { full_change_id: selectedId };
    const result = await copyCodexStartPromptForState(state);
    setQueueActionMessage(result.message);
  };


  const handleCopyCodexEditOnlyWorkflowPrompt = async () => {
    if (!selectedId) return;
    const prompt = buildFullChangeEditOnlyWorkflowPrompt({
      selectedFullChange: selected,
      linkedChanges,
      projectOptions,
    });
    setCodexStartPromptText(prompt);
    try {
      const copied = await copyQueueTextToClipboard(prompt);
      const message = copied
        ? "Full Change edit-only workflow prompt copied to clipboard."
        : "Full Change edit-only workflow prompt is ready below. Browser clipboard copy was blocked.";
      setCodexStartPromptCopyMessage(message);
      setQueueActionMessage(message);
    } catch (err) {
      const message = "Full Change edit-only workflow prompt is ready below. Browser clipboard copy was blocked.";
      setCodexStartPromptCopyMessage(message);
      setQueueActionMessage(message);
    }
  };

  const handleCodexAutomationAction = async (action) => {
    if (!selectedId) return null;

    setCodexAutomationLoading(true);
    setCodexAutomationError(null);
    setQueueActionMessage(`Codex automation action: ${action}.`);

    try {
      const res = await fetch("/api/dev-ide/full-change/codex-automation-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ full_change_id: selectedId, action }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error((data && (data.error || (data.errors || []).join(", "))) || `Codex automation action failed (status ${res.status}).`);
      }

      const state = data.codex_automation_state || null;
      setCodexAutomationState(state);
      const actionLabel = getCodexAutomationNextActionLabel(state);
      const statusLabel = getCodexAutomationStatusLabel(state && state.automation_status);
      let actionMessage = `${statusLabel}. Safe next action: ${actionLabel}. ${state?.status_message || ""}`.trim();

      if (action === "start" && state) {
        const copyResult = await copyCodexStartPromptForState(state);
        actionMessage = `${actionMessage} ${copyResult.message}`.trim();
      }

      setQueueActionMessage(actionMessage);

      const shouldRunProcessNextSideEffects = action === "process_next" || action === "auto_process_next";

      if (shouldRunProcessNextSideEffects && state?.next_action?.type === "export_codex_handoff") {
        const activeId = state.active_dev_ide_change_id || (state.active_linked_change && state.active_linked_change.id);
        const activeChange = (linkedChanges || []).find((row) => String(row.id) === String(activeId)) || state.active_linked_change;
        if (activeChange && activeChange.id) {
          await handleQueueExportCodexHandoff(activeChange);
          await loadCodexAutomationState(selectedId);
        }
      }

      if (shouldRunProcessNextSideEffects && state?.next_action?.type === "import_codex_return") {
        const activeId = state.active_dev_ide_change_id || (state.active_linked_change && state.active_linked_change.id);
        const activeChange = (linkedChanges || []).find((row) => String(row.id) === String(activeId)) || state.active_linked_change;
        if (activeChange && activeChange.id) {
          const returnedZipContext = buildCodexReturnedZipContext(state.next_action, state);
          setQueueActionMessage(
            `Detected Codex returned ZIP for active linked change #${activeChange.id}. ${getCodexImportModeMessage(state)}`,
          );
          await handleQueueImportCodexReturn(activeChange, returnedZipContext);
          await loadCodexAutomationState(selectedId);
        }
      }

      return state;
    } catch (e) {
      setCodexAutomationError(e.message);
      setQueueActionMessage(e.message || "Codex automation action failed.");
      return null;
    } finally {
      setCodexAutomationLoading(false);
    }
  };

  const handleQueueExportCodexHandoff = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;

    const initialModuleName = normaliseQueueModuleName(linkedChange.module || linkedChange.module_name || "ALL");
    const initialContextLabel = `linked DEV IDE change #${linkedChange.id} — ${linkedChange.title || "Untitled change"}`;

    setQueueActionLoadingId(`codex-export-${linkedChange.id}`);
    setQueueActionMessage(`Preparing Codex handoff package for ${initialContextLabel}.`);
    await markQueueWorkflowStep(linkedChange, "packageExported", "running", `Codex handoff export started for ${initialContextLabel}.`);

    try {
      const exportChange = await loadFullQueueDevIdeChange(linkedChange);
      const moduleName = normaliseQueueModuleName(exportChange.module || exportChange.module_name || initialModuleName || "ALL");
      const projectId = getQueueProjectId(exportChange);
      const contextLabel = `linked DEV IDE change #${exportChange.id} — ${exportChange.title || "Untitled change"}`;

      const prompt = buildZipImplementationPrompt({
        projectId,
        codeModule: moduleName,
        includeDocs: true,
        docsMode: "all",
        docModules: ["ALL"],
        changeTitle: exportChange?.title || "",
        changeDescription: exportChange?.description || "",
        docsChanged: "Yes - update existing docs included in the ZIP when needed",
      });

      const res = await fetch(`/api/dev-ide/codex-handoff/export-package`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: projectId,
          full_change_id: selectedId,
          dev_ide_change_id: exportChange.id,
          module: moduleName,
          implementation_prompt: prompt,
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {}

      if (!res.ok || !data.success) {
        throw new Error((data && (data.error || data.message)) || `Codex handoff export failed (status ${res.status}).`);
      }

      const packagePath = data.package?.package_path || data.package?.outgoing_folder || "configured outgoing folder";
      const runId = data.run?.run_id || data.manifest?.run_id || "created";
      const successMessage = `Codex handoff package exported for ${contextLabel}. Run ${runId}. Outgoing path: ${packagePath}.`;
      setQueueActionMessage(successMessage);
      await markQueueWorkflowStep(exportChange, "packageExported", "success", successMessage);
      await loadCodexHandoffStatuses([exportChange]);
      await loadCodexAutomationState(selectedId);
    } catch (e) {
      const message = e.message || `Codex handoff export failed for ${initialContextLabel}.`;
      setQueueActionMessage(message);
      await markQueueWorkflowStep(linkedChange, "packageExported", "failure", message);
    } finally {
      setQueueActionLoadingId(null);
    }
  };


  const handleExportPlanningPackage = async () => {
    if (!selectedId) return;
    setPlanningExportLoading(true);
    setQueueActionMessage("Preparing Full Change planning package for GPT.");
    try {
      const res = await fetch(`/api/dev-ide/full-change/export-planning-package?full_change_id=${encodeURIComponent(selectedId)}`, { method: "GET" });
      if (!res.ok) {
        let errMsg = `Failed to export planning package (status ${res.status}).`;
        try {
          const data = await res.json();
          if (data && data.error) errMsg = data.error;
          if (data && data.errors) errMsg = data.errors.join(", ");
        } catch {}
        throw new Error(errMsg);
      }

      const blob = await res.blob();
      const contentDisposition = res.headers.get("content-disposition") || "";
      const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fallbackTitle = String((selected && selected.title) || `full_change_${selectedId}`).toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || `full_change_${selectedId}`;
      const filename = filenameMatch && filenameMatch[1]
        ? decodeURIComponent(filenameMatch[1])
        : `${fallbackTitle}_planning_export.zip`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setQueueActionMessage("Full Change planning package exported. Preparing matching code/docs context ZIP.");

      try {
        const params = new URLSearchParams();
        params.set("module", "ALL");
        if (selected && selected.project_id) {
          params.set("project_id", selected.project_id);
        }
        params.set("include_documents", "true");

        const contextRes = await fetch(`/api/dev-ide/zip-module?${params.toString()}`, { method: "GET" });
        if (!contextRes.ok) {
          let errMsg = `Failed to download code/docs context ZIP (status ${contextRes.status}).`;
          try {
            const data = await contextRes.json();
            if (data && data.error) errMsg = data.error;
          } catch {}
          throw new Error(errMsg);
        }

        const contextBlob = await contextRes.blob();
        const contextUrl = window.URL.createObjectURL(contextBlob);
        const contextLink = document.createElement("a");
        contextLink.href = contextUrl;
        contextLink.download = "all_modules_dev_ide_files_with_docs.zip";
        document.body.appendChild(contextLink);
        contextLink.click();
        contextLink.remove();
        window.URL.revokeObjectURL(contextUrl);

        setQueueActionMessage("Full Change planning package and matching all-modules code/docs context ZIP exported for GPT.");
      } catch (contextError) {
        setQueueActionMessage((contextError && contextError.message)
          ? `Full Change planning package exported, but code/docs context ZIP failed: ${contextError.message}`
          : "Full Change planning package exported, but code/docs context ZIP failed.");
      }
    } catch (e) {
      setQueueActionMessage(e.message || "Full Change planning package export failed.");
    } finally {
      setPlanningExportLoading(false);
    }
  };

  const resetPlanningImportState = () => {
    setPlanningImportPreview(null);
    setPlanningImportManifest(null);
    planningImportZipRef.current = null;
    setPlanningImportGptResponse("");
    setPlanningImportErrors([]);
    setPlanningImportWarnings([]);
  };

  const handlePlanningImportFileSelected = async (event) => {
    if (!selectedId) return;
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    event.target.value = "";
    if (!file) return;

    resetPlanningImportState();
    planningImportZipRef.current = file;
    setPlanningImportLoading(true);
    setPlanningImportDialogOpen(true);
    setQueueActionMessage("Validating Full Change planning import package from GPT.");

    try {
      const formData = new FormData();
      formData.append("full_change_id", selectedId);
      formData.append("file", file);
      const res = await fetch("/api/dev-ide/full-change/import-planning-package/preview", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!data.success) {
        setPlanningImportErrors(data.errors || [data.error || "Planning import validation failed."]);
        setPlanningImportWarnings(data.warnings || []);
        setPlanningImportPreview(data.preview || null);
        setPlanningImportManifest(data.manifest || null);
        setPlanningImportGptResponse(data.gpt_response || "");
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Planning import validation failed.");
      }
      setPlanningImportPreview(data.preview || null);
      setPlanningImportManifest(data.manifest || null);
      setPlanningImportGptResponse(data.gpt_response || "");
      setPlanningImportWarnings(data.warnings || []);
      setPlanningImportErrors([]);
      setQueueActionMessage("Planning import preview loaded. Review before applying; no changes have been saved yet.");
    } catch (e) {
      setQueueActionMessage(e.message || "Planning import preview failed.");
    } finally {
      setPlanningImportLoading(false);
    }
  };

  const handleCancelPlanningImport = () => {
    resetPlanningImportState();
    setPlanningImportDialogOpen(false);
    setQueueActionMessage("Planning import cancelled. No planning changes were applied.");
  };

  const handleApplyPlanningImport = async () => {
    if (!selectedId || !planningImportManifest) return;
    setPlanningImportApplyLoading(true);
    setPlanningImportErrors([]);
    try {
      const schemaV2 = Number(planningImportManifest.schema_version) === 2;
      let options;
      if (schemaV2) {
        if (!planningImportZipRef.current) throw new Error("Original planning ZIP is missing. Select the ZIP and preview it again.");
        const formData = new FormData();
        formData.append("full_change_id", selectedId);
        formData.append("file", planningImportZipRef.current);
        options = { method: "POST", body: formData };
      } else {
        // Preserve the original schema v1 Apply contract for linked changes/tasks.
        options = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ full_change_id: selectedId, manifest: planningImportManifest }),
        };
      }
      const res = await fetch("/api/dev-ide/full-change/import-planning-package/apply", options);
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Planning import apply failed.");
      }
      setSelected(data.full_change || selected);
      setLinkedChanges(data.linked_changes || []);
      setTasks(data.tasks || []);
      await loadList();
      await loadLinked(selectedId);
      await loadTasks(selectedId);
      setPlanningDocumentsRefreshVersion((version) => version + 1);
      setPlanningImportWarnings(data.warnings || []);
      setQueueActionMessage("Planning import applied. Planning documents, linked changes and tasks have been refreshed.");
      setPlanningImportDialogOpen(false);
      resetPlanningImportState();
    } catch (e) {
      setPlanningImportErrors([e.message || "Planning import apply failed."]);
      setQueueActionMessage(e.message || "Planning import apply failed.");
    } finally {
      setPlanningImportApplyLoading(false);
    }
  };

  const getPlanningActionChipColor = (action) => {
    if (action === "delete") return "error";
    if (action === "create") return "success";
    if (action === "update") return "warning";
    return "default";
  };

  const renderPlanningFieldChanges = (fieldChanges) => {
    if (!fieldChanges || Object.keys(fieldChanges).length === 0) {
      return <Typography variant="caption" color="text.secondary">No field changes proposed.</Typography>;
    }

    return (
      <Box sx={{ mt: 1, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1 }}>
        {Object.entries(fieldChanges).map(([field, change]) => (
          <Box key={field} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "160px 1fr 1fr" }, gap: 1, p: 0.75, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{field}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>From: {String(change.from ?? "")}</Typography>
            <Typography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>To: {String(change.to ?? "")}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const getPlanningActionCounts = (rows = []) => {
    const counts = { create: 0, update: 0, "no-change": 0, delete: 0 };
    rows.forEach((row) => {
      const action = String(row?.action || "no-change").trim().toLowerCase() || "no-change";
      if (action === "no_change" || action === "no change") {
        counts["no-change"] += 1;
      } else if (Object.prototype.hasOwnProperty.call(counts, action)) {
        counts[action] += 1;
      }
    });
    return counts;
  };

  const renderPlanningActionCounts = (label, rows, summaryPrefix, previewSummary = {}) => {
    const rowCounts = getPlanningActionCounts(rows || []);
    const actions = ["create", "update", "no-change", "delete"];
    const getSummaryValue = (action) => {
      const normalizedAction = action.replace("-", "_");
      const pluralPrefix = summaryPrefix.endsWith("s") ? summaryPrefix : `${summaryPrefix}s`;
      const shortPrefix = summaryPrefix.replace("linked_dev_ide_change", "linked_change");
      const pluralShortPrefix = shortPrefix.endsWith("s") ? shortPrefix : `${shortPrefix}s`;
      const candidates = [
        `${summaryPrefix}_${action}`,
        `${summaryPrefix}_${normalizedAction}`,
        `${pluralPrefix}_${action}`,
        `${pluralPrefix}_${normalizedAction}`,
        `${shortPrefix}_${action}`,
        `${shortPrefix}_${normalizedAction}`,
        `${pluralShortPrefix}_${action}`,
        `${pluralShortPrefix}_${normalizedAction}`,
      ];
      for (const candidate of candidates) {
        if (previewSummary && previewSummary[candidate] !== undefined) {
          return previewSummary[candidate];
        }
      }
      return rowCounts[action];
    };

    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
          {actions.map((action) => (
            <Chip
              key={`${summaryPrefix}-${action}`}
              size="small"
              label={`${action}: ${getSummaryValue(action) ?? 0}`}
              color={getPlanningActionChipColor(action)}
              variant="outlined"
            />
          ))}
        </Stack>
      </Box>
    );
  };

  const getPlanningRowOrder = (row) => (
    row?.order ??
    row?.full_change_order ??
    row?.queue_order ??
    row?.display_order ??
    row?.proposed_order ??
    row?.sort_order ??
    row?.proposed?.order ??
    row?.proposed?.full_change_order ??
    null
  );

  const getPlanningRowValue = (row, fieldNames) => {
    const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
    const proposed = row?.proposed || {};
    for (const name of names) {
      if (proposed[name] !== undefined && proposed[name] !== null && proposed[name] !== "") return proposed[name];
      if (row && row[name] !== undefined && row[name] !== null && row[name] !== "") return row[name];
      const change = row?.field_changes?.[name];
      if (change && change.to !== undefined && change.to !== null && change.to !== "") return change.to;
    }
    return "";
  };

  const formatPlanningPreviewValue = (value) => {
    if (Array.isArray(value)) return value.map((item) => String(item || "").trim()).filter(Boolean).join("\n");
    if (value && typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value || "").trim();
  };

  const renderLinkedPlanningDetails = (row) => {
    const detailRows = [
      ["Description", getPlanningRowValue(row, "description")],
      ["Scope summary", getPlanningRowValue(row, "scope_summary")],
      ["Status", getPlanningRowValue(row, "status")],
      ["Module", getPlanningRowValue(row, ["module", "module_name"])],
      ["Acceptance criteria", getPlanningRowValue(row, "acceptance_criteria")],
    ]
      .map(([label, value]) => [label, formatPlanningPreviewValue(value)])
      .filter(([, value]) => value);

    const proposedId = getPlanningRowValue(row, ["id", "dev_ide_change_id"]);
    const proposedOrder = getPlanningRowOrder(row);
    const planningKey = getPlanningRowValue(row, "planning_key");

    if (!detailRows.length && !proposedId && !proposedOrder && !planningKey) return null;

    return (
      <Box sx={{ mt: 1 }}>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75, mb: detailRows.length ? 1 : 0 }}>
          {planningKey ? <Chip size="small" label={`planning key: ${planningKey}`} variant="outlined" /> : null}
          {proposedId ? <Chip size="small" label={`id: ${proposedId}`} variant="outlined" /> : null}
          {proposedOrder !== null && proposedOrder !== undefined && proposedOrder !== "" ? <Chip size="small" label={`order: ${proposedOrder}`} variant="outlined" /> : null}
        </Stack>
        {detailRows.length ? (
          <Box sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1, overflow: "hidden" }}>
            {detailRows.map(([label, value]) => (
              <Box key={label} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "160px 1fr" }, gap: 1, p: 0.75, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
                <Typography variant="caption" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderPlanningPreviewSection = (title, rows, summaryPrefix, previewSummary = {}) => (
    <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, mb: 2, overflow: "hidden" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      {renderPlanningActionCounts("Action summary", rows, summaryPrefix, previewSummary)}
      {(!rows || rows.length === 0) ? (
        <Typography variant="caption" color="text.secondary">None proposed.</Typography>
      ) : (
        <Stack spacing={1}>
          {rows.map((row, index) => {
            const rowOrder = getPlanningRowOrder(row);
            return (
              <Paper key={`${title}-${index}`} variant="outlined" sx={{ p: { xs: 1, sm: 1.25 }, bgcolor: "background.default", overflow: "hidden" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", mb: 0.5 }}>
                  <Chip size="small" label={row.action || "no-change"} color={getPlanningActionChipColor(row.action)} />
                  {rowOrder !== null && rowOrder !== undefined && rowOrder !== "" ? (
                    <Chip size="small" label={`order: ${rowOrder}`} variant="outlined" />
                  ) : null}
                  <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>{row.title || row.task_title || "Untitled"}</Typography>
                  {row.existing_id ? <Typography variant="caption" color="text.secondary">existing #{row.existing_id}</Typography> : null}
                  {row.match_method ? <Typography variant="caption" color="text.secondary">match: {row.match_method}</Typography> : null}
                </Stack>
                {row.planning_key ? <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>planning_key: {row.planning_key}</Typography> : null}
                {summaryPrefix === "linked_dev_ide_change" ? renderLinkedPlanningDetails(row) : null}
                {(row.warnings || []).map((warning, idx) => (
                  <Alert key={idx} severity="warning" sx={{ mt: 0.75, py: 0.25 }}>{warning}</Alert>
                ))}
                {renderPlanningFieldChanges(row.field_changes)}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Paper>
  );

  const renderPlanningImportDialog = () => {
    const previewSummary = (planningImportPreview && planningImportPreview.summary) || {};
    const fullChangePreview = (planningImportPreview && planningImportPreview.full_change) || { action: "no-change", field_changes: {}, warnings: [] };
    const manifestNotes = planningImportPreview && Array.isArray(planningImportPreview.notes) ? planningImportPreview.notes : [];
    const manifestWarnings = planningImportPreview && Array.isArray(planningImportPreview.manifest_warnings) ? planningImportPreview.manifest_warnings : [];
    const hasValidPlanningImportManifest = Boolean(planningImportManifest) && planningImportErrors.length === 0;

    return (
      <Dialog
        data-testid="full-change-planning-import-dialog"
        open={planningImportDialogOpen}
        onClose={() => {
          if (!planningImportLoading && !planningImportApplyLoading) handleCancelPlanningImport();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "calc(100% - 64px)" },
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle sx={{ pr: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>Full Change Planning Import Preview</DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Review before applying. This planning import may create or version Full Change-linked Planning documents in Document Manager, alongside linked changes and tasks. It never imports application source code or SQL.
          </Alert>
          {planningImportLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Validating planning import package…</Typography>
            </Stack>
          )}

          {planningImportErrors.map((message, index) => (
            <Alert key={`planning-import-error-${index}`} severity="error" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {planningImportWarnings.map((message, index) => (
            <Alert key={`planning-import-warning-${index}`} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {manifestWarnings.map((message, index) => (
            <Alert key={`planning-manifest-warning-${index}`} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {!planningImportPreview && !planningImportLoading ? (
            <Typography variant="body2" color="text.secondary">
              No valid planning import preview is currently loaded.
            </Typography>
          ) : null}

          {planningImportPreview ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, overflow: "hidden" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview summary</Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75, mb: 1 }}>
                  {Object.entries(previewSummary).map(([key, value]) => (
                    <Chip key={key} size="small" label={`${key}: ${value ?? 0}`} variant="outlined" />
                  ))}
                  {Object.entries(previewSummary).length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No backend summary counts were provided.</Typography>
                  ) : null}
                </Stack>
                {renderPlanningActionCounts("Linked DEV IDE changes", planningImportPreview.linked_dev_ide_changes, "linked_dev_ide_change", previewSummary)}
                {renderPlanningActionCounts("Tasks", planningImportPreview.tasks, "task", previewSummary)}
                {manifestNotes.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Planning notes</Typography>
                    {manifestNotes.map((note, index) => (
                      <Typography key={`planning-note-${index}`} variant="caption" sx={{ display: "block" }}>
                        • {String(note)}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Paper>

              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, overflow: "hidden" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Full Change field changes</Typography>
                <Paper variant="outlined" sx={{ p: 1, overflow: "hidden" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", mb: 0.5 }}>
                    <Chip size="small" label={fullChangePreview.action || "no-change"} color={getPlanningActionChipColor(fullChangePreview.action)} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Parent Full Change</Typography>
                  </Stack>
                  {(fullChangePreview.warnings || []).map((warning, index) => (
                    <Alert key={`full-change-planning-warning-${index}`} severity="warning" sx={{ mt: 0.75, py: 0.25 }}>
                      {warning}
                    </Alert>
                  ))}
                  {renderPlanningFieldChanges(fullChangePreview.field_changes)}
                </Paper>
              </Paper>

              {renderPlanningPreviewSection("Linked DEV IDE change proposed actions", planningImportPreview.linked_dev_ide_changes, "linked_dev_ide_change", previewSummary)}
              {renderPlanningPreviewSection("Task proposed actions", planningImportPreview.tasks, "task", previewSummary)}
              {Array.isArray(planningImportPreview.planning_documents) ? (
                <Paper variant="outlined" sx={{ p: 1.5, overflow: "hidden" }} data-testid="full-change-planning-document-preview">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Planning document changes</Typography>
                  {planningImportPreview.planning_documents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No Planning document changes proposed.</Typography>
                  ) : planningImportPreview.planning_documents.map((doc, index) => (
                    <Paper key={`${doc.slug}-${index}`} variant="outlined" sx={{ p: 1.5, mb: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Chip size="small" label={doc.action} color={doc.conflict ? "error" : getPlanningActionChipColor(doc.action)} />
                        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{doc.title || doc.slug}</Typography>
                        <Typography variant="caption" color="text.secondary">{doc.slug} · exported version {doc.base_version ?? "new"} · current version {doc.current_version ?? "none"}</Typography>
                      </Stack>
                      {(doc.warnings || []).map((warning, w) => <Alert severity="error" key={w} sx={{ mt: 1 }}>{warning}</Alert>)}
                      {doc.content_diff ? <Box component="pre" sx={{ mt: 1, p: 1, fontSize: 11, whiteSpace: "pre-wrap", overflowWrap: "anywhere", bgcolor: "action.hover", maxHeight: 240, overflow: "auto" }}>{doc.content_diff}</Box> : null}
                    </Paper>
                  ))}
                </Paper>
              ) : null}

              {planningImportGptResponse ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>GPT_PLANNING_RESPONSE.md</Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      fontSize: 12,
                      maxHeight: { xs: 320, sm: 260 },
                      overflow: "auto",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {planningImportGptResponse}
                  </Paper>
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            position: { xs: "sticky", sm: "static" },
            bottom: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            borderTop: { xs: "1px solid rgba(0,0,0,0.12)", sm: "none" },
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 1 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button data-testid="full-change-planning-import-cancel" onClick={handleCancelPlanningImport} disabled={planningImportLoading || planningImportApplyLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
            Cancel
          </Button>
          <Button
            data-testid="full-change-planning-import-apply"
            variant="contained"
            onClick={handleApplyPlanningImport}
            disabled={planningImportLoading || planningImportApplyLoading || !hasValidPlanningImportManifest}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {planningImportApplyLoading ? "Applying..." : "Apply Planning Import"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleRunTargetPreview = async () => {
    if (!selectedId) return;

    const sourceProjectId = String(targetPreviewSourceProjectId || "").trim();
    const targetProjectId = String(targetPreviewTargetProjectId || "").trim();
    if (!sourceProjectId) {
      setTargetPreviewError("Select a source project first.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (!targetProjectId) {
      setTargetPreviewError("Select a target project first.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (sourceProjectId === targetProjectId) {
      setTargetPreviewError("Source and target projects must be different.");
      setTargetPreviewDialogOpen(true);
      return;
    }

    setTargetPreviewDialogOpen(true);
    setTargetPreviewLoading(true);
    setTargetPreviewError(null);
    setTargetPreviewResult(null);
    setTargetInstallError(null);
    setTargetInstallResult(null);
    setQueueActionMessage(`Building aggregate target migration preview from ${sourceProjectId} to ${targetProjectId}.`);

    try {
      const res = await fetch("/api/dev-ide/full-change/target-preview-context", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          source_project_id: sourceProjectId,
          target_project_id: targetProjectId,
          mode: "preview",
          include_sql: targetPreviewIncludeSql,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Full Change target preview failed.");
      }
      setTargetPreviewResult(data);
      const summary = data.summary || {};
      setQueueActionMessage(
        `Aggregate target preview ready for ${targetProjectId}. ${summary.changed_count || 0} changed candidate(s), ${summary.blocked_count || 0} blocked child change(s), ${summary.skipped_count || 0} skipped child change(s).`
      );
    } catch (e) {
      setTargetPreviewError(e.message || "Full Change target preview failed.");
      setQueueActionMessage(e.message || "Full Change target preview failed.");
    } finally {
      setTargetPreviewLoading(false);
    }
  };



  const handleInstallTargetMigration = async () => {
    if (!selectedId) return;

    const sourceProjectId = String(targetPreviewSourceProjectId || "").trim();
    const targetProjectId = String(targetPreviewTargetProjectId || "").trim();
    if (!sourceProjectId) {
      setTargetInstallError("Select a source project before installing.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (!targetProjectId) {
      setTargetInstallError("Select a target project before installing.");
      setTargetPreviewDialogOpen(true);
      return;
    }
    if (sourceProjectId === targetProjectId) {
      setTargetInstallError("Source and target projects must be different before installing.");
      setTargetPreviewDialogOpen(true);
      return;
    }

    setTargetPreviewDialogOpen(true);
    setTargetInstallLoading(true);
    setTargetInstallError(null);
    setTargetInstallResult(null);
    setQueueActionMessage(`Installing eligible Full Change target migration candidates from ${sourceProjectId} to ${targetProjectId}.`);

    try {
      const res = await fetch("/api/dev-ide/full-change/target-install", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          source_project_id: sourceProjectId,
          target_project_id: targetProjectId,
          include_sql: targetPreviewIncludeSql,
          change_summary: `Full Change ${selectedId} aggregate install to ${targetProjectId}`,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        if (data.path_conflicts || data.duplicate_changed_path_warnings || data.duplicate_path_warnings || data.preview) {
          setTargetInstallResult(data);
          if (data.preview) setTargetPreviewResult(data.preview);
        }
        throw new Error(data.error || data.block_reason || "Full Change target install failed.");
      }
      setTargetInstallResult(data);
      if (data.preview) setTargetPreviewResult(data.preview);
      const summary = data.summary || {};
      setQueueActionMessage(
        `Full Change target install finished for ${targetProjectId}. ${summary.applied_count || 0} file(s) applied, ${summary.skipped_count || 0} skipped, ${summary.error_count || 0} error(s), ${summary.warning_count || 0} warning(s).`
      );
    } catch (e) {
      setTargetInstallError(e.message || "Full Change target install failed.");
      setQueueActionMessage(e.message || "Full Change target install failed.");
    } finally {
      setTargetInstallLoading(false);
    }
  };


  const renderTargetPreviewSummaryChips = (summary = {}) => {
    const keys = [
      "linked_change_count",
      "total_count",
      "previewed_count",
      "skipped_count",
      "blocked_count",
      "candidate_count",
      "changed_count",
      "unchanged_count",
      "created_count",
      "updated_count",
      "error_count",
      "warning_count",
    ];
    return (
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75 }}>
        {keys.map((key) => (
          <Chip key={key} size="small" label={`${key}: ${summary[key] ?? 0}`} variant="outlined" />
        ))}
      </Stack>
    );
  };



  const renderDuplicateTargetPathWarnings = (warnings = [], keyPrefix = "target-duplicate-warning") => {
    if (!warnings.length) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5, color: "warning.main" }}>
          Duplicate changed target path warnings
        </Typography>
        {warnings.map((warning, idx) => (
          <Alert key={`${keyPrefix}-${idx}`} severity="warning" sx={{ mb: 0.75, py: 0.25 }}>
            {warning.path}: linked changes {(warning.linked_changes || []).map((item) => `#${item.change_id}${item.order !== null && item.order !== undefined ? ` order ${item.order}` : ""}`).join(", ")}. Install will continue in Full Change order; later child changes may be skipped if the single-change apply recompute finds the file unchanged.
          </Alert>
        ))}
      </Box>
    );
  };


  const renderTargetInstallResult = () => {
    if (!targetInstallResult) return null;
    const groups = targetInstallResult.linked_changes || [];
    const duplicateWarnings = targetInstallResult.duplicate_changed_path_warnings || targetInstallResult.duplicate_path_warnings || targetInstallResult.path_conflicts || [];
    return (
      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Install result</Typography>
        {renderTargetPreviewSummaryChips(targetInstallResult.summary || {})}
        {renderDuplicateTargetPathWarnings(duplicateWarnings, "target-install-duplicate-warning")}
        {groups.length > 0 ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {groups.map((group, idx) => (
              <Paper key={`target-install-result-${group.change_id || idx}`} variant="outlined" sx={{ p: 1, bgcolor: "background.default" }}>
                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mb: 0.75 }}>
                  <Chip size="small" label={`Change #${group.change_id || group.id || "?"}`} variant="outlined" />
                  <Chip size="small" label={`install: ${group.install_status || "—"}`} color={group.install_status === "applied" ? "success" : group.install_status === "error" || group.install_status === "blocked" ? "error" : "warning"} variant={group.install_status === "applied" ? "filled" : "outlined"} />
                  {group.order !== null && group.order !== undefined ? <Chip size="small" label={`order: ${group.order}`} variant="outlined" /> : null}
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word", mb: 0.75 }}>{group.title || "Untitled linked change"}</Typography>
                {group.block_reason ? <Alert severity="error" sx={{ mb: 0.75, py: 0.25 }}>{group.block_reason}</Alert> : null}
                {group.skip_reason ? <Alert severity="warning" sx={{ mb: 0.75, py: 0.25 }}>{group.skip_reason}</Alert> : null}
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", mb: 0.75 }}>
                  <Chip size="small" label={`applied: ${(group.summary && group.summary.applied_count) || 0}`} variant="outlined" />
                  <Chip size="small" label={`skipped: ${(group.summary && group.summary.skipped_count) || 0}`} variant="outlined" />
                  <Chip size="small" label={`blocked: ${(group.summary && group.summary.blocked_count) || 0}`} variant="outlined" />
                  <Chip size="small" label={`errors: ${(group.summary && group.summary.error_count) || 0}`} variant="outlined" />
                </Stack>
                {(group.applied || []).map((row, rowIdx) => (
                  <Typography key={`target-install-applied-${group.change_id}-${rowIdx}`} variant="caption" sx={{ display: "block", fontFamily: "monospace", overflowWrap: "anywhere" }}>
                    applied: {row.path || "(no path)"}{row.backup_path ? ` · backup: ${row.backup_path}` : ""}
                  </Typography>
                ))}
                {(group.errors || []).map((row, rowIdx) => (
                  <Alert key={`target-install-error-${group.change_id}-${rowIdx}`} severity="error" sx={{ mt: 0.75, py: 0.25 }}>
                    {row.path ? `${row.path}: ` : ""}{row.error || row.message || JSON.stringify(row)}
                  </Alert>
                ))}
              </Paper>
            ))}
          </Stack>
        ) : null}
      </Paper>
    );
  };


  const renderTargetPreviewDialog = () => {
    const groups = (targetPreviewResult && targetPreviewResult.linked_changes) || [];
    const summary = (targetPreviewResult && targetPreviewResult.summary) || {};

    return (
      <Dialog
        open={targetPreviewDialogOpen}
        onClose={() => { if (!targetPreviewLoading) setTargetPreviewDialogOpen(false); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "calc(100% - 64px)" },
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle>Full Change Aggregate Target Preview</DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            This dialog is for aggregate target migration only. It does not import parent planning ZIPs and does not replace the child returned-package Import Review; it previews and installs already-reviewed child change files from the selected source project into the selected target project.
          </Alert>

          {targetPreviewLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Building aggregate target preview…</Typography>
            </Stack>
          )}

          {targetPreviewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {targetPreviewError}
            </Alert>
          )}

          {targetInstallError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {targetInstallError}
            </Alert>
          )}

          {targetInstallLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Installing aggregate target candidates…</Typography>
            </Stack>
          )}

          {renderTargetInstallResult()}

          {!targetPreviewLoading && !targetPreviewError && !targetPreviewResult && (
            <Typography variant="body2" color="text.secondary">
              No aggregate target preview loaded yet. Choose source and target projects from the Full Change queue area, then run Preview to Target to see which linked child files would migrate before using Install to Target.
            </Typography>
          )}

          {targetPreviewResult && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview summary</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Source project: {targetPreviewResult.source_project_id || "—"} · Target project: {targetPreviewResult.target_project_id || "—"} · Include SQL: {targetPreviewResult.include_sql ? "yes" : "no"}
                </Typography>
                {renderTargetPreviewSummaryChips(summary)}
                {renderDuplicateTargetPathWarnings(targetPreviewResult.duplicate_changed_path_warnings || targetPreviewResult.duplicate_path_warnings || [], "target-preview-duplicate-warning")}
              </Paper>

              {groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No linked child change preview groups were returned.</Typography>
              ) : null}

              {groups.map((group, groupIndex) => {
                const candidates = group.candidates || [];
                const errors = group.errors || [];
                return (
                  <Paper key={group.change_id || groupIndex} variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, overflow: "hidden" }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", mb: 1 }}>
                      <Chip size="small" label={`Change #${group.change_id || group.id || "?"}`} variant="outlined" />
                      <Chip size="small" label={`Status: ${group.preview_status || "previewed"}`} color={group.preview_status === "blocked" ? "error" : group.preview_status === "skipped" ? "warning" : "success"} variant={group.preview_status === "previewed" ? "filled" : "outlined"} />
                      {group.order !== null && group.order !== undefined ? <Chip size="small" label={`order: ${group.order}`} variant="outlined" /> : null}
                      <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>{group.title || "Untitled linked change"}</Typography>
                    </Stack>

                    {group.skip_reason ? <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>{group.skip_reason}</Alert> : null}
                    {group.block_reason ? <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{group.block_reason}</Alert> : null}

                    <Box sx={{ mb: 1 }}>{renderTargetPreviewSummaryChips(group.summary || {})}</Box>

                    {errors.length > 0 ? (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>Errors</Typography>
                        {errors.map((err, idx) => (
                          <Alert key={`target-preview-error-${group.change_id || groupIndex}-${idx}`} severity="error" sx={{ mb: 0.75, py: 0.25 }}>
                            {err.path ? `${err.path}: ` : ""}{err.error || err.message || JSON.stringify(err)}
                          </Alert>
                        ))}
                      </Box>
                    ) : null}

                    {candidates.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">No candidate files returned for this linked child change.</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {candidates.map((candidate, idx) => (
                          <Paper key={`${candidate.path || idx}-${idx}`} variant="outlined" sx={{ p: 1, bgcolor: "background.default", overflow: "hidden" }}>
                            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mb: 0.75 }}>
                              <Chip size="small" label={`kind: ${candidate.kind || "—"}`} variant="outlined" />
                              <Chip size="small" label={`target_exists: ${candidate.target_exists ? "true" : "false"}`} variant="outlined" />
                              <Chip size="small" label={`changed: ${candidate.changed ? "true" : "false"}`} color={candidate.changed ? "warning" : "default"} variant="outlined" />
                              <Chip size="small" label={`source_exists: ${candidate.source_exists ? "true" : "false"}`} variant="outlined" />
                            </Stack>
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, overflowWrap: "anywhere", mb: 0.75 }}>
                              {candidate.path || "(no path)"}
                            </Typography>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1,
                                whiteSpace: "pre-wrap",
                                fontFamily: "monospace",
                                fontSize: 12,
                                maxHeight: { xs: 260, sm: 360 },
                                overflow: "auto",
                                bgcolor: "background.paper",
                              }}
                            >
                              {candidate.diff_text || "(no diff text)"}
                            </Paper>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            position: { xs: "sticky", sm: "static" },
            bottom: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            borderTop: { xs: "1px solid rgba(0,0,0,0.12)", sm: "none" },
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 1 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button onClick={() => setTargetPreviewDialogOpen(false)} disabled={targetPreviewLoading || targetInstallLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>Close</Button>
          <Button onClick={handleRunTargetPreview} variant="outlined" disabled={targetPreviewLoading || targetInstallLoading || !selectedId} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {targetPreviewLoading ? "Previewing…" : "Refresh Preview"}
          </Button>
          <Button onClick={handleInstallTargetMigration} variant="contained" color="warning" disabled={targetPreviewLoading || targetInstallLoading || !selectedId || !targetPreviewSourceProjectId || !targetPreviewTargetProjectId || targetPreviewSourceProjectId === targetPreviewTargetProjectId} sx={{ width: { xs: "100%", sm: "auto" } }}>
            {targetInstallLoading ? "Installing…" : "Install to Target"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleQueueImportReturnedPackage = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;

    const initialContextLabel = `linked DEV IDE change #${linkedChange.id} — ${linkedChange.title || "Untitled change"}`;
    setQueueActionLoadingId(`import-${linkedChange.id}`);
    setQueueActionMessage(`Opening Import Review in Full Change for ${initialContextLabel}.`);

    try {
      const importChange = await loadFullQueueDevIdeChange(linkedChange);
      const projectId = getQueueProjectId(importChange) || selectedProjectId || "";
      const contextLabel = `linked DEV IDE change #${importChange.id} — ${importChange.title || "Untitled change"}`;

      if (projectId && typeof onSelectedProjectChange === "function") {
        onSelectedProjectChange(projectId);
      }

      if (typeof onImportReturnedPackage !== "function") {
        throw new Error("The shared import workflow is not available from Full Change.");
      }

      await onImportReturnedPackage({
        changeId: importChange.id,
        projectId,
        includeDocuments: true,
        source: "full-change-returned-package",
        fullChangeId: selected && selected.id ? selected.id : null,
        autoSaveAfterImport: true,
      });

      const message = `Import Review opened in Full Change for ${contextLabel}. Import ZIP with docs is ready to load the returned package and will auto-save eligible changed files after a successful import.`;
      setQueueActionMessage(message);
    } catch (e) {
      setQueueActionMessage(e.message || `Import returned package failed to start for ${initialContextLabel}.`);
    } finally {
      setQueueActionLoadingId(null);
    }
  };

  const handleQueueImportCodexReturn = async (linkedChange, codexReturnedZipContext = {}, options = {}) => {
    if (!linkedChange || !linkedChange.id) return;

    const initialContextLabel = `linked DEV IDE change #${linkedChange.id} — ${linkedChange.title || "Untitled change"}`;
    setQueueActionLoadingId(`codex-import-${linkedChange.id}`);
    const manualReviewOnly = Boolean(options.manualReviewOnly);
    setQueueActionMessage(`Checking Codex incoming handoff folder for ${initialContextLabel}.`);

    try {
      const importChange = await loadFullQueueDevIdeChange(linkedChange);
      const projectId = codexReturnedZipContext.project_id || getQueueProjectId(importChange) || selectedProjectId || "";
      const fullChangeId = codexReturnedZipContext.full_change_id || (selected && selected.id ? selected.id : null);
      const devIdeChangeId = codexReturnedZipContext.dev_ide_change_id || importChange.id;
      const contextLabel = `linked DEV IDE change #${importChange.id} — ${importChange.title || "Untitled change"}`;

      if (projectId && typeof onSelectedProjectChange === "function") {
        onSelectedProjectChange(projectId);
      }

      if (typeof onImportReturnedPackage !== "function") {
        throw new Error("The shared import workflow is not available from Full Change.");
      }

      const params = new URLSearchParams();
      params.set("project_id", projectId || "default");
      if (fullChangeId) params.set("full_change_id", fullChangeId);
      params.set("dev_ide_change_id", devIdeChangeId);

      const res = await fetch(`/api/dev-ide/codex-handoff/status?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error((data && data.error) || `Could not check Codex incoming folder (status ${res.status}).`);
      }

      const contextReturnedZip = codexReturnedZipContext.returned_zip || {};
      const returnedZip = {
        ...contextReturnedZip,
        ...(data.returned_zip || {}),
      };
      if (codexReturnedZipContext.run_id && !returnedZip.run_id) returnedZip.run_id = codexReturnedZipContext.run_id;
      if (codexReturnedZipContext.filename && !returnedZip.filename) returnedZip.filename = codexReturnedZipContext.filename;
      if (codexReturnedZipContext.path && !returnedZip.path) returnedZip.path = codexReturnedZipContext.path;
      if (!returnedZip.filename && !returnedZip.path && !returnedZip.run_id) {
        const statusMessage = data.operator_status && data.operator_status.message ? ` ${data.operator_status.message}` : "";
        throw new Error(`No returned Codex ZIP was found for the latest handoff run yet.${statusMessage} Place Codex's DEV IDE-compatible implementation ZIP in the configured incoming handoff folder for this linked change, then use Open Codex Import Review or Run Codex Auto Import again.`);
      }
      if (returnedZip.valid_for_context === false) {
        const errors = returnedZip.metadata_validation_errors || [];
        throw new Error(errors.length ? errors.join("; ") : "Returned Codex ZIP failed handoff metadata validation.");
      }

      const controlledSaveEnabled = Boolean(codexAutomationState?.automation_metadata_json?.controlled_save_enabled);
      const forceSaveAllEnabled = Boolean(codexAutomationState?.automation_metadata_json?.force_save_all_returned_files_enabled);
      const openImportReviewFallback = async () => {
        await onImportReturnedPackage({
          changeId: importChange.id,
          projectId,
          includeDocuments: true,
          source: "full-change-codex-handoff",
          fullChangeId,
          initialZipPath: returnedZip.path || "",
          autoImportFromPath: true,
          codexHandoffContext: {
            projectId,
            fullChangeId,
            devIdeChangeId,
            runId: returnedZip.run_id || null,
            filename: returnedZip.filename || null,
            returnedZipPath: returnedZip.path || null,
            controlledSaveEnabled,
            forceSaveAllEnabled,
            manualReviewOnly,
          },
        });
      };

      if (manualReviewOnly) {
        await openImportReviewFallback();
        setQueueActionMessage(
          `Import Review opened in Full Change for ${contextLabel}. Returned Codex ZIP '${returnedZip.filename || returnedZip.path || "detected"}' is being loaded into Import Review only. No files are saved, SQL is not executed, and no build/restart/deployment/verification was run.`,
        );
        await loadCodexHandoffStatuses([importChange]);
        await loadCodexAutomationState(selectedId);
        return;
      }

      if (forceSaveAllEnabled) {
        const forceRes = await fetch("/api/dev-ide/codex-handoff/import-returned-zip", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: projectId || "default",
            full_change_id: fullChangeId,
            dev_ide_change_id: devIdeChangeId,
            run_id: returnedZip.run_id || null,
            filename: returnedZip.filename || null,
            force_save: true,
          }),
        });
        const forceData = await forceRes.json();
        if (!forceRes.ok || !forceData.success) {
          throw new Error((forceData && forceData.error) || `Force-save Codex return failed to start (status ${forceRes.status}).`);
        }

        const forceSave = forceData?.codex_handoff?.force_save_all || {};
        if (["saved", "skipped"].includes(forceSave.status)) {
          const savedCode = forceSave.saved_code_count || 0;
          const savedDocs = forceSave.saved_document_count || 0;
          setQueueActionMessage(
            forceSave.message ||
              `Force-save completed for ${contextLabel}: ${savedCode} code file(s), ${savedDocs} document(s). No build, restart, deployment, or verification was run.`,
          );
        } else {
          setQueueActionMessage(
            forceSave.message
              ? `${forceSave.message} No automatic retry was attempted. Use Open Codex Import Review if recovery is needed.`
              : `Force-save did not complete for ${contextLabel}. No automatic retry was attempted. Use Open Codex Import Review if recovery is needed.`,
          );
        }
        await loadCodexHandoffStatuses([importChange]);
        await loadCodexAutomationState(selectedId);
        return;
      }

      if (controlledSaveEnabled) {
        const saveRes = await fetch("/api/dev-ide/codex-handoff/import-returned-zip", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: projectId || "default",
            full_change_id: fullChangeId,
            dev_ide_change_id: devIdeChangeId,
            run_id: returnedZip.run_id || null,
            filename: returnedZip.filename || null,
            auto_save: true,
          }),
        });
        const saveData = await saveRes.json();
        if (!saveRes.ok || !saveData.success) {
          throw new Error((saveData && saveData.error) || `Controlled Codex auto-save failed to start (status ${saveRes.status}).`);
        }

        const autoSave = saveData?.codex_handoff?.controlled_auto_save || {};
        if (["saved", "skipped"].includes(autoSave.status)) {
          const savedCode = autoSave.saved_code_count || 0;
          const savedDocs = autoSave.saved_document_count || 0;
          setQueueActionMessage(
            autoSave.message ||
              `Controlled Codex auto-save completed for ${contextLabel}: ${savedCode} code file(s), ${savedDocs} document(s). No build, restart, deployment, or verification was run.`,
          );
          await loadCodexHandoffStatuses([importChange]);
          await loadCodexAutomationState(selectedId);
          return;
        }

        if (autoSave.status === "blocked") {
          setQueueActionMessage(
            autoSave.message ||
              `Controlled Codex auto-save was blocked for ${contextLabel}. No files were saved.`,
          );
          await loadCodexHandoffStatuses([importChange]);
          await loadCodexAutomationState(selectedId);
          return;
        }

        await openImportReviewFallback();
        setQueueActionMessage(
          autoSave.message
            ? `${autoSave.message} Opening Import Review fallback for ${contextLabel}.`
            : `Controlled Codex auto-save could not complete for ${contextLabel}. Opening Import Review fallback.`,
        );
      } else {
        await openImportReviewFallback();
        setQueueActionMessage(
          `Import Review opened in Full Change for ${contextLabel}. Returned Codex ZIP '${returnedZip.filename}' is being loaded into the existing Import Review workflow without saving files.`,
        );
      }

      await loadCodexHandoffStatuses([importChange]);
      await loadCodexAutomationState(selectedId);
    } catch (e) {
      setQueueActionMessage(e.message || `Import Codex return failed to start for ${initialContextLabel}.`);
    } finally {
      setQueueActionLoadingId(null);
    }
  };

  const handleQueueCopyResultSummary = async (linkedChange) => {
    if (!linkedChange || !linkedChange.id) return;
    setQueueActionLoadingId(`summary-${linkedChange.id}`);
    try {
      const summary = buildQueueResultSummary(linkedChange);
      const copied = await copyQueueTextToClipboard(summary);
      if (!copied) throw new Error("Clipboard copy was blocked by the browser.");
      const message = `Result summary copied for linked DEV IDE change #${linkedChange.id}.`;
      setQueueActionMessage(message);
      await markQueueWorkflowStep(linkedChange, "verificationSummaryCopied", "success", message);
    } catch (e) {
      const message = e.message || `Copy result summary failed for linked DEV IDE change #${linkedChange.id}.`;
      setQueueActionMessage(message);
      await markQueueWorkflowStep(linkedChange, "verificationSummaryCopied", "failure", message);
    } finally {
      setQueueActionLoadingId(null);
    }
  };

  const loadTasks = async (fullChangeId) => {
    if (!fullChangeId) {
      setTasks([]);
      setSelectedTaskId(null);
      setSelectedTask(null);
      return;
    }
    setTasksLoading(true);
    setTasksError(null);
    try {
      const res = await fetch(`/api/dev-ide/full-change/tasks?full_change_id=${encodeURIComponent(fullChangeId)}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load tasks.");
      const taskRows = data.tasks || [];
      setTasks(taskRows);
      setSelectedTask((prev) => {
        if (!prev || !prev.id) return null;
        return taskRows.find((t) => t.id === prev.id) || null;
      });
      setSelectedTaskId((prev) => {
        if (!prev) return taskRows.length === 1 ? taskRows[0].id : prev;
        return taskRows.some((t) => t.id === prev) ? prev : null;
      });
    } catch (e) {
      setTasksError(e.message);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const loadPlannerRuns = async (fullChangeId) => {
    if (!fullChangeId) {
      setPlannerRuns([]);
      return;
    }
    setPlannerRunsLoading(true);
    setPlannerRunsError(null);
    try {
      const res = await fetch(`/api/dev-ide/planner/runs?full_change_id=${encodeURIComponent(fullChangeId)}&limit=200&offset=0`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load planner runs.");
      setPlannerRuns(data.runs || []);
    } catch (e) {
      setPlannerRunsError(e.message);
      setPlannerRuns([]);
    } finally {
      setPlannerRunsLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    loadProjectOptions();
  }, []);

  useEffect(() => {
    // Do not erase an existing selection before the server list has validated it.
    if (selectionValidated) persistSelectedFullChangeId(selectedId);
  }, [selectionValidated, selectedId]);

  useEffect(() => {
    // A child-creation draft/result belongs to one parent, never another.
    createLinkedParentRef.current = selectedId === null ? null : String(selectedId);
    setCreateLinkedDialogOpen(false);
    setCreateLinkedError("");
    setCreatedLinkedChange(null);
  }, [selectedId]);

  useEffect(() => {
    if (selectedId) {
      setPlannerCreateError(null);
      setTaskGenerationInfo(null);
      loadOne(selectedId);
      loadLinked(selectedId);
      loadTasks(selectedId);
      loadPlannerRuns(selectedId);
      loadCodexAutomationState(selectedId);
    } else {
      ++linkedLoadSequenceRef.current;
      ++queueWorkflowLoadSequenceRef.current;
      setQueueWorkflowStatusByChangeId({});
      setLinkedLoadedForId(null);
      setLinkedLoading(false);
      setSelected(null);
      setLinkedChanges([]);
      setTasks([]);
      setSelectedTaskId(null);
      setSelectedTask(null);
      setPlannerRuns([]);
      setPlannerCreateError(null);
      setPlannerGoalText("");
      setTaskGenerationInfo(null);
      setCodexAutomationState(null);
      setCodexAutomationError(null);
    }
  }, [selectedId]);

  useEffect(() => {
    // On parent/project change, never display the previous parent's focused child.
    if (linkedQueueContextRef.current === linkedQueueContextKey) return;
    linkedQueueContextRef.current = linkedQueueContextKey;
    restoredLinkedQueueRef.current = "";
    restoringLinkedQueueScrollRef.current = false;
    linkedFocusRef.current = null;
    setLastInteractedLinkedChild(null);
  }, [linkedQueueContextKey]);

  useEffect(() => {
    if (!linkedQueueContextKey || linkedLoading || linkedError ||
        String(linkedLoadedForId) !== String(selectedId) ||
        restoredLinkedQueueRef.current === linkedQueueContextKey) return;
    restoredLinkedQueueRef.current = linkedQueueContextKey;
    const stored = readLinkedQueueNavigation(linkedQueueContextKey);
    if (!stored) return;
    const validChild = (linkedChanges || []).find((row) =>
      String(row.id) === stored.childId &&
      (!row.project_id || String(row.project_id) === String(selected.project_id))
    );
    if (!validChild) {
      // Remove only the stale child navigation record, not the selected parent.
      persistLinkedQueueNavigation(linkedQueueContextKey, null);
      return;
    }
    const focus = { contextKey: linkedQueueContextKey, childId: stored.childId };
    linkedFocusRef.current = focus;
    setLastInteractedLinkedChild(focus);
    const container = linkedQueueRef.current;
    const row = getVisibleLinkedQueueRow(container, stored.childId);
    if (!row) return;
    restoringLinkedQueueScrollRef.current = true;
    if (container && Number.isFinite(stored.scrollLeft)) {
      container.scrollLeft = Math.min(stored.scrollLeft, Math.max(0, container.scrollWidth - container.clientWidth));
    }
    if (stored.rowTop !== null) {
      const verticalParent = findLinkedQueueScrollParent(container);
      const parentTop = verticalParent ? verticalParent.getBoundingClientRect().top : 0;
      const delta = row.getBoundingClientRect().top - parentTop - stored.rowTop;
      if (Number.isFinite(delta) && Math.abs(delta) < 100000) {
        if (verticalParent) verticalParent.scrollTop += delta;
        else window.scrollBy(0, delta);
      }
    }
    // Scroll restoration is one-shot; status polling and queue reloads never re-scroll.
    window.requestAnimationFrame(() => { restoringLinkedQueueScrollRef.current = false; });
  }, [linkedQueueContextKey, linkedLoadedForId, linkedLoading, linkedError, linkedChanges]);

  useEffect(() => {
    // A child may be unlinked while this panel remains open: revalidate on each
    // successful queue refresh, not just the first navigation restoration.
    if (!linkedQueueContextKey || linkedLoading || linkedError ||
        String(linkedLoadedForId) !== String(selectedId)) return;
    const focus = linkedFocusRef.current;
    if (!focus || focus.contextKey !== linkedQueueContextKey) return;
    const stillLinked = (linkedChanges || []).some((row) =>
      String(row.id) === focus.childId &&
      (!row.project_id || String(row.project_id) === String(selected.project_id))
    );
    if (!stillLinked) {
      linkedFocusRef.current = null;
      setLastInteractedLinkedChild(null);
      persistLinkedQueueNavigation(linkedQueueContextKey, null);
    }
  }, [linkedQueueContextKey, linkedLoadedForId, linkedLoading, linkedError, linkedChanges]);

  useEffect(() => {
    if (!linkedQueueContextKey || linkedLoading || linkedError ||
        String(linkedLoadedForId) !== String(selectedId)) return undefined;
    const container = linkedQueueRef.current;
    if (!container) return undefined;
    const verticalParent = findLinkedQueueScrollParent(container);
    let scheduledFrame = null;
    const captureViewport = () => {
      scheduledFrame = null;
      const focus = linkedFocusRef.current;
      if (restoringLinkedQueueScrollRef.current || !focus ||
          focus.contextKey !== linkedQueueContextKey) return;
      const viewport = getLinkedQueueViewport(focus.childId);
      if (viewport.rowTop === null) return;
      persistLinkedQueueNavigation(linkedQueueContextKey, { childId: focus.childId, ...viewport });
    };
    const scheduleCapture = () => {
      if (!restoringLinkedQueueScrollRef.current && scheduledFrame === null) {
        scheduledFrame = window.requestAnimationFrame(captureViewport);
      }
    };
    (verticalParent || window).addEventListener("scroll", scheduleCapture, { passive: true });
    container.addEventListener("scroll", scheduleCapture, { passive: true });
    return () => {
      (verticalParent || window).removeEventListener("scroll", scheduleCapture);
      container.removeEventListener("scroll", scheduleCapture);
      if (scheduledFrame !== null) window.cancelAnimationFrame(scheduledFrame);
    };
  }, [linkedQueueContextKey, linkedLoadedForId, linkedLoading, linkedError]);

  useEffect(() => {
    if (!importSavedVersion || !selectedId) return;
    loadOne(selectedId);
    loadLinked(selectedId);
    loadCodexAutomationState(selectedId);
  }, [importSavedVersion]);

  useEffect(() => {
    if (linkedChanges && linkedChanges.length > 0 && !linkedLoading && String(linkedLoadedForId) === String(selectedId)) {
      loadQueueWorkflowStatuses(linkedChanges);
      loadCodexHandoffStatuses(linkedChanges);
    } else {
      ++queueWorkflowLoadSequenceRef.current;
      setQueueWorkflowStatusByChangeId({});
      setCodexHandoffStatusByChangeId({});
    }
  }, [linkedChanges, linkedLoadedForId, linkedLoading, selectedId, selected && selected.project_id]);

  useEffect(() => {
    const autoProcessNext = codexAutomationState?.auto_process_next || null;
    const marker = autoProcessNext?.idempotency_marker || "";
    if (!selectedId || !autoProcessNext?.enabled || !autoProcessNext?.eligible || !marker) return;
    if (autoProcessNextInFlightRef.current || codexAutomationLoading || Boolean(queueActionLoadingId)) return;
    if (autoProcessNextLastMarkerRef.current === marker) return;

    autoProcessNextInFlightRef.current = true;
    autoProcessNextLastMarkerRef.current = marker;
    handleCodexAutomationAction("auto_process_next").finally(() => {
      autoProcessNextInFlightRef.current = false;
    });
  }, [
    selectedId,
    codexAutomationState?.auto_process_next?.enabled,
    codexAutomationState?.auto_process_next?.eligible,
    codexAutomationState?.auto_process_next?.idempotency_marker,
    codexAutomationLoading,
    queueActionLoadingId,
  ]);

  useEffect(() => {
    const autoProcessNext = codexAutomationState?.auto_process_next || null;
    const status = String(codexAutomationState?.automation_status || "").toLowerCase();
    const blockedStatuses = new Set(["not_started", "paused", "failed", "completed", "returned_zip_detected"]);
    if (!selectedId || !autoProcessNext?.enabled || blockedStatuses.has(status)) return undefined;

    const timer = window.setInterval(() => {
      if (!autoProcessNextInFlightRef.current && !codexAutomationLoading && !queueActionLoadingId) {
        loadCodexAutomationState(selectedId);
      }
    }, 10000);

    return () => window.clearInterval(timer);
  }, [
    selectedId,
    codexAutomationState?.auto_process_next?.enabled,
    codexAutomationState?.automation_status,
    codexAutomationLoading,
    queueActionLoadingId,
  ]);

  useEffect(() => {
    if (newProjectId) loadModuleOptions(newProjectId, "create");
    else setCreateModuleOptions([]);
  }, [newProjectId]);

  useEffect(() => {
    const projectId = selected && selected.project_id ? selected.project_id : "";
    if (projectId) loadModuleOptions(projectId, "selected");
    else setSelectedModuleOptions([]);
  }, [selected && selected.project_id]);

  useEffect(() => {
    const moduleName = selected && selected.module_name ? String(selected.module_name) : "";
    if (moduleName && !isAllModulesValue(moduleName)) setPlannerModule(moduleName);
  }, [selected && selected.module_name]);

  useEffect(() => {
    const selectedProject = (selected && selected.project_id) || selectedProjectId || "";
    setTargetPreviewSourceProjectId((prev) => prev || pickDefaultTargetPreviewSourceProjectId(projectOptions, selectedProject));
  }, [projectOptions, selected && selected.project_id, selectedProjectId]);

  useEffect(() => {
    setTargetPreviewTargetProjectId((prev) => {
      if (prev && prev !== targetPreviewSourceProjectId) return prev;
      return pickDefaultTargetPreviewTargetProjectId(projectOptions, targetPreviewSourceProjectId);
    });
  }, [projectOptions, targetPreviewSourceProjectId]);

  useEffect(() => {
    setTaskSchemaTargetResult(null);
    setTaskSchemaTargetError(null);
    if (!selectedTaskId) {
      setSelectedTask(null);
      return;
    }
    const match = (tasks || []).find((task) => task.id === selectedTaskId) || null;
    setSelectedTask(match);
  }, [selectedTaskId, tasks]);

  const handleCreate = async () => {
    const title = (newTitle || "").trim();
    if (!title) {
      setError("Title is required.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          description: newDescription || "",
          status: "DRAFT",
          project_id: newProjectId || null,
          module_name: newModuleName || null,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create failed.");
      }

      setNewTitle("");
      setNewDescription("");
      setNewProjectId("");
      setNewModuleName("");
      await loadList();

      if (data.full_change && data.full_change.id) {
        setSelectedId(data.full_change.id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSelected = async () => {
    if (!selected || !selected.id) return;

    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selected.id,
          title: selected.title,
          description: selected.description,
          status: selected.status,
          priority: selected.priority,
          owner: selected.owner,
          project_id: selected.project_id || null,
          module_name: selected.module_name || null,
          local_codex_handoff_root_path: selected.local_codex_handoff_root_path || null,
          workflow_mode: getFullChangeWorkflowMode(selected.workflow_mode),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Update failed.");

      setSelected(data.full_change || selected);
      await loadList();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetCreateLinkedForm = () => {
    setCreateLinkedTitle("");
    setCreateLinkedDescription("");
    setCreateLinkedModule(ALL_MODULES_VALUE);
  };

  const openCreateLinkedDialog = () => {
    if (!selectedId || linkedLoading || createLinkedLoading ||
        String(selected?.id || "") !== String(selectedId)) return;
    resetCreateLinkedForm();
    setCreateLinkedModule(String(selected?.module_name || ALL_MODULES_VALUE).trim() || ALL_MODULES_VALUE);
    setCreateLinkedError("");
    setCreatedLinkedChange(null);
    setCreateLinkedDialogOpen(true);
  };

  const closeCreateLinkedDialog = () => {
    if (createLinkedLoading || createLinkedRequestRef.current) return;
    setCreateLinkedDialogOpen(false);
    setCreateLinkedError("");
    resetCreateLinkedForm();
  };

  const handleCreateLinkedDevIdeChange = async () => {
    if (!selectedId || !createLinkedDialogOpen || createLinkedRequestRef.current) return;

    const title = String(createLinkedTitle || "").trim();
    if (!title) {
      setCreateLinkedError("Change title is required.");
      return;
    }

    const parentId = String(selectedId);
    const moduleName = String(createLinkedModule || "").trim();
    const allowedModules = new Set([ALL_MODULES_VALUE, ...(selectedModuleOptions || []).map(getModuleOptionValue)]);
    // The current parent module may be a legacy module absent from the registry.
    if (selected?.module_name) allowedModules.add(String(selected.module_name).trim());
    if (!moduleName || !allowedModules.has(moduleName)) {
      setCreateLinkedError("Select a valid module for this project.");
      return;
    }
    const payload = {
      full_change_id: parentId,
      title,
      description: createLinkedDescription,
      status: "OPEN",
      module: moduleName,
    };

    // This synchronous guard also prevents two submissions before React rerenders.
    createLinkedRequestRef.current = true;
    setCreateLinkedLoading(true);
    setCreateLinkedError("");
    try {
      const res = await fetch("/api/dev-ide/full-change/create-linked-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create linked DEV IDE change failed.");
      }

      // Never overwrite a newly selected parent's queue with a previous response.
      if (createLinkedParentRef.current !== parentId) return;
      const created = data.change || {};
      setLinkedChanges(Array.isArray(data.linked_changes) ? data.linked_changes : []);
      setCreatedLinkedChange({ parentId, id: created.id || null, title: created.title || title });
      resetCreateLinkedForm();
      setCreateLinkedDialogOpen(false);
    } catch (e) {
      if (createLinkedParentRef.current === parentId) {
        setCreateLinkedError(e.message || "Create linked DEV IDE change failed. Check the queue before retrying.");
      }
    } finally {
      createLinkedRequestRef.current = false;
      setCreateLinkedLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedId) return;

    const devId = String(assignDevIdeChangeId || "").trim();
    if (!devId) {
      setLinkedError("dev_ide_change_id is required.");
      return;
    }

    const orderVal = String(assignOrder || "").trim();
    const payload = {
      full_change_id: selectedId,
      dev_ide_change_id: devId,
    };
    if (orderVal !== "") {
      payload.full_change_order = orderVal;
    }

    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/assign-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Assign failed.");
      }

      setAssignDevIdeChangeId("");
      setAssignOrder("");
      await loadLinked(selectedId);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  const handleUnassign = async (devIdeChangeId) => {
    if (!devIdeChangeId) return;

    setLinkedLoading(true);
    setLinkedError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/unassign-dev-ide-change", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dev_ide_change_id: devIdeChangeId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Unassign failed.");
      await loadLinked(selectedId);
    } catch (e) {
      setLinkedError(e.message);
    } finally {
      setLinkedLoading(false);
    }
  };

  // B2 is an on-demand, child-scoped read; opening the audit never changes the active change.
  const openChildAudit = async (child) => {
    if (!child?.id || !selectedId) return;
    const sequence = ++auditLoadSequenceRef.current;
    const projectId = getQueueProjectId(child);
    setAuditChild({ ...child, auditParentId: selectedId, auditProjectId: projectId });
    setAuditHistoryRecord(null);
    setAuditLoad({ loading: true, data: null, error: "" });
    try {
      const params = new URLSearchParams({ project_id: projectId, full_change_id: String(selectedId) });
      const response = await fetch(`/api/dev-ide/changes/${encodeURIComponent(child.id)}/import-audit?${params}`);
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Unable to load this child's audit.");
      if (sequence === auditLoadSequenceRef.current) setAuditLoad({ loading: false, data, error: "" });
    } catch (error) {
      if (sequence === auditLoadSequenceRef.current) setAuditLoad({ loading: false, data: null, error: error.message });
    }
  };

  const closeChildAudit = () => {
    ++auditLoadSequenceRef.current;
    setAuditChild(null);
    setAuditHistoryRecord(null);
    setAuditLoad({ loading: false, data: null, error: "" });
  };

  useEffect(() => {
    ++auditLoadSequenceRef.current;
    setAuditChild(null);
    setAuditHistoryRecord(null);
    setAuditLoad({ loading: false, data: null, error: "" });
  }, [selectedId]);

  const showAuditSaveHistoryRecord = async (historyId) => {
    if (!auditChild?.id || !historyId) return;
    const currentSequence = auditLoadSequenceRef.current;
    setAuditHistoryRecord({ loading: true, id: historyId });
    try {
      const params = new URLSearchParams({ project_id: auditChild.auditProjectId });
      const response = await fetch(`/api/dev-ide/changes/${encodeURIComponent(auditChild.id)}?${params}`);
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Change history could not be loaded.");
      const row = (data.history || []).find((entry) => String(entry.id) === String(historyId));
      if (currentSequence === auditLoadSequenceRef.current) {
        setAuditHistoryRecord({ id: historyId, row: row || null, error: row ? "" : "Save record not present in the available change history." });
      }
    } catch (error) {
      if (currentSequence === auditLoadSequenceRef.current) setAuditHistoryRecord({ id: historyId, error: error.message });
    }
  };

  const renderChildAuditModal = () => (
    <Dialog data-testid="full-change-child-import-audit-dialog" open={Boolean(auditChild)} onClose={closeChildAudit} maxWidth="md" fullWidth>
      <DialogTitle>Import &amp; save audit — Child #{auditChild?.id || ""}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            Parent #{auditChild?.auditParentId} · {auditChild?.auditProjectId} · This view does not import, save or execute anything.
          </Typography>
          {auditLoad.loading ? <CircularProgress size={20} /> : null}
          {auditLoad.error ? <Alert severity="error">{auditLoad.error}</Alert> : null}
          {auditLoad.data ? (
            <>
              <Alert severity={auditLoad.data.state === "recorded" ? "info" : "warning"} data-testid="full-change-child-audit-coverage">
                {auditLoad.data.state === "recorded"
                  ? "Recorded browser ZIP and server-path review attempts only. Unselected preview files were not saved; alternate Codex writers are not fully audited."
                  : (auditLoad.data.message || "Import audit evidence unavailable.")}
                {" "}Historical previews/skips before B2 were not captured and cannot be reconstructed.
              </Alert>
              {(auditLoad.data.attempts || []).length === 0 ? (
                <Typography variant="body2">No B2 import attempts recorded for this child. This does not establish that earlier imports did not occur.</Typography>
              ) : null}
              {(auditLoad.data.attempts || []).map((attempt) => {
                const events = attempt.events || [];
                const counts = events.reduce((acc, event) => {
                  const key = `${event.phase}:${event.outcome}`;
                  acc[key] = (acc[key] || 0) + 1;
                  return acc;
                }, {});
                return (
                  <Paper key={attempt.id} variant="outlined" sx={{ p: 1.25 }} data-testid={`full-change-audit-attempt-${attempt.id}`}>
                    <Typography variant="subtitle2">Attempt #{attempt.id} · {attempt.package_name || "ZIP"} · {attempt.result}</Typography>
                    <Typography variant="caption" sx={{ overflowWrap: "anywhere", display: "block" }}>
                      {attempt.created_at} · {attempt.source_kind} · {attempt.reported_actor ? `Reported user: ${attempt.reported_actor} · ` : ""}SHA-256 {attempt.package_sha256}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Coverage: {attempt.coverage || "unknown"} · {Object.entries(counts).map(([key, count]) => `${key}: ${count}`).join(" · ") || "No per-file evidence"}
                    </Typography>
                    {attempt.error_message ? <Alert severity="warning">{attempt.error_message}</Alert> : null}
                    <Box component="details" sx={{ mt: 0.5 }}>
                      <Box component="summary" sx={{ cursor: "pointer", fontSize: "0.85rem" }}>View {events.length} recorded file events</Box>
                      <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {events.map((event, i) => (
                          <Box key={`${attempt.id}-${i}`} sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 0.5 }}>
                            <Typography variant="caption" sx={{ overflowWrap: "anywhere", display: "block" }}>
                              {event.file_path} · {event.asset_kind} · {event.phase}: <strong>{event.outcome}</strong>
                            </Typography>
                            {event.reason ? <Typography variant="caption" color="text.secondary">{event.reason}</Typography> : null}
                            {event.change_file_history_id ? (
                              <Button size="small" variant="text" onClick={() => showAuditSaveHistoryRecord(event.change_file_history_id)}>
                                View save-history record #{event.change_file_history_id}
                              </Button>
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Paper>
                );
              })}
              {auditHistoryRecord ? (
                <Alert data-testid="full-change-audit-save-history" severity={auditHistoryRecord.error ? "warning" : "info"}>
                  {auditHistoryRecord.loading ? "Loading saved-file history…" : auditHistoryRecord.error
                    ? auditHistoryRecord.error
                    : auditHistoryRecord.row
                      ? `History #${auditHistoryRecord.id}: ${auditHistoryRecord.row.file_path || "document"} — ${auditHistoryRecord.row.saved_at || "timestamp unavailable"} — ${auditHistoryRecord.row.save_summary || "save details unavailable"}`
                      : "Save history not available."}
                </Alert>
              ) : null}
              <Typography variant="subtitle2">Pre-existing saved-file evidence (project-attributed)</Typography>
              {(auditLoad.data.historical_saves || []).length ? (
                <Stack spacing={0.3}>
                  {(auditLoad.data.historical_saves || []).map((entry) => (
                    <Typography key={entry.id} variant="caption" sx={{ overflowWrap: "anywhere" }}>
                      #{entry.id} · {entry.file_path} · {entry.saved_at}
                    </Typography>
                  ))}
                </Stack>
              ) : <Typography variant="body2" color="text.secondary">No safely project-attributed legacy save records available. Other historic activity may exist.</Typography>}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-testid="full-change-child-import-audit-close" onClick={closeChildAudit}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const renderLinkedChangeViewModal = () => {
    const change = viewLinkedChange;
    const changedFilesCount = change ? (change.changed_files_count ?? change.changed_file_count ?? change.file_count ?? change.files_count) : "";
    const details = change ? [
      ["ID", `#${formatLinkedChangeDetailValue(change.id)}`],
      ["Title", formatLinkedChangeDetailValue(change.title)],
      ["Status", formatLinkedChangeDetailValue(change.status)],
      ["Module", formatLinkedChangeDetailValue(change.module || change.module_name)],
      ["Order", formatLinkedChangeDetailValue(change.full_change_order)],
      ["Changed files", formatLinkedChangeDetailValue(changedFilesCount)],
      ["Developer", formatLinkedChangeDetailValue(change.developer)],
    ] : [];
    const textBlocks = change ? [
      ["Description", getLinkedChangeDetailValue(change, ["description"])],
      ["Scope summary", getLinkedChangeDetailValue(change, ["scope_summary", "scopeSummary"])],
      ["Allowed files", getLinkedChangeDetailValue(change, ["allowed_files", "allowedFiles"])],
      ["Implementation notes", getLinkedChangeDetailValue(change, ["implementation_notes", "implementationNotes"])],
      ["Acceptance criteria", getLinkedChangeDetailValue(change, ["acceptance_criteria", "acceptanceCriteria"])],
      ["Notes", getLinkedChangeDetailValue(change, ["notes"])],
    ].filter(([, value]) => String(value || "").trim() !== "") : [];

    return (
      <Dialog
        data-testid="full-change-child-details-dialog"
        open={Boolean(change)}
        onClose={() => setViewLinkedChange(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { width: { xs: "calc(100% - 16px)", sm: "100%" }, m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle sx={{ pr: { xs: 2, sm: 3 }, pb: 1 }}>
          Linked DEV IDE Change Details
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 }, pb: 3 }}>
          {change ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Change #{formatLinkedChangeDetailValue(change.id)}
                </Typography>
                <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
                  {formatLinkedChangeDetailValue(change.title, "Untitled change")}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1,
                }}
              >
                {details.map(([label, value]) => (
                  <Paper key={label} variant="outlined" sx={{ p: 1, bgcolor: "background.default" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700 }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                      {value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {textBlocks.length > 0 ? (
                <Stack spacing={1.25}>
                  {textBlocks.map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="subtitle2">{label}</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                      >
                        {String(value || "")}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No description or extended metadata is available for this linked change.
                </Typography>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, py: 1.5 }}>
          <Button data-testid="full-change-child-details-close" onClick={() => setViewLinkedChange(null)} sx={{ width: { xs: "100%", sm: "auto" } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleCreateTask = async () => {
    if (!selectedId) return;
    const title = String(taskCreateTitle || "").trim();
    if (!title) {
      setTasksError("Task title is required.");
      return;
    }

    setTaskCreateLoading(true);
    setTasksError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          title,
          task_type: taskCreateType,
          status: "DRAFT",
          created_by: "full_change_panel",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create task failed.");
      }
      setTaskCreateTitle("");
      setTaskCreateType("GENERAL");
      await loadTasks(selectedId);
      if (data.task && data.task.id) {
        setSelectedTaskId(data.task.id);
      }
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskCreateLoading(false);
    }
  };

  const handleSaveTask = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSaveLoading(true);
    setTasksError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: selectedTask.id,
          title: selectedTask.title,
          description: selectedTask.description,
          task_type: selectedTask.task_type,
          status: selectedTask.status,
          scope_summary: selectedTask.scope_summary,
          allowed_files: selectedTask.allowed_files,
          acceptance_criteria: selectedTask.acceptance_criteria,
          sql_summary: selectedTask.sql_summary,
          sql_proposal: selectedTask.sql_proposal,
          rollback_sql: selectedTask.rollback_sql,
          task_order: selectedTask.task_order,
          updated_by: "full_change_panel",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Save task failed.");
      }
      setSelectedTask(data.task || selectedTask);
      await loadTasks(selectedId);
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskSaveLoading(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskDeleteLoading(true);
    setTasksError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedTask.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Delete task failed.");
      const deletedId = selectedTask.id;
      setSelectedTaskId(null);
      setSelectedTask(null);
      await loadTasks(selectedId);
      setTasks((prev) => prev.filter((task) => task.id !== deletedId));
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskDeleteLoading(false);
    }
  };

  const handleGenerateTasks = async () => {
    if (!selectedId) return;
    setTaskGenerateLoading(true);
    setTasksError(null);
    setTaskGenerationInfo(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/generate-drafts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: selectedId,
          replace_existing: replaceTasksOnGenerate,
          use_llm: useLlmGeneration,
          created_by: "full_change_task_generator",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Generate tasks failed.");
      }
      setTaskGenerationInfo({
        generationMethod: data.generation_method,
        createdCount: data.created_count,
      });
      await loadTasks(selectedId);
    } catch (e) {
      setTasksError(e.message);
    } finally {
      setTaskGenerateLoading(false);
    }
  };

  const handleRunTaskDiscovery = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskDiscoveryLoading(true);
    setTaskDiscoveryError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/discover-files", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          top_n: 8,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "File discovery failed.");
      }
      const findings = (data && data.findings) || {};
      setTaskDiscoveryResult(data);
      setTaskDiscoverySelectedFiles(findings.default_selected_files || []);
      setTaskDiscoveryTargetIds(findings.suggested_target_task_ids || []);
      setTaskDiscoveryDialogOpen(true);
    } catch (e) {
      setTaskDiscoveryError(e.message);
    } finally {
      setTaskDiscoveryLoading(false);
    }
  };

  const toggleDiscoveryFile = (path) => {
    setTaskDiscoverySelectedFiles((prev) => {
      const items = Array.isArray(prev) ? prev.slice() : [];
      return items.includes(path) ? items.filter((p) => p !== path) : [...items, path];
    });
  };

  const toggleDiscoveryTargetTask = (taskId) => {
    setTaskDiscoveryTargetIds((prev) => {
      const items = Array.isArray(prev) ? prev.slice() : [];
      return items.includes(taskId) ? items.filter((id) => id !== taskId) : [...items, taskId];
    });
  };

  const handleApplyTaskDiscovery = async () => {
    if (!selectedTask || !selectedTask.id) return;
    if (!taskDiscoverySelectedFiles || taskDiscoverySelectedFiles.length === 0) {
      setTaskDiscoveryError("Select at least one approved file.");
      return;
    }
    setTaskDiscoveryApplyLoading(true);
    setTaskDiscoveryError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/apply-discovery", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_task_id: selectedTask.id,
          approved_files: taskDiscoverySelectedFiles,
          target_task_ids: taskDiscoveryTargetIds,
          discovery_summary: ((taskDiscoveryResult && taskDiscoveryResult.findings && taskDiscoveryResult.findings.summary) || "") + ((taskDiscoveryResult && taskDiscoveryResult.findings && taskDiscoveryResult.findings.recommended_primary_file) ? `\nRecommended primary file: ${taskDiscoveryResult.findings.recommended_primary_file}` : ""),
          updated_by: "full_change_panel_discovery",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Applying discovery findings failed.");
      }
      await loadTasks(selectedId);
      setTaskDiscoveryDialogOpen(false);
      setTaskDiscoveryResult(null);
      setTaskDiscoverySelectedFiles([]);
      setTaskDiscoveryTargetIds([]);
    } catch (e) {
      setTaskDiscoveryError(e.message);
    } finally {
      setTaskDiscoveryApplyLoading(false);
    }
  };

  const handleRunTaskPatcher = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskPatchPreviewLoading(true);
    setTaskPatchPreviewError(null);
    setTaskPatchApplyResult(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/build-patch-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          max_tokens: 6000,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Patch preview failed.");
      }
      setTaskPatchPreviewResult(data);
      setTaskPatchDialogOpen(true);
    } catch (e) {
      setTaskPatchPreviewError(e.message);
    } finally {
      setTaskPatchPreviewLoading(false);
    }
  };

  const handleApplyTaskPatch = async () => {
    if (!selectedTask || !selectedTask.id || !taskPatchPreviewResult?.preview?.previews) return;
    setTaskPatchApplyLoading(true);
    setTaskPatchPreviewError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/apply-patch-preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          previews: taskPatchPreviewResult.preview.previews,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Apply patch failed.");
      }
      setTaskPatchApplyResult(data.apply_result || null);
      await loadTasks(selectedId);
      if (data.task && data.task.id) {
        setSelectedTaskId(data.task.id);
      }
    } catch (e) {
      setTaskPatchPreviewError(e.message);
    } finally {
      setTaskPatchApplyLoading(false);
    }
  };


  const handleResolveTaskSchemaTarget = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSchemaTargetLoading(true);
    setTaskSchemaTargetError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/resolve-schema-target", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ task_id: selectedTask.id }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Schema target resolution failed.");
      }
      setTaskSchemaTargetResult(data.schema_target || null);
    } catch (e) {
      setTaskSchemaTargetError(e.message);
      setTaskSchemaTargetResult(null);
    } finally {
      setTaskSchemaTargetLoading(false);
    }
  };

  const handleRunTaskSqlProposal = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSqlProposalLoading(true);
    setTaskSqlProposalError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/generate-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          use_llm: true,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "SQL proposal generation failed.");
      }
      const proposal = data.proposal || {};
      setTaskSqlProposalResult(data);
      setTaskSchemaTargetResult(data.schema_target || taskSchemaTargetResult || null);
      setTaskSqlSummaryDraft(proposal.summary || "");
      setTaskSqlProposalDraft(proposal.sql_proposal || "");
      setTaskRollbackSqlDraft(proposal.rollback_sql || "");
      setTaskSqlDialogOpen(true);
    } catch (e) {
      setTaskSqlProposalError(e.message);
    } finally {
      setTaskSqlProposalLoading(false);
    }
  };

  const handleApplyTaskSqlProposal = async () => {
    if (!selectedTask || !selectedTask.id) return;
    if (!String(taskSqlProposalDraft || "").trim()) {
      setTaskSqlProposalError("SQL proposal is required.");
      return;
    }
    setTaskSqlApplyLoading(true);
    setTaskSqlProposalError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/apply-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          sql_summary: taskSqlSummaryDraft,
          sql_proposal: taskSqlProposalDraft,
          rollback_sql: taskRollbackSqlDraft,
          status: String((selectedTask && selectedTask.status) || "").toUpperCase() === "DRAFT" ? "PROPOSED" : selectedTask.status,
          updated_by: "full_change_sql_panel",
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Saving SQL proposal failed.");
      }
      await loadTasks(selectedId);
      if (data.task && data.task.id) {
        setSelectedTaskId(data.task.id);
      }
      setTaskSqlDialogOpen(false);
    } catch (e) {
      setTaskSqlProposalError(e.message);
    } finally {
      setTaskSqlApplyLoading(false);
    }
  };

  const handleOpenTaskSqlRunDialog = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSqlRunDialogOpen(true);
    setTaskSqlRunLoading(true);
    setTaskSqlRunError(null);
    setTaskSqlRunExecution(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/run-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          dry_run: true,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "SQL run preview failed.");
      }
      setTaskSqlRunPreview(data.preview || null);
      setTaskSqlRunExecution(data.execution || null);
    } catch (e) {
      setTaskSqlRunError(e.message);
    } finally {
      setTaskSqlRunLoading(false);
    }
  };

  const handleExecuteTaskSqlProposal = async () => {
    if (!selectedTask || !selectedTask.id) return;
    setTaskSqlRunLoading(true);
    setTaskSqlRunError(null);
    try {
      const res = await fetch("/api/dev-ide/full-change/tasks/run-sql-proposal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: selectedTask.id,
          dry_run: false,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "SQL execution failed.");
      }
      setTaskSqlRunPreview(data.preview || null);
      setTaskSqlRunExecution(data.execution || null);
      await loadTasks(selectedId);
    } catch (e) {
      setTaskSqlRunError(e.message);
    } finally {
      setTaskSqlRunLoading(false);
    }
  };

  const handleCreatePlannerRun = async () => {
    if (!selectedId) return;

    const mod = String(plannerModule || "").trim();
    if (!mod) {
      setPlannerCreateError("Module is required.");
      return;
    }
    if (isAllModulesValue(mod)) {
      setPlannerCreateError("Planner advisory lane requires a specific module. Use task-level file discovery for project-wide scope.");
      return;
    }

    const goal = String(plannerGoalText || "").trim();
    if (!goal) {
      setPlannerCreateError("Planner goal text is required.");
      return;
    }

    setPlannerCreateLoading(true);
    setPlannerCreateError(null);
    try {
      const sessRes = await fetch("/api/dev-ide/planner/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          module: mod,
          full_change_id: selectedId,
        }),
      });
      const sessData = await sessRes.json();
      if (!sessData.success) {
        throw new Error((sessData.errors && sessData.errors.join(", ")) || sessData.error || "Create planner session failed.");
      }

      const plannerSessionId = sessData.planner_session_id ?? sessData.session_id ?? sessData.sessionId ?? (sessData.session && sessData.session.id) ?? null;
      if (plannerSessionId === null || plannerSessionId === undefined || plannerSessionId === "") {
        throw new Error("Create planner session did not return planner_session_id.");
      }

      const res = await fetch("/api/dev-ide/planner/runs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planner_session_id: plannerSessionId,
          full_change_id: selectedId,
          module: mod,
          status: "DRAFT",
          goal_text: goal,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Create planner run failed.");
      }

      try {
        const contextText = buildFullChangeContextText(selected, linkedChanges, goal);
        await fetch("/api/dev-ide/planner/goals", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planner_session_id: plannerSessionId,
            category: "FULL_CHANGE",
            description: "Full Change context captured at run creation time.",
            active: true,
            goal_text: contextText,
          }),
        });
      } catch (e) {
        // non-blocking
      }

      await loadPlannerRuns(selectedId);
    } catch (e) {
      setPlannerCreateError(e.message);
    } finally {
      setPlannerCreateLoading(false);
    }
  };

  const selectedWorkflowMode = getFullChangeWorkflowMode(selected && selected.workflow_mode);
  const isCodexLedWorkflow = selectedWorkflowMode === "codex_led";
  const isPersonLedGptChatWorkflow = selectedWorkflowMode === "person_led_gpt_chat";

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: "100%", minHeight: 0, overflow: { xs: "auto", md: "hidden" } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ height: { xs: "auto", md: "100%" }, minHeight: 0, overflow: { xs: "visible", md: "hidden" } }}
      >
        <Paper
          sx={{
            width: { xs: "100%", md: 360 },
            flexShrink: 0,
            p: 2,
            overflowY: "auto",
            maxHeight: { xs: "45vh", md: "100%" },
          }}
        >
          <Typography variant="h6">Full Change</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Governing change record with child execution tasks underneath it.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2">Create</Typography>
          <TextField size="small" fullWidth label="Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} sx={{ mt: 1 }} />
          <TextField
            size="small"
            fullWidth
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            sx={{ mt: 1 }}
            multiline
            minRows={3}
          />
          <TextField
            size="small"
            select
            fullWidth
            label="Project"
            value={newProjectId}
            onChange={(e) => {
              setNewProjectId(e.target.value);
              setNewModuleName("");
            }}
            sx={{ mt: 1 }}
            helperText={projectOptionsError || "Optional but recommended."}
          >
            <MenuItem value="">None</MenuItem>
            {(projectOptions || []).map((project) => (
              <MenuItem key={project.project_id} value={project.project_id}>
                {project.label || project.project_id}
              </MenuItem>
            ))}
          </TextField>
          {createModuleOptions && createModuleOptions.length > 0 ? (
            <TextField
              size="small"
              select
              fullWidth
              label="Module"
              value={newModuleName}
              onChange={(e) => setNewModuleName(e.target.value)}
              sx={{ mt: 1 }}
              disabled={!newProjectId}
              helperText={!newProjectId ? "Select a project first." : "Module within the selected project."}
            >
              <MenuItem value="">None</MenuItem>
              {createModuleOptions.map((row) => {
                const moduleName = getModuleOptionValue(row);
                return (
                  <MenuItem key={moduleName} value={moduleName}>
                    {getModuleOptionLabel(row)}
                  </MenuItem>
                );
              })}
            </TextField>
          ) : (
            <TextField
              size="small"
              fullWidth
              label="Module"
              value={newModuleName}
              onChange={(e) => setNewModuleName(e.target.value)}
              sx={{ mt: 1 }}
              disabled={!newProjectId}
              helperText={!newProjectId ? "Select a project first." : (moduleOptionsLoading ? "Loading modules..." : "Enter module name if not listed.")}
            />
          )}
          <Button variant="contained" onClick={handleCreate} sx={{ mt: 1 }} disabled={loading || projectOptionsLoading}>
            {loading ? "Working..." : "Create Full Change"}
          </Button>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing</Typography>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}

          <TextField
            size="small"
            select
            fullWidth
            label="Select Full Change"
            value={selectedId ? String(selectedId) : ""}
            onChange={(e) => {
              const nextId = e.target.value;
              const match = (items || []).find((it) => String(it.id) === String(nextId));
              setSelectedId(match ? match.id : nextId);
            }}
            disabled={loading || !(items || []).length}
            helperText={(items || []).length ? "Options show Full Change ID, title, and status." : "No Full Changes found."}
            SelectProps={{
              SelectDisplayProps: { "data-testid": "full-change-select-parent" },
              displayEmpty: true,
              renderValue: (value) => {
                if (!value) return "Choose a Full Change";
                const match = (items || []).find((it) => String(it.id) === String(value));
                return match ? getFullChangeSelectorLabel(match) : `#${value}`;
              },
            }}
          >
            <MenuItem value="" disabled>
              Choose a Full Change
            </MenuItem>
            {(items || []).map((it) => (
              <MenuItem key={it.id} value={String(it.id)} data-testid={`full-change-parent-option-${it.id}`}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                      #{it.id} {it.title || "Untitled Full Change"}
                    </Typography>
                    <Chip size="small" label={getFullChangeStatusLabel(it)} />
                  </Stack>
                  {(it.project_id || it.module_name) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {it.project_id || ""}
                      {it.project_id && it.module_name ? " - " : ""}
                      {it.module_name ? getModuleOptionLabel(it.module_name) : ""}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        <Paper
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 1.5, md: 2 },
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: "auto", md: 0 },
            overflowY: "auto",
          }}
        >
          {!selectedId ? (
            <Typography variant="body2" color="text.secondary">
              Select a Full Change on the left.
            </Typography>
          ) : (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>Full Change Details</Typography>

              <TextField
                size="small"
                fullWidth
                label="Title"
                value={(selected && selected.title) || ""}
                onChange={(e) => setSelected((prev) => ({ ...(prev || {}), title: e.target.value }))}
                sx={{ mb: 1 }}
              />

              <TextField
                size="small"
                fullWidth
                label="Description"
                value={(selected && selected.description) || ""}
                onChange={(e) => setSelected((prev) => ({ ...(prev || {}), description: e.target.value }))}
                sx={{ mb: 1 }}
                multiline
                minRows={4}
              />

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Project"
                  value={(selected && selected.project_id) || ""}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), project_id: e.target.value, module_name: "" }))}
                  helperText={projectOptionsError || "Select the target project first."}
                >
                  <MenuItem value="">None</MenuItem>
                  {(projectOptions || []).map((project) => (
                    <MenuItem key={project.project_id} value={project.project_id}>
                      {project.label || project.project_id}
                    </MenuItem>
                  ))}
                </TextField>
                {selectedModuleOptions && selectedModuleOptions.length > 0 ? (
                  <TextField
                    size="small"
                    select
                    fullWidth
                    label="Module"
                    value={(selected && selected.module_name) || ""}
                    onChange={(e) => setSelected((prev) => ({ ...(prev || {}), module_name: e.target.value }))}
                    helperText={!selected?.project_id ? "Select a project first." : "Module within the selected project."}
                    disabled={!selected?.project_id}
                  >
                    <MenuItem value="">None</MenuItem>
                    {selectedModuleOptions.map((row) => {
                      const moduleName = getModuleOptionValue(row);
                      return (
                        <MenuItem key={moduleName} value={moduleName}>
                          {getModuleOptionLabel(row)}
                        </MenuItem>
                      );
                    })}
                    {(selected && selected.module_name && !selectedModuleOptions.some((row) => getModuleOptionValue(row) === selected.module_name)) ? (
                      <MenuItem value={selected.module_name}>{getModuleOptionLabel(selected.module_name)}</MenuItem>
                    ) : null}
                  </TextField>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Module"
                    value={(selected && selected.module_name) || ""}
                    onChange={(e) => setSelected((prev) => ({ ...(prev || {}), module_name: e.target.value }))}
                    helperText={!selected?.project_id ? "Select a project first." : (moduleOptionsLoading ? "Loading modules..." : "Enter module name if not listed.")}
                    disabled={!selected?.project_id}
                  />
                )}
              </Stack>

              <TextField
                size="small"
                select
                fullWidth
                label="Workflow mode"
                value={getFullChangeWorkflowMode(selected && selected.workflow_mode)}
                onChange={(e) => setSelected((prev) => ({ ...(prev || {}), workflow_mode: e.target.value }))}
                helperText={getFullChangeWorkflowModeHelperText(selected && selected.workflow_mode)}
                sx={{ mb: 1 }}
              >
                {FULL_CHANGE_WORKFLOW_MODES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                fullWidth
                label="Local Codex handoff root"
                value={(selected && selected.local_codex_handoff_root_path) || ""}
                onChange={(e) => setSelected((prev) => ({ ...(prev || {}), local_codex_handoff_root_path: e.target.value }))}
                helperText="Optional operator guidance only; DEV IDE will not write directly to this local path."
                sx={{ mb: 1 }}
              />

              {buildLocalCodexHandoffFolders(selected && selected.local_codex_handoff_root_path).length > 0 ? (
                <Paper variant="outlined" sx={{ p: 1.25, mb: 1, bgcolor: "background.default" }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Derived local handoff folders</Typography>
                  <Stack spacing={0.5}>
                    {buildLocalCodexHandoffFolders(selected && selected.local_codex_handoff_root_path).map((row) => (
                      <Box
                        key={row.folder}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "170px 1fr" },
                          gap: 0.5,
                          alignItems: "baseline",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">{row.folder}</Typography>
                        <Typography variant="caption" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                          {row.path}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                    These local folders are for manual sync or future runner guidance only.
                  </Typography>
                </Paper>
              ) : null}

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Status"
                  value={(selected && selected.status) || "DRAFT"}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), status: e.target.value }))}
                  helperText="DRAFT | PLANNED | IN_PROGRESS | READY_FOR_TEST | APPROVED | RELEASED"
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Priority"
                  value={(selected && selected.priority) || ""}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), priority: e.target.value }))}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Owner"
                  value={(selected && selected.owner) || ""}
                  onChange={(e) => setSelected((prev) => ({ ...(prev || {}), owner: e.target.value }))}
                />
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: "wrap" }}>
                <Button variant="contained" onClick={handleUpdateSelected} disabled={loading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {loading ? "Working..." : "Save"}
                </Button>
                <Button data-testid="full-change-refresh-linked-changes" variant="outlined" onClick={() => loadLinked(selectedId)} disabled={linkedLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Refresh Linked Changes
                </Button>
                <Button variant="outlined" onClick={handleExportPlanningPackage} disabled={planningExportLoading || loading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {planningExportLoading ? "Exporting planning ZIP..." : "Export Planning Package for GPT"}
                </Button>
                <Button data-testid="full-change-planning-zip-choose" variant="outlined" component="label" disabled={planningImportLoading || loading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {planningImportLoading ? "Validating planning ZIP..." : "Import Planning Package from GPT"}
                  <input data-testid="full-change-planning-zip-file-input" type="file" hidden accept=".zip,application/zip" onChange={handlePlanningImportFileSelected} />
                </Button>
                <Button variant="outlined" onClick={() => loadTasks(selectedId)} disabled={tasksLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Refresh Tasks
                </Button>
                <Button variant="outlined" onClick={() => loadPlannerRuns(selectedId)} disabled={plannerRunsLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Refresh Planner Runs
                </Button>
              </Stack>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Demo guide: parent planning packages are separate from implementation packages.
                </Typography>
                <Typography variant="body2">
                  Export Planning Package for GPT downloads the parent Full Change planning ZIP and the all-modules code/docs context ZIP. Only import GPT's returned planning ZIP here; the context ZIP is for review only and is not a planning import package.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Code and document changes still move through each linked DEV IDE child change using Export Agent Package, Import Returned Package, optional Open Codex Import Review or Run Codex Auto Import, and the existing Import Review workflow.
                </Typography>
              </Alert>

              <Divider sx={{ mb: 2 }} />

              {selected && String(selected.id) === String(selectedId) && (
                <FullChangePlanningDocumentationEntry key={`${selected.project_id || ""}:${selectedId}`} fullChangeId={selectedId}>
                  <FullChangePlanningDocumentsPanel
                    key={`${selected.project_id || ""}:${selectedId}`}
                    fullChangeId={selectedId}
                    projectId={selected.project_id}
                    refreshVersion={planningDocumentsRefreshVersion + importSavedVersion}
                    onOpenDocument={onOpenPlanningDocument}
                  />
                </FullChangePlanningDocumentationEntry>
              )}

              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "primary.light", backgroundColor: "rgba(25,118,210,0.03)" }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, mb: 1 }}>
                  <Box>
                    <Typography variant="h6">Linked DEV IDE Changes — Implementation Queue</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Each linked DEV IDE change is a rollbackable implementation unit under the parent Full Change. In demos, use this queue to show how each child package can be exported to GPT, re-imported for review, saved, verified, and summarized independently.
                    </Typography>
                  </Box>
                  <Button data-testid="full-change-queue-refresh" variant="outlined" onClick={() => loadLinked(selectedId)} disabled={linkedLoading}>
                    Refresh Queue
                  </Button>
                </Stack>

                <Alert severity="info" sx={{ mb: 1, py: 0.75 }}>
                  <Typography variant="body2">
                    Queue terms: Export Agent Package is the child implementation ZIP for GPT; Import Returned Package opens the standard returned implementation ZIP flow within Full Change; Open Codex Import Review detects a returned ZIP from the configured Codex incoming folder and always opens Import Review without saving. Run Codex Auto Import uses controlled-save or force-save settings while still enforcing hard ZIP/path safety. Build, restart, health check, smoke test, and final Full Change review remain separate.
                  </Typography>
                </Alert>

                {isPersonLedGptChatWorkflow ? (
                  <Alert severity="info" sx={{ mb: 1, py: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Person-led GPT Chat mode is active.
                    </Typography>
                    <Typography variant="body2">
                      Codex handoff, Codex return import, and Codex automation controls are hidden for this Full Change. Use View, Export Agent Package, Import Returned Package, Copy Result Summary, verification, review, and task tools for the manual/GPT Chat workflow.
                    </Typography>
                  </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ display: isCodexLedWorkflow ? "block" : "none", p: 1.5, mt: 1, mb: 1, backgroundColor: "rgba(255,255,255,0.72)", borderColor: "warning.light" }}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2">Full Change Codex Automation Controls</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Guided operator workflow. Process Next may trigger the next Codex handoff export or process a detected returned ZIP. Controlled save remains risk-gated; force-save is autonomous and bypasses only the soft per-change review/risk pause while hard ZIP/path safety remains enforced. Save evidence remains visible for final Full Change review. No save mode runs build, restart, health check, smoke test, or deployment verification.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                      {renderQueueStatusChip(getCodexAutomationChipStatus(codexAutomationState), codexAutomationState?.status_message || "Full Change Codex automation state")}
                      <Button size="small" variant="text" onClick={() => loadCodexAutomationState(selectedId)} disabled={codexAutomationLoading || !selectedId}>Refresh</Button>
                    </Stack>
                  </Stack>

                  {codexAutomationError ? (
                    <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{codexAutomationError}</Alert>
                  ) : null}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
                      gap: 0.75,
                      alignItems: "baseline",
                      mb: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">Active linked change</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                      {codexAutomationState?.active_linked_change
                        ? `#${codexAutomationState.active_linked_change.id} — ${codexAutomationState.active_linked_change.title || "Untitled change"}`
                        : codexAutomationState?.active_dev_ide_change_id
                          ? `#${codexAutomationState.active_dev_ide_change_id}`
                          : "No active linked DEV IDE change selected yet."}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Safe next action</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {getCodexAutomationNextActionLabel(codexAutomationState)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Active save status</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {getCodexSaveProgressLabel(codexAutomationState?.active_save_progress)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Next queued change</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                      {getCodexLinkedChangeLabel(codexAutomationState?.next_queued_change)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">State message</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {codexAutomationState?.status_message || codexAutomationState?.next_action?.message || "Start the automation to select the first linked DEV IDE change."}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Full Change summary</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {getCodexFullChangeSummaryLabel(codexAutomationState?.full_change_codex_summary)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Auto Process Next setting</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {getAutoProcessNextLabel(codexAutomationState?.auto_process_next)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Auto waiting / blocked reason</Typography>
                    <Typography variant="caption" color={codexAutomationState?.auto_process_next?.blocked ? "warning.main" : "text.secondary"} sx={{ fontWeight: codexAutomationState?.auto_process_next?.blocked ? 700 : 400 }}>
                      {getAutoProcessNextDetail(codexAutomationState?.auto_process_next, codexAutomationState)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Last automatic Process Next</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getAutoProcessNextLastActionLabel(codexAutomationState?.auto_process_next)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Finish instruction</Typography>
                    <Typography variant="caption" color={codexAutomationState?.finish_instruction ? "warning.main" : "text.secondary"} sx={{ fontWeight: codexAutomationState?.finish_instruction ? 700 : 400 }}>
                      {codexAutomationState?.finish_instruction?.instruction || "Not ready. Finish signal appears only after all linked DEV IDE changes are saved or otherwise resolved and Process Next completes the parent queue."}
                    </Typography>
                  </Box>

                  {(() => {
                    const activeCodexChangeId = codexAutomationState?.active_linked_change?.id || codexAutomationState?.active_dev_ide_change_id;
                    const activeCodexStatus = activeCodexChangeId ? codexHandoffStatusByChangeId[activeCodexChangeId] : {};
                    return renderCodexLatestSaveEvidence(codexAutomationState, activeCodexStatus || {});
                  })()}

                  {codexAutomationState?.finish_instruction ? (
                    <Alert severity="warning" sx={{ mb: 1, py: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {codexAutomationState.finish_instruction.title || "Full Change Codex processing complete"}
                      </Typography>
                      <Typography variant="caption">
                        Parent closure review is required. This finish signal does not trigger rebuild, restart, deployment, promotion, or verification automation.
                      </Typography>
                    </Alert>
                  ) : null}

                  {renderCodexCloseoutChecklist(codexAutomationState)}

                  <Box sx={{ border: "1px solid rgba(0,0,0,0.10)", borderRadius: 1, p: 1, mb: 1, bgcolor: "background.default" }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Configured Verification Actions</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                      Run the project-configured build, restart, and health-check actions after the returned files have been saved. Results are stored in workflow status and remain visible after refresh.
                    </Typography>
                    {renderVerificationActionControls(getVerificationTargetChange(), false)}
                  </Box>

                  <FullChangeSmokeTestPanel
                    fullChangeId={selectedId}
                    moduleName={selected?.module_name}
                    visible={shouldShowCodexCloseoutChecklist(codexAutomationState)}
                  />

                  <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                      <Checkbox
                        checked={Boolean(codexAutomationState?.automation_metadata_json?.controlled_save_enabled)}
                        onChange={(event) => handleCodexControlledSaveToggle(event.target.checked)}
                        disabled={codexAutomationLoading || !selectedId || !codexAutomationState}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Enable controlled save after approved Codex Import Review risk gate. This can save changed files for the next export, but will not run rebuild, restart, health check, or verification.
                      </Typography>
                    }
                  />

                  <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                      <Checkbox
                        checked={Boolean(codexAutomationState?.automation_metadata_json?.force_save_all_returned_files_enabled)}
                        onChange={(event) => handleCodexForceSaveAllToggle(event.target.checked)}
                        disabled={codexAutomationLoading || !selectedId || !codexAutomationState}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Force save all eligible returned Codex ZIP files. This bypasses the Development tab Import Review modal and does not pause on normal risk-gate review/block decisions, but hard ZIP/path safety checks still apply.
                      </Typography>
                    }
                  />

                  <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                      <Checkbox
                        checked={Boolean(codexAutomationState?.automation_metadata_json?.auto_process_next_enabled)}
                        onChange={(event) => handleCodexAutoProcessNextToggle(event.target.checked)}
                        disabled={codexAutomationLoading || !selectedId || !codexAutomationState}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Auto Process Next when safe. This only advances the guided Codex queue by invoking the existing Process Next action once per safe checkpoint; it is separate from controlled-save and force-save and does not rebuild, restart, deploy, verify, or copy local runner files.
                      </Typography>
                    }
                  />

                  {codexAutomationState?.automation_metadata_json?.auto_process_next_enabled ? (
                    <Alert severity={codexAutomationState?.automation_status === "paused" ? "warning" : "info"} sx={{ mb: 1, py: 0.75 }}>
                      <Typography variant="caption">
                        {getAutoProcessNextDetail(codexAutomationState?.auto_process_next, codexAutomationState)} Manual controls remain visible for override and troubleshooting.
                      </Typography>
                    </Alert>
                  ) : null}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}>
                    <Button size="small" variant="contained" onClick={() => handleCodexAutomationAction("start")} disabled={codexAutomationLoading || Boolean(queueActionLoadingId) || !selectedId}>
                      Start
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleCodexAutomationAction("pause")} disabled={codexAutomationLoading || Boolean(queueActionLoadingId) || !selectedId}>
                      Pause
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => handleCodexAutomationAction("resume")} disabled={codexAutomationLoading || Boolean(queueActionLoadingId) || !selectedId}>
                      Resume
                    </Button>
                    <Button size="small" variant="contained" color="warning" onClick={() => handleCodexAutomationAction("process_next")} disabled={codexAutomationLoading || Boolean(queueActionLoadingId) || !selectedId}>
                      {codexAutomationLoading ? "Processing…" : "Process Next"}
                    </Button>
                  </Stack>

                  {(codexStartPromptText || codexStartPromptCopyMessage) ? (
                    <Box sx={{ border: "1px solid rgba(0,0,0,0.10)", borderRadius: 1, p: 1, mt: 1, bgcolor: "background.default" }}>
                      <Stack spacing={0.75}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75} sx={{ alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}>
                          <Box>
                            <Typography variant="subtitle2">Full Change Start / Edit-Only Workflow Prompt</Typography>
                            {codexStartPromptCopyMessage ? (
                              <Typography variant="caption" color="text.secondary">
                                {codexStartPromptCopyMessage}
                              </Typography>
                            ) : null}
                          </Box>
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
                            <Button size="small" variant="outlined" onClick={handleCopyCodexStartPrompt} disabled={codexAutomationLoading || !selectedId}>
                              Copy Start Prompt
                            </Button>
                            <Button size="small" variant="outlined" onClick={handleCopyCodexEditOnlyWorkflowPrompt} disabled={codexAutomationLoading || !selectedId}>
                              Copy Edit-Only Workflow Prompt
                            </Button>
                          </Stack>
                        </Stack>
                        {codexStartPromptText ? (
                          <TextField
                            size="small"
                            multiline
                            minRows={5}
                            maxRows={10}
                            value={codexStartPromptText}
                            onChange={(event) => setCodexStartPromptText(event.target.value)}
                            fullWidth
                            inputProps={{ spellCheck: "false" }}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                  ) : null}
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, backgroundColor: "rgba(255,255,255,0.72)" }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2">Create Linked DEV IDE Change</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Create a new child under this Full Change. Existing queue links are unchanged.
                      </Typography>
                    </Box>
                    <Button
                      data-testid="full-change-create-child-open"
                      variant="contained"
                      onClick={openCreateLinkedDialog}
                      disabled={linkedLoading || createLinkedLoading || !selectedId || String(selected?.id || "") !== String(selectedId)}
                      sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
                    >
                      Add DEV IDE Change
                    </Button>
                  </Stack>
                  {createdLinkedChange && String(createdLinkedChange.parentId) === String(selectedId) ? (
                    <Alert data-testid="full-change-create-child-success" severity="success" sx={{ mt: 1 }}>
                      {createdLinkedChange.id ? (
                        <>Created linked DEV IDE change <strong data-testid="full-change-create-child-created-id">#{createdLinkedChange.id}</strong> — {createdLinkedChange.title}. The child is in the queue below.</>
                      ) : (
                        <>Change created, but the server did not return its ID. Check the refreshed queue before creating another change.</>
                      )}
                    </Alert>
                  ) : null}
                </Paper>

                <Dialog
                  data-testid="full-change-create-child-dialog"
                  open={createLinkedDialogOpen}
                  onClose={closeCreateLinkedDialog}
                  maxWidth="sm"
                  fullWidth
                  PaperProps={{ sx: { m: { xs: 1, sm: 2 }, width: { xs: "calc(100% - 16px)", sm: "100%" } } }}
                >
                  <Box component="form" onSubmit={(event) => { event.preventDefault(); handleCreateLinkedDevIdeChange(); }}>
                    <DialogTitle>Create Linked DEV IDE Change</DialogTitle>
                    <DialogContent dividers>
                      <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <Typography data-testid="full-change-create-child-parent" variant="body2" color="text.secondary">
                          Full Change #{selectedId}{String(selected?.id || "") === String(selectedId) && selected?.title ? ` — ${selected.title}` : ""}
                          {selected?.project_id ? ` · Project: ${selected.project_id}` : ""}
                        </Typography>
                        <TextField
                          autoFocus
                          required
                          size="small"
                          label="Change title"
                          value={createLinkedTitle}
                          onChange={(event) => { setCreateLinkedTitle(event.target.value); setCreateLinkedError(""); }}
                          inputProps={{ "data-testid": "full-change-create-child-title" }}
                          disabled={createLinkedLoading}
                          fullWidth
                        />
                        <TextField
                          size="small"
                          label="Change description"
                          value={createLinkedDescription}
                          onChange={(event) => setCreateLinkedDescription(event.target.value)}
                          inputProps={{ "data-testid": "full-change-create-child-description" }}
                          disabled={createLinkedLoading}
                          multiline
                          minRows={2}
                          fullWidth
                        />
                        <TextField
                          select
                          required
                          size="small"
                          label="Module"
                          value={createLinkedModule}
                          onChange={(event) => { setCreateLinkedModule(event.target.value); setCreateLinkedError(""); }}
                          SelectProps={{ SelectDisplayProps: { "data-testid": "full-change-create-child-module" } }}
                          inputProps={{ "data-testid": "full-change-create-child-module-input" }}
                          disabled={createLinkedLoading || moduleOptionsLoading || !selected?.project_id}
                          helperText={moduleOptionsLoading ? "Loading project modules..." : "Defaults to the Full Change module; choose another module or ALL if needed."}
                          fullWidth
                        >
                          <MenuItem value={ALL_MODULES_VALUE}>{ALL_MODULES_LABEL}</MenuItem>
                          {(selectedModuleOptions || []).filter((row) => {
                            const value = getModuleOptionValue(row);
                            return value && !isAllModulesValue(value);
                          }).map((row) => {
                            const value = getModuleOptionValue(row);
                            return <MenuItem key={value} value={value}>{getModuleOptionLabel(row)}</MenuItem>;
                          })}
                          {createLinkedModule && !isAllModulesValue(createLinkedModule) &&
                            !(selectedModuleOptions || []).some((row) => getModuleOptionValue(row) === createLinkedModule) ? (
                              <MenuItem value={createLinkedModule}>{getModuleOptionLabel(createLinkedModule)} (parent module)</MenuItem>
                            ) : null}
                        </TextField>
                        <Typography variant="body2" color="text.secondary">
                          Status: OPEN · Queue order: automatic (next estimated {getNextLinkedChangeOrder()}).
                          The server assigns the actual queue order.
                        </Typography>
                        {createLinkedError && (
                          <Alert data-testid="full-change-create-child-error" severity="error">{createLinkedError}</Alert>
                        )}
                      </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, py: 1.5 }}>
                      <Button
                        data-testid="full-change-create-child-cancel"
                        type="button"
                        onClick={closeCreateLinkedDialog}
                        disabled={createLinkedLoading}
                      >Cancel</Button>
                      <Button
                        data-testid="full-change-create-child-submit"
                        type="submit"
                        variant="contained"
                        disabled={createLinkedLoading || linkedLoading || moduleOptionsLoading || !String(createLinkedTitle || "").trim() || !String(createLinkedModule || "").trim()}
                      >
                        {createLinkedLoading ? "Creating..." : "Create Linked Change"}
                      </Button>
                    </DialogActions>
                  </Box>
                </Dialog>

                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, backgroundColor: "rgba(255,255,255,0.72)" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Assign Existing to Queue</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", sm: "center" } }}>
                    <TextField size="small" label="existing dev_ide_change_id" value={assignDevIdeChangeId} onChange={(e) => setAssignDevIdeChangeId(e.target.value)} fullWidth sx={{ width: { xs: "100%", sm: 220 } }} />
                    <TextField size="small" label="order (optional)" value={assignOrder} onChange={(e) => setAssignOrder(e.target.value)} fullWidth sx={{ width: { xs: "100%", sm: 160 } }} />
                    <Button variant="outlined" onClick={handleAssign} disabled={linkedLoading} sx={{ width: { xs: "100%", sm: "auto" } }}>Assign Existing to Queue</Button>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, mb: 1, backgroundColor: "rgba(255,255,255,0.72)" }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Aggregate Preview to Target</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Target migration is a deployment preview/install workflow, not a planning ZIP import and not the returned implementation Import Review. Use it after child changes have been reviewed and saved to compare the source project against a selected target, then install eligible changed files through the existing migration apply path.
                  </Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: { xs: "stretch", md: "center" } }}>
                    <TextField
                      size="small"
                      select
                      label="Source project"
                      value={targetPreviewSourceProjectId}
                      onChange={(e) => setTargetPreviewSourceProjectId(e.target.value)}
                      fullWidth
                      sx={{ minWidth: { xs: "100%", md: 220 }, flex: 1 }}
                    >
                      <MenuItem value="">Select source</MenuItem>
                      {(projectOptions || []).map((project) => (
                        <MenuItem key={`target-preview-source-${project.project_id}`} value={project.project_id}>
                          {project.label || project.project_id}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      select
                      label="Target project"
                      value={targetPreviewTargetProjectId}
                      onChange={(e) => setTargetPreviewTargetProjectId(e.target.value)}
                      fullWidth
                      sx={{ minWidth: { xs: "100%", md: 220 }, flex: 1 }}
                    >
                      <MenuItem value="">Select target</MenuItem>
                      {(projectOptions || []).map((project) => (
                        <MenuItem key={`target-preview-target-${project.project_id}`} value={project.project_id} disabled={project.project_id === targetPreviewSourceProjectId}>
                          {project.label || project.project_id}
                        </MenuItem>
                      ))}
                    </TextField>
                    <FormControlLabel
                      control={<Checkbox checked={targetPreviewIncludeSql} onChange={(e) => setTargetPreviewIncludeSql(e.target.checked)} />}
                      label="Include SQL"
                      sx={{ mx: 0 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleRunTargetPreview}
                      disabled={targetPreviewLoading || targetInstallLoading || linkedLoading || !selectedId || !targetPreviewSourceProjectId || !targetPreviewTargetProjectId || targetPreviewSourceProjectId === targetPreviewTargetProjectId}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      {targetPreviewLoading ? "Previewing…" : "Preview to Target"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleInstallTargetMigration}
                      disabled={targetPreviewLoading || targetInstallLoading || linkedLoading || !selectedId || !targetPreviewSourceProjectId || !targetPreviewTargetProjectId || targetPreviewSourceProjectId === targetPreviewTargetProjectId}
                      sx={{ width: { xs: "100%", md: "auto" } }}
                    >
                      {targetInstallLoading ? "Installing…" : "Install to Target"}
                    </Button>
                  </Stack>
                </Paper>

                {linkedError && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                    {linkedError}
                  </Typography>
                )}
                {queueWorkflowStatusError && (
                  <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                    Workflow evidence unavailable for one or more linked changes; the affected children are marked Status unavailable: {queueWorkflowStatusError}
                  </Alert>
                )}
                {queueActionMessage ? (
                  <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                    {queueActionMessage}
                  </Alert>
                ) : null}

                {renderQueueLifecycleTotals()}

                <Box data-testid="full-change-queue" ref={linkedQueueRef} sx={{ border: "1px solid rgba(25,118,210,0.20)", borderRadius: 1, overflowX: "auto", maxWidth: "100%", backgroundColor: "background.paper" }}>
                  {linkedLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}

                  {!linkedLoading && (!linkedChanges || linkedChanges.length === 0) && (
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        No linked DEV IDE changes yet.
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        Next step: create or assign at least one child DEV IDE change, or import GPT's returned planning ZIP to create proposed child units. Keep the all-modules code/docs context ZIP out of this importer; use it only as GPT reference material. Implementation ZIPs are imported later through each child change's Import Returned Package action, or through Open Codex Import Review / Run Codex Auto Import when the ZIP is in the configured Codex incoming folder.
                      </Typography>
                    </Box>
                  )}

                  {!linkedLoading && linkedChanges && linkedChanges.length > 0 && (
                    <>
                      <Stack spacing={1.25} sx={{ display: { xs: "flex", md: "none" }, p: 1 }}>
                        {(linkedChanges || []).map((c, idx) => {
                          const changedFilesCount = c.changed_files_count ?? c.changed_file_count ?? c.file_count ?? c.files_count;
                          const rowWorkflowStatus = getChildQueueWorkflowStatus(c);
                          const exportStatus = getQueueEvidence(rowWorkflowStatus, "packageExported").label;
                          const importStatus = getQueueEvidence(rowWorkflowStatus, "packageImported").label;
                          const saveApplyStatus = getQueueEvidence(rowWorkflowStatus, "changedFilesSaved").label;
                          const verificationStatus = getQueueVerificationChecksEvidence(rowWorkflowStatus);
                          const humanReviewStatus = c.human_review_status ?? c.review_status ?? c.acceptance_status ?? "Not recorded";
                          const codexStatus = codexHandoffStatusByChangeId[c.id] || {};
                          const codexSyncSummary = buildCodexManualSyncSummary(selected, c, codexStatus);
                          const workflowProjectId = `Project: ${getQueueProjectId(c)} · Change #${c.id}`;
                          const rowLoadingExport = queueActionLoadingId === `export-${c.id}`;
                          const rowLoadingCodexExport = queueActionLoadingId === `codex-export-${c.id}`;
                          const rowLoadingImport = queueActionLoadingId === `import-${c.id}`;
                          const rowLoadingCodexImport = queueActionLoadingId === `codex-import-${c.id}`;
                          const rowLoadingSummary = queueActionLoadingId === `summary-${c.id}`;
                          const unavailableText = "Not available inline yet. Use Development tab / Import Review for this existing workflow action.";
                          const mobileStatusRows = [
                            ["Export", exportStatus, "Export package workflow status"],
                            ["Import", importStatus, "Import package workflow status"],
                            ["Save/apply", saveApplyStatus, "Save/apply workflow status"],
                            ["Checks (not acceptance)", verificationStatus, "Recorded action checks; not a verification decision"],
                            ["Human review", humanReviewStatus, "Human review workflow status"],
                          ];

                          return (
                            <Paper
                              key={c.id}
                              data-testid={`full-change-child-${c.id}-mobile`}
                              data-linked-queue-child-id={c.id}
                              onClickCapture={() => rememberLinkedChild(c)}
                              variant="outlined"
                              sx={{
                                p: 1.25,
                                borderRadius: 2,
                                ...(highlightedLinkedChildId === String(c.id) ? {
                                  borderColor: "primary.main", backgroundColor: "action.selected",
                                } : {}),
                              }}
                            >
                              <Stack spacing={1}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Order {c.full_change_order ?? idx + 1} · Change #{c.id}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {c.title || "Untitled change"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Rollbackable child implementation package
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                                  <Chip size="small" label={`Status: ${c.status || "—"}`} variant="outlined" />
                                  <Chip size="small" label={`Files: ${changedFilesCount ?? "—"}`} variant="outlined" />
                                </Stack>

                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "minmax(86px, 0.9fr) minmax(120px, 1.1fr)",
                                    gap: 0.75,
                                    alignItems: "center",
                                  }}
                                >
                                  {mobileStatusRows.map(([label, status, title]) => (
                                    <React.Fragment key={label}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        {label}
                                      </Typography>
                                      <Box sx={{ minWidth: 0 }}>{renderQueueStatusChip(status, `${workflowProjectId} · ${title}`)}</Box>
                                    </React.Fragment>
                                  ))}
                                  {isCodexLedWorkflow ? (
                                    <>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                        Codex
                                      </Typography>
                                      <Box sx={{ minWidth: 0 }}>{renderCodexHandoffStatus(codexStatus, true)}</Box>
                                    </>
                                  ) : null}
                                </Box>

                                {renderChildLifecycleEvidence(c, true)}

                                <Typography variant="caption" color="text.secondary">
                                  Active linked change context: #{c.id} — {c.title || "Untitled change"}. {isCodexLedWorkflow ? "Codex handoff export is manual and does not run Codex." : "Use the standard package actions for person-led GPT Chat work."}
                                </Typography>

                                {isCodexLedWorkflow ? renderCodexManualSyncGuidance(codexSyncSummary, false) : null}
                                {isCodexLedWorkflow ? renderCodexTargetingEditor(c, true) : null}

                                <Stack spacing={0.75}>
                                  <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-view-mobile`} onClick={() => setViewLinkedChange(c)} disabled={Boolean(queueActionLoadingId)} fullWidth>
                                    View
                                  </Button>
                                  <Button size="small" variant="contained" data-testid={`full-change-child-${c.id}-export-mobile`} onClick={() => handleQueueExportPackage(c)} disabled={Boolean(queueActionLoadingId)} fullWidth>
                                    {rowLoadingExport ? "Exporting..." : "Export Agent Package"}
                                  </Button>
                                  {isCodexLedWorkflow ? (
                                    <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-export-codex-handoff-mobile`} onClick={() => handleQueueExportCodexHandoff(c)} disabled={Boolean(queueActionLoadingId)} fullWidth>
                                      {rowLoadingCodexExport ? "Exporting Codex..." : "Export Codex Handoff"}
                                    </Button>
                                  ) : null}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    data-testid={`full-change-child-${c.id}-import-returned-mobile`}
                                    onClick={() => handleQueueImportReturnedPackage(c)}
                                    disabled={Boolean(queueActionLoadingId)}
                                    fullWidth
                                  >
                                    {rowLoadingImport ? "Opening import..." : "Import Returned Package"}
                                  </Button>
                                  {isCodexLedWorkflow ? (
                                    <>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        data-testid={`full-change-child-${c.id}-codex-import-review-mobile`}
                                        onClick={() => handleQueueImportCodexReturn(c, {}, { manualReviewOnly: true })}
                                        disabled={Boolean(queueActionLoadingId)}
                                        fullWidth
                                      >
                                        {rowLoadingCodexImport ? "Checking Codex..." : "Open Codex Import Review"}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        data-testid={`full-change-child-${c.id}-codex-auto-import-mobile`}
                                        onClick={() => handleQueueImportCodexReturn(c)}
                                        disabled={Boolean(queueActionLoadingId)}
                                        fullWidth
                                      >
                                        {rowLoadingCodexImport ? "Checking Codex..." : "Run Codex Auto Import"}
                                      </Button>
                                    </>
                                  ) : null}
                                  <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-import-audit-mobile`} onClick={() => openChildAudit(c)} fullWidth>
                                    Import / Save Audit
                                  </Button>
                                  <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-copy-summary-mobile`} onClick={() => handleQueueCopyResultSummary(c)} disabled={Boolean(queueActionLoadingId)} fullWidth>
                                    {rowLoadingSummary ? "Copying..." : "Copy Result Summary"}
                                  </Button>
                                  <Button size="small" variant="text" data-testid={`full-change-child-${c.id}-unassign-mobile`} onClick={() => handleUnassign(c.id)} disabled={linkedLoading || Boolean(queueActionLoadingId)} fullWidth>
                                    Unassign
                                  </Button>
                                </Stack>

                                <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                                  <Box component="span" title={unavailableText} sx={{ flex: "1 1 140px" }}>
                                    <Button data-testid={`full-change-child-${c.id}-save-placeholder-mobile`} size="small" variant="outlined" disabled fullWidth>Save Changed Files</Button>
                                  </Box>
                                  <Box component="span" title={unavailableText} sx={{ flex: "1 1 140px" }}>
                                    <Button data-testid={`full-change-child-${c.id}-review-placeholder-mobile`} size="small" variant="outlined" disabled fullWidth>Open Import Review</Button>
                                  </Box>
                                </Stack>

                                {renderVerificationActionControls(c, true)}

                                <Typography variant="caption" color="text.secondary">
                                  {isCodexLedWorkflow ? CODEX_QUEUE_HELPER_TEXT : "Person-led GPT Chat mode keeps manual package review actions visible and hides Codex handoff automation."}
                                </Typography>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>

                      <Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: isCodexLedWorkflow
                              ? "70px minmax(240px, 1.4fr) 110px 80px 120px 120px 120px 135px 135px 170px minmax(520px, 1fr)"
                              : "70px minmax(240px, 1.4fr) 110px 80px 120px 120px 120px 135px 135px minmax(520px, 1fr)",
                            gap: 1,
                            px: 1,
                            py: 0.75,
                            borderBottom: "1px solid rgba(0,0,0,0.12)",
                            backgroundColor: "rgba(0,0,0,0.03)",
                            minWidth: isCodexLedWorkflow ? 1820 : 1650,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Order</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Change title</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Status</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Files</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Export</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Import</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Save/apply</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Checks</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Human review</Typography>
                          {isCodexLedWorkflow ? (
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Codex handoff</Typography>
                          ) : null}
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Inline Agent Workflow actions</Typography>
                        </Box>

                        {(linkedChanges || []).map((c, idx) => {
                          const changedFilesCount = c.changed_files_count ?? c.changed_file_count ?? c.file_count ?? c.files_count;
                          const rowWorkflowStatus = getChildQueueWorkflowStatus(c);
                          const exportStatus = getQueueEvidence(rowWorkflowStatus, "packageExported").label;
                          const importStatus = getQueueEvidence(rowWorkflowStatus, "packageImported").label;
                          const saveApplyStatus = getQueueEvidence(rowWorkflowStatus, "changedFilesSaved").label;
                          const verificationStatus = getQueueVerificationChecksEvidence(rowWorkflowStatus);
                          const humanReviewStatus = c.human_review_status ?? c.review_status ?? c.acceptance_status ?? "Not recorded";
                          const codexStatus = codexHandoffStatusByChangeId[c.id] || {};
                          const codexSyncSummary = buildCodexManualSyncSummary(selected, c, codexStatus);
                          const workflowProjectId = `Project: ${getQueueProjectId(c)} · Change #${c.id}`;
                          const rowLoadingExport = queueActionLoadingId === `export-${c.id}`;
                          const rowLoadingCodexExport = queueActionLoadingId === `codex-export-${c.id}`;
                          const rowLoadingImport = queueActionLoadingId === `import-${c.id}`;
                          const rowLoadingCodexImport = queueActionLoadingId === `codex-import-${c.id}`;
                          const rowLoadingSummary = queueActionLoadingId === `summary-${c.id}`;
                          const unavailableText = "Not available inline yet. Use Development tab / Import Review for this existing workflow action.";

                          return (
                            <Box
                              key={c.id}
                              data-testid={`full-change-child-${c.id}-desktop`}
                              data-linked-queue-child-id={c.id}
                              onClickCapture={() => rememberLinkedChild(c)}
                              sx={{
                                ...(highlightedLinkedChildId === String(c.id) ? {
                                  backgroundColor: "action.selected",
                                  boxShadow: "inset 3px 0 0 #1976d2",
                                } : {}),
                                display: "grid",
                                gridTemplateColumns: isCodexLedWorkflow
                                  ? "70px minmax(240px, 1.4fr) 110px 80px 120px 120px 120px 135px 135px 170px minmax(520px, 1fr)"
                                  : "70px minmax(240px, 1.4fr) 110px 80px 120px 120px 120px 135px 135px minmax(520px, 1fr)",
                                gap: 1,
                                px: 1,
                                py: 1,
                                borderBottom: "1px solid rgba(0,0,0,0.08)",
                                alignItems: "center",
                                minWidth: isCodexLedWorkflow ? 1820 : 1650,
                              }}
                            >
                              <Typography variant="body2">{c.full_change_order ?? idx + 1}</Typography>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Change #{c.id} — {c.title || "Untitled change"}</Typography>
                                <Typography variant="caption" color="text.secondary">Rollbackable child implementation package</Typography>
                                {renderChildLifecycleEvidence(c, false)}
                              </Box>
                              <Typography variant="body2">{c.status || "—"}</Typography>
                              <Typography variant="body2">{changedFilesCount ?? "—"}</Typography>
                              {renderQueueStatusChip(exportStatus, workflowProjectId || "Export package workflow status")}
                              {renderQueueStatusChip(importStatus, workflowProjectId || "Import package workflow status")}
                              {renderQueueStatusChip(saveApplyStatus, workflowProjectId || "Save/apply workflow status")}
                              {renderQueueStatusChip(verificationStatus, `${workflowProjectId} · Checkpoint outcomes, not full verification`)}
                              {renderQueueStatusChip(humanReviewStatus, workflowProjectId || "Human review workflow status")}
                              {isCodexLedWorkflow ? renderCodexHandoffStatus(codexStatus, true) : null}
                              <Stack spacing={0.75}>
                                <Typography variant="caption" color="text.secondary">
                                  Active linked change context: #{c.id} — {c.title || "Untitled change"}. {isCodexLedWorkflow ? "Codex handoff export is manual and does not run Codex." : "Use the standard package actions for person-led GPT Chat work."}
                                </Typography>
                                {isCodexLedWorkflow ? renderCodexManualSyncGuidance(codexSyncSummary, true) : null}
                                {isCodexLedWorkflow ? renderCodexTargetingEditor(c, false) : null}
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-view-desktop`} onClick={() => setViewLinkedChange(c)} disabled={Boolean(queueActionLoadingId)}>
                                    View
                                  </Button>
                                  <Button size="small" variant="contained" data-testid={`full-change-child-${c.id}-export-desktop`} onClick={() => handleQueueExportPackage(c)} disabled={Boolean(queueActionLoadingId)}>
                                    {rowLoadingExport ? "Exporting..." : "Export Agent Package"}
                                  </Button>
                                  {isCodexLedWorkflow ? (
                                    <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-export-codex-handoff-desktop`} onClick={() => handleQueueExportCodexHandoff(c)} disabled={Boolean(queueActionLoadingId)}>
                                      {rowLoadingCodexExport ? "Exporting Codex..." : "Export Codex Handoff"}
                                    </Button>
                                  ) : null}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    data-testid={`full-change-child-${c.id}-import-returned-desktop`}
                                    onClick={() => handleQueueImportReturnedPackage(c)}
                                    disabled={Boolean(queueActionLoadingId)}
                                  >
                                    {rowLoadingImport ? "Opening import..." : "Import Returned Package"}
                                  </Button>
                                  {isCodexLedWorkflow ? (
                                    <>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        data-testid={`full-change-child-${c.id}-codex-import-review-desktop`}
                                        onClick={() => handleQueueImportCodexReturn(c, {}, { manualReviewOnly: true })}
                                        disabled={Boolean(queueActionLoadingId)}
                                      >
                                        {rowLoadingCodexImport ? "Checking Codex..." : "Open Codex Import Review"}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        data-testid={`full-change-child-${c.id}-codex-auto-import-desktop`}
                                        onClick={() => handleQueueImportCodexReturn(c)}
                                        disabled={Boolean(queueActionLoadingId)}
                                      >
                                        {rowLoadingCodexImport ? "Checking Codex..." : "Run Codex Auto Import"}
                                      </Button>
                                    </>
                                  ) : null}
                                  <Box component="span" title={unavailableText}>
                                    <Button data-testid={`full-change-child-${c.id}-save-placeholder-desktop`} size="small" variant="outlined" disabled>Save Changed Files</Button>
                                  </Box>
                                  <Box component="span" title={unavailableText}>
                                    <Button data-testid={`full-change-child-${c.id}-review-placeholder-desktop`} size="small" variant="outlined" disabled>Open Import Review</Button>
                                  </Box>
                                  <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-import-audit-desktop`} onClick={() => openChildAudit(c)}>
                                    Import / Save Audit
                                  </Button>
                                  <Button size="small" variant="outlined" data-testid={`full-change-child-${c.id}-copy-summary-desktop`} onClick={() => handleQueueCopyResultSummary(c)} disabled={Boolean(queueActionLoadingId)}>
                                    {rowLoadingSummary ? "Copying..." : "Copy Result Summary"}
                                  </Button>
                                  <Button size="small" variant="text" data-testid={`full-change-child-${c.id}-unassign-desktop`} onClick={() => handleUnassign(c.id)} disabled={linkedLoading || Boolean(queueActionLoadingId)}>Unassign</Button>
                                </Stack>
                                {renderVerificationActionControls(c, false)}
                                <Typography variant="caption" color="text.secondary">
                                  {isCodexLedWorkflow ? CODEX_QUEUE_HELPER_TEXT : "Person-led GPT Chat mode keeps manual package review actions visible and hides Codex handoff automation."}
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}

                </Box>
              </Paper>

              <FullChangeLocMetricsPanel fullChangeId={selectedId} />
              <FullChangeSqlReviewPanel fullChangeId={selectedId} />

              <FullChangeReviewWorkspace
                fullChangeId={selectedId}
                selectedFullChange={selected}
              />

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Governance / Planning / Checklist Tasks</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Use tasks for optional governance, planning, checklist, analysis, testing, documentation, release, or reviewer work. The linked DEV IDE changes above are the primary rollbackable implementation units.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  label="New task title"
                  value={taskCreateTitle}
                  onChange={(e) => setTaskCreateTitle(e.target.value)}
                  sx={{ minWidth: 260 }}
                />
                <TextField
                  size="small"
                  select
                  label="Type"
                  value={taskCreateType}
                  onChange={(e) => setTaskCreateType(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  {TASK_TYPES.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={handleCreateTask} disabled={taskCreateLoading}>
                  {taskCreateLoading ? "Working..." : "Create Task"}
                </Button>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={replaceTasksOnGenerate} onChange={(e) => setReplaceTasksOnGenerate(e.target.checked)} />}
                  label="Replace existing tasks"
                />
                <FormControlLabel
                  control={<Checkbox checked={useLlmGeneration} onChange={(e) => setUseLlmGeneration(e.target.checked)} />}
                  label="Use LLM generation"
                />
                <Button variant="contained" onClick={handleGenerateTasks} disabled={taskGenerateLoading}>
                  {taskGenerateLoading ? "Generating..." : "Generate Draft Tasks"}
                </Button>
              </Stack>

              {taskGenerationInfo && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Generated {taskGenerationInfo.createdCount} task(s) via {taskGenerationInfo.generationMethod}.
                </Typography>
              )}

              {tasksError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                  {tasksError}
                </Typography>
              )}

              {taskDiscoveryError && !taskDiscoveryDialogOpen && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                  {taskDiscoveryError}
                </Typography>
              )}

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1, minHeight: 220 }}>
                  {tasksLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  )}

                  {!tasksLoading && (!tasks || tasks.length === 0) && (
                    <Typography variant="caption" color="text.secondary">
                      No tasks yet. Create one manually or generate draft tasks from this change.
                    </Typography>
                  )}

                  {(tasks || []).map((task) => (
                    <Box
                      key={task.id}
                      onClick={() => setSelectedTaskId(task.id)}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        border: "1px solid rgba(0,0,0,0.08)",
                        mb: 1,
                        backgroundColor: selectedTaskId === task.id ? "rgba(25,118,210,0.08)" : "transparent",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {task.task_order ? `${task.task_order}. ` : ""}{task.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.task_type || "GENERAL"} • {task.status || "DRAFT"}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ flex: 1.3, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1, minHeight: 220 }}>
                  {!selectedTask ? (
                    <Typography variant="caption" color="text.secondary">
                      Select a task to edit its scope, acceptance criteria, and status.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>{selectedTaskSummary}</Typography>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Task title"
                          value={selectedTask.title || ""}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), title: e.target.value }))}
                        />
                        <TextField
                          size="small"
                          label="Order"
                          value={selectedTask.task_order ?? ""}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), task_order: e.target.value }))}
                          sx={{ width: 120 }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Task type"
                          value={selectedTask.task_type || "GENERAL"}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), task_type: e.target.value }))}
                        >
                          {TASK_TYPES.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Status"
                          value={selectedTask.status || "DRAFT"}
                          onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), status: e.target.value }))}
                        >
                          {TASK_STATUSES.map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </TextField>
                      </Stack>

                      <TextField
                        size="small"
                        fullWidth
                        label="Description"
                        value={selectedTask.description || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), description: e.target.value }))}
                        multiline
                        minRows={3}
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        size="small"
                        fullWidth
                        label="Scope summary"
                        value={selectedTask.scope_summary || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), scope_summary: e.target.value }))}
                        multiline
                        minRows={2}
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        size="small"
                        fullWidth
                        label="Allowed files / scope boundaries"
                        value={selectedTask.allowed_files || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), allowed_files: e.target.value }))}
                        multiline
                        minRows={2}
                        helperText="Optional: list the intended files or folders this task is allowed to touch."
                        sx={{ mb: 1 }}
                      />

                      <TextField
                        size="small"
                        fullWidth
                        label="Acceptance criteria"
                        value={selectedTask.acceptance_criteria || ""}
                        onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), acceptance_criteria: e.target.value }))}
                        multiline
                        minRows={3}
                        sx={{ mb: 1 }}
                      />


                      {String((selectedTask.task_type || "")).toUpperCase() === "SCHEMA" && (
                        <>
                          <TextField
                            size="small"
                            fullWidth
                            label="SQL proposal summary"
                            value={selectedTask.sql_summary || ""}
                            onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), sql_summary: e.target.value }))}
                            multiline
                            minRows={2}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Proposed SQL"
                            value={selectedTask.sql_proposal || ""}
                            onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), sql_proposal: e.target.value }))}
                            multiline
                            minRows={4}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Rollback SQL"
                            value={selectedTask.rollback_sql || ""}
                            onChange={(e) => setSelectedTask((prev) => ({ ...(prev || {}), rollback_sql: e.target.value }))}
                            multiline
                            minRows={3}
                            sx={{ mb: 1 }}
                          />
                        </>
                      )}
                      {String((selectedTask.task_type || "")).toUpperCase() === "SCHEMA" && taskSchemaTargetResult && (
                        <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1.5, mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Schema target resolution</Typography>
                          <Typography variant="body2">Database: {taskSchemaTargetResult.schema_name || ""}</Typography>
                          <Typography variant="body2">Schema intent: {taskSchemaTargetResult.schema_intent || "UNRESOLVED"}</Typography>
                          <Typography variant="body2">Resolved table: {taskSchemaTargetResult.resolved_table || "(not resolved)"}</Typography>
                          <Typography variant="body2">Proposed new table: {taskSchemaTargetResult.proposed_table_name || ""}</Typography>
                          <Typography variant="body2">Target column: {taskSchemaTargetResult.column_name || ""}</Typography>
                          <Typography variant="body2">Confidence: {taskSchemaTargetResult.resolution_confidence || "UNRESOLVED"}</Typography>
                          {!!taskSchemaTargetResult.llm_confidence && (
                            <Typography variant="body2">LLM confidence: {taskSchemaTargetResult.llm_confidence}</Typography>
                          )}
                          {!!(taskSchemaTargetResult.matched_tables || []).length && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              Top matches: {(taskSchemaTargetResult.matched_tables || []).slice(0, 5).map((row) => `${row.table_name} (${row.score})`).join(", ")}
                            </Typography>
                          )}
                          {!!taskSchemaTargetResult.llm_reason && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                              LLM reason: {taskSchemaTargetResult.llm_reason}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            {taskSchemaTargetResult.guidance || ""}
                          </Typography>
                        </Box>
                      )}
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                        <Button variant="contained" onClick={handleSaveTask} disabled={taskSaveLoading}>
                          {taskSaveLoading ? "Saving..." : "Save Task"}
                        </Button>
                        <Button variant="outlined" onClick={handleRunTaskDiscovery} disabled={taskDiscoveryLoading || !selected?.project_id}>
                          {taskDiscoveryLoading ? "Scanning..." : "Run File Discovery"}
                        </Button>
                        {String((selectedTask.task_type || "")).toUpperCase() === "SCHEMA" && (
                          <>
                            <Button variant="outlined" onClick={handleResolveTaskSchemaTarget} disabled={taskSchemaTargetLoading || !canResolveTaskSchemaTarget}>
                              {taskSchemaTargetLoading ? "Resolving..." : "Resolve Schema Target"}
                            </Button>
                            <Button variant="outlined" onClick={handleRunTaskSqlProposal} disabled={taskSqlProposalLoading || !canRunTaskSqlProposal}>
                              {taskSqlProposalLoading ? "Generating SQL..." : "Generate SQL Proposal"}
                            </Button>
                            <Button variant="outlined" color="warning" onClick={handleOpenTaskSqlRunDialog} disabled={taskSqlRunLoading || !canRunSavedTaskSql}>
                              {taskSqlRunLoading && taskSqlRunDialogOpen ? "Loading SQL..." : "Run SQL Proposal"}
                            </Button>
                          </>
                        )}
                        <Button variant="outlined" onClick={handleRunTaskPatcher} disabled={taskPatchPreviewLoading || !canRunTaskPatcher}>
                          {taskPatchPreviewLoading ? "Building Patch..." : "Run Patcher"}
                        </Button>
                        <Button variant="outlined" color="error" onClick={handleDeleteTask} disabled={taskDeleteLoading}>
                          {taskDeleteLoading ? "Deleting..." : "Delete Task"}
                        </Button>
                      </Stack>
                      {!selected?.project_id && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Set a project on the Full Change before running file discovery or patcher.
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && taskSchemaTargetError && (
                        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                          {taskSchemaTargetError}
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && !taskSchemaTargetResult && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Resolve the schema target before generating SQL so the proposal is locked to the correct live table.
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && taskSchemaTargetResult && !canRunTaskSqlProposal && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          SQL generation is blocked until the schema target resolves with strong confidence.
                        </Typography>
                      )}
                      {selected?.project_id && String((selectedTask && selectedTask.task_type) || "").toUpperCase() === "SCHEMA" && !canRunSavedTaskSql && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Save a SQL proposal on this task before running it.
                        </Typography>
                      )}
                      {selected?.project_id && !canRunTaskPatcher && (["FRONTEND", "BACKEND", "SCHEMA", "GENERAL"].includes(String((selectedTask && selectedTask.task_type) || "").toUpperCase())) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          Approve at least one file into this task before running the patcher.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Create Planner Run (optional advisory lane)</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Planner remains optional. The primary workflow should start from the approved Full Change and its child tasks. Set the Full Change project and module first so downstream planner work inherits the correct scope.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1, flexWrap: "wrap" }}>
                {selectedModuleOptions && selectedModuleOptions.length > 0 ? (
                  <TextField
                    size="small"
                    select
                    label="module"
                    value={plannerModule}
                    onChange={(e) => setPlannerModule(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {selectedModuleOptions.map((row) => {
                      const moduleName = getModuleOptionValue(row);
                      return (
                        <MenuItem key={moduleName} value={moduleName}>
                          {getModuleOptionLabel(row)}
                        </MenuItem>
                      );
                    })}
                    {(plannerModule && !selectedModuleOptions.some((row) => getModuleOptionValue(row) === plannerModule)) ? (
                      <MenuItem value={plannerModule}>{getModuleOptionLabel(plannerModule)}</MenuItem>
                    ) : null}
                  </TextField>
                ) : (
                  <TextField size="small" label="module" value={plannerModule} onChange={(e) => setPlannerModule(e.target.value)} />
                )}
                <Button variant="outlined" onClick={handleCreatePlannerRun} disabled={plannerCreateLoading}>
                  {plannerCreateLoading ? "Working..." : "Create Planner Run"}
                </Button>
              </Stack>

              <TextField
                size="small"
                fullWidth
                label="planner goal text"
                value={plannerGoalText}
                onChange={(e) => setPlannerGoalText(e.target.value)}
                multiline
                minRows={4}
                sx={{ mb: 1 }}
              />

              {plannerCreateError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>{plannerCreateError}</Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1">Linked Planner Runs</Typography>

              {plannerRunsError && (
                <Typography color="error" variant="body2" sx={{ mb: 1 }}>{plannerRunsError}</Typography>
              )}

              <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1 }}>
                {plannerRunsLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}

                {!plannerRunsLoading && (!plannerRuns || plannerRuns.length === 0) && (
                  <Typography variant="caption" color="text.secondary">No planner runs linked to this Full Change.</Typography>
                )}

                {(plannerRuns || []).map((r) => {
                  const guardStatus = (r.status || "DRAFT").toUpperCase();
                  const notApproved = guardStatus !== "APPROVED";

                  return (
                    <Box key={r.id} sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Run #{r.id} — {r.module || ""}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.status ? `Status: ${r.status}` : ""}
                        {r.planner_session_id !== null && r.planner_session_id !== undefined ? `${r.status ? " • " : ""}Session: ${r.planner_session_id}` : ""}
                        {r.created_at ? ` • Created: ${r.created_at}` : ""}
                      </Typography>

                      {notApproved && (
                        <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                          Not approved — planner output is advisory only.
                        </Typography>
                      )}

                      {r.goal_text && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {String(r.goal_text).slice(0, 200)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}


          <Dialog open={taskSqlDialogOpen} onClose={() => { if (!taskSqlProposalLoading && !taskSqlApplyLoading) setTaskSqlDialogOpen(false); }} maxWidth="md" fullWidth>
            <DialogTitle>Schema SQL Proposal</DialogTitle>
            <DialogContent dividers>
              {taskSqlProposalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskSqlProposalError}
                </Alert>
              )}
              {!taskSqlProposalResult && !taskSqlProposalLoading && (
                <Typography variant="body2" color="text.secondary">
                  No SQL proposal loaded.
                </Typography>
              )}
              {taskSqlProposalLoading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Generating SQL proposal…</Typography>
                </Stack>
              )}
              {taskSqlProposalResult && (
                <>
                  {!!(taskSqlProposalResult?.schema_target || taskSchemaTargetResult) && (
                    <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1.5, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Resolved schema target</Typography>
                      <Typography variant="body2">Database: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.schema_name || ""}</Typography>
                      <Typography variant="body2">Schema intent: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.schema_intent || ""}</Typography>
                      <Typography variant="body2">Table: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.resolved_table || (taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.proposed_table_name || ""}</Typography>
                      <Typography variant="body2">Column: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.column_name || ""}</Typography>
                      <Typography variant="body2">Confidence: {(taskSqlProposalResult?.schema_target || taskSchemaTargetResult)?.resolution_confidence || ""}</Typography>
                    </Box>
                  )}
                  <TextField
                    size="small"
                    fullWidth
                    label="Summary"
                    value={taskSqlSummaryDraft}
                    onChange={(e) => setTaskSqlSummaryDraft(e.target.value)}
                    multiline
                    minRows={3}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Proposed SQL"
                    value={taskSqlProposalDraft}
                    onChange={(e) => setTaskSqlProposalDraft(e.target.value)}
                    multiline
                    minRows={8}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Rollback SQL"
                    value={taskRollbackSqlDraft}
                    onChange={(e) => setTaskRollbackSqlDraft(e.target.value)}
                    multiline
                    minRows={6}
                    sx={{ mb: 2 }}
                  />
                  {!!(taskSqlProposalResult?.proposal?.resolved_files || []).length && (
                    <Typography variant="caption" color="text.secondary">
                      Inspected files: {(taskSqlProposalResult.proposal.resolved_files || []).join(", ")}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskSqlDialogOpen(false)} disabled={taskSqlProposalLoading || taskSqlApplyLoading}>
                Close
              </Button>
              <Button onClick={handleRunTaskSqlProposal} variant="outlined" disabled={taskSqlProposalLoading || taskSqlApplyLoading || !canRunTaskSqlProposal}>
                Rebuild Proposal
              </Button>
              <Button onClick={handleApplyTaskSqlProposal} variant="contained" disabled={taskSqlProposalLoading || taskSqlApplyLoading || !String(taskSqlProposalDraft || "").trim()}>
                {taskSqlApplyLoading ? "Saving..." : "Accept SQL Proposal"}
              </Button>
            </DialogActions>
          </Dialog>

          {renderPlanningImportDialog()}
          {renderTargetPreviewDialog()}
          {renderLinkedChangeViewModal()}
          {renderChildAuditModal()}

          <Dialog open={taskSqlRunDialogOpen} onClose={() => { if (!taskSqlRunLoading) setTaskSqlRunDialogOpen(false); }} maxWidth="md" fullWidth>
            <DialogTitle>Run SQL Proposal</DialogTitle>
            <DialogContent dividers>
              {taskSqlRunError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskSqlRunError}
                </Alert>
              )}
              {taskSqlRunExecution && taskSqlRunExecution.success === true && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {taskSqlRunExecution.message || "SQL executed successfully."}
                </Alert>
              )}
              {taskSqlRunExecution && taskSqlRunExecution.success === false && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskSqlRunExecution.message || "SQL execution failed."}
                </Alert>
              )}
              {taskSqlRunLoading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Preparing SQL execution details…</Typography>
                </Stack>
              )}
              {!taskSqlRunLoading && !taskSqlRunPreview && (
                <Typography variant="body2" color="text.secondary">
                  No SQL execution preview loaded.
                </Typography>
              )}
              {taskSqlRunPreview && (
                <Stack spacing={2}>
                  <Alert severity="warning">
                    Review the target database and exact SQL below before execution. This action runs against the configured project database.
                  </Alert>
                  <TextField
                    size="small"
                    fullWidth
                    label="Target database"
                    value={taskSqlRunPreview.database_name || ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Schema source"
                    value={taskSqlRunPreview.schema_source || ""}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="SQL summary"
                    value={taskSqlRunPreview.sql_summary || ""}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={2}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={`SQL to run (${taskSqlRunPreview.statement_count || 0} statement${(taskSqlRunPreview.statement_count || 0) === 1 ? "" : "s"})`}
                    value={taskSqlRunPreview.sql_proposal || ""}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={8}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Rollback SQL"
                    value={taskSqlRunPreview.rollback_sql || ""}
                    InputProps={{ readOnly: true }}
                    multiline
                    minRows={5}
                  />
                  <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Execution outcome</Typography>
                    {!taskSqlRunExecution || taskSqlRunExecution.dry_run ? (
                      <Typography variant="body2" color="text.secondary">
                        Preview loaded. SQL has not been executed yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          Database: {taskSqlRunExecution.database_name || taskSqlRunPreview.database_name || ""}
                        </Typography>
                        <Typography variant="body2">
                          Executed statements: {taskSqlRunExecution.executed_count || 0} / {taskSqlRunExecution.statement_count || 0}
                        </Typography>
                        {(taskSqlRunExecution.statement_results || []).map((result) => (
                          <Box key={result.index || JSON.stringify(result)} sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1, p: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Statement {result.index || "?"}
                            </Typography>
                            {result.preview && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                {result.preview}
                              </Typography>
                            )}
                            {result.error ? (
                              <Typography variant="body2" color="error">
                                Error: {result.error}
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="body2">Row count: {result.rowcount ?? 0}</Typography>
                                {result.returned_row_count !== undefined && (
                                  <Typography variant="body2">Returned rows: {result.returned_row_count}</Typography>
                                )}
                              </>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskSqlRunDialogOpen(false)} disabled={taskSqlRunLoading}>
                Close
              </Button>
              <Button onClick={handleOpenTaskSqlRunDialog} variant="outlined" disabled={taskSqlRunLoading || !canRunSavedTaskSql}>
                Refresh Preview
              </Button>
              <Button onClick={handleExecuteTaskSqlProposal} variant="contained" color="warning" disabled={taskSqlRunLoading || !taskSqlRunPreview}>
                {taskSqlRunLoading && taskSqlRunPreview ? "Running SQL..." : "Run SQL Proposal"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={taskPatchDialogOpen} onClose={() => { if (!taskPatchPreviewLoading && !taskPatchApplyLoading) setTaskPatchDialogOpen(false); }} maxWidth="lg" fullWidth>
            <DialogTitle>Task Patch Preview</DialogTitle>
            <DialogContent dividers>
              {taskPatchPreviewError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {taskPatchPreviewError}
                </Alert>
              )}
              {taskPatchApplyResult && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Applied {taskPatchApplyResult?.applied?.length || 0} file(s).
                </Alert>
              )}
              {!taskPatchPreviewResult && !taskPatchPreviewLoading && (
                <Typography variant="body2" color="text.secondary">
                  No patch preview yet.
                </Typography>
              )}
              {taskPatchPreviewLoading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Building patch preview…</Typography>
                </Stack>
              )}
              {taskPatchPreviewResult && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {taskPatchPreviewResult.preview?.summary || "Review the diff(s) before apply."}
                  </Typography>
                  {(taskPatchPreviewResult.preview?.previews || []).map((p, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {p.path} {p.success ? "" : "(error)"}
                      </Typography>
                      {!p.success && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {p.error || "Failed to build preview."}
                        </Alert>
                      )}
                      {p.success && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            whiteSpace: "pre",
                            fontFamily: "monospace",
                            fontSize: 12,
                            overflow: "auto",
                            maxHeight: 320,
                          }}
                        >
                          {p.diff || "(no diff)"}
                        </Paper>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskPatchDialogOpen(false)} disabled={taskPatchPreviewLoading || taskPatchApplyLoading}>
                Close
              </Button>
              <Button onClick={handleRunTaskPatcher} variant="outlined" disabled={taskPatchPreviewLoading || taskPatchApplyLoading}>
                Rebuild Preview
              </Button>
              <Button
                onClick={handleApplyTaskPatch}
                variant="contained"
                disabled={taskPatchPreviewLoading || taskPatchApplyLoading || !taskPatchPreviewResult}
              >
                {taskPatchApplyLoading ? "Applying..." : "Apply Patch"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={taskDiscoveryDialogOpen} onClose={() => setTaskDiscoveryDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>File Discovery Findings</DialogTitle>
            <DialogContent dividers>
              {!taskDiscoveryResult ? (
                <Typography variant="body2" color="text.secondary">No discovery findings loaded.</Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {taskDiscoveryResult.findings?.summary || "Review the recommended files and accept the ones this change should use."}
                  </Typography>
                  {taskDiscoveryResult.findings?.recommended_primary_file && (
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                      Recommended primary file: {taskDiscoveryResult.findings.recommended_primary_file}
                    </Typography>
                  )}

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Approve files</Typography>
                  <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1, mb: 2, maxHeight: 260, overflowY: "auto" }}>
                    {(taskDiscoveryResult.findings?.candidates || []).map((row) => (
                      <Box key={row.path} sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)", py: 1 }}>
                        <FormControlLabel
                          control={<Checkbox checked={taskDiscoverySelectedFiles.includes(row.path)} onChange={() => toggleDiscoveryFile(row.path)} />}
                          label={row.path}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 4 }}>
                          {row.reason || row.description || "Candidate file"}
                        </Typography>
                      </Box>
                    ))}
                    {(!taskDiscoveryResult.findings?.candidates || taskDiscoveryResult.findings.candidates.length === 0) && (
                      <Typography variant="caption" color="text.secondary">No candidate files found.</Typography>
                    )}
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Apply findings to tasks</Typography>
                  <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 1 }}>
                    {(taskDiscoveryResult.findings?.target_task_suggestions || []).map((row) => (
                      <FormControlLabel
                        key={row.id}
                        control={<Checkbox checked={taskDiscoveryTargetIds.includes(row.id)} onChange={() => toggleDiscoveryTargetTask(row.id)} />}
                        label={`#${row.task_order || row.id} ${row.title} (${row.task_type || "GENERAL"})`}
                      />
                    ))}
                    {(!taskDiscoveryResult.findings?.target_task_suggestions || taskDiscoveryResult.findings.target_task_suggestions.length === 0) && (
                      <Typography variant="caption" color="text.secondary">No downstream task suggestions were found. The findings will still be written back to the current task.</Typography>
                    )}
                  </Box>

                  {taskDiscoveryError && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                      {taskDiscoveryError}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskDiscoveryDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleApplyTaskDiscovery} disabled={taskDiscoveryApplyLoading || taskDiscoverySelectedFiles.length === 0}>
                {taskDiscoveryApplyLoading ? "Applying..." : "Accept Findings"}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Stack>
    </Box>
  );
};

export default FullChangePanel;
